import { ApplicantTabNavigation } from "@components/layout/ApplicantTabNavigation";
import { ApplicationHeader } from "@components/layout/ApplicationHeader";
import { getApplicationHeaderProps } from "@lib/utils/applicationHeaderHelpers";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import { useGoogleTranslateRefresh } from '@/hooks/useGoogleTranslateRefresh'
import {
  User,
  MapPin,
  Save,
  ArrowRight,
  Mail,
  MessageSquare,
  Loader2,
  Info,
} from "lucide-react";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import {
  getTabPathWithApplicantId,
  getTabNavigationOptions,
  type ApplicationTab,
} from "@lib/utils/applicationNavigation";
import {
  selectApplicationData,
  selectApplicationLoading,
  selectApplications,
  selectIsReadOnly,
  fetchApplications,
  fetchApplicationById,
  updateApplication,
} from "@/features/applicant/applicationSlice";
import {
  formatPhoneNumber,
  formatDateForInput,
  unformatPhoneNumber,
} from "@/types/application";
import { normalizeDateFromAPI } from "@/features/application/utils/applicationFormatters";
import { getAllProperties, getAgent } from "@/lib/api/propertyService";
import {
  validateDateOfBirth,
  convertToDateInputFormat,
} from "@/lib/utils/dateValidation";
import { filterPhoneInput } from "@/lib/utils/formatters";
import { SSNInput } from "@components/ui/SSNInput";
import { DatePicker } from "@components/ui/DatePicker";
import { AddressAutocomplete } from "@components/ui/AddressAutocomplete";
import {
  parseAddressComponents,
  extractAptUnit,
} from "@lib/utils/addressParser";
import type { PlaceResult } from "@/hooks/useGooglePlacesAutocomplete";
import { useReferenceData } from "@/hooks/useReferenceData";
import {
  buildAddressData,
  calendarMonthsBetween,
  getMoveInDate,
  stripAptFromStreetAddress,
  formatAddressDisplay,
  syncSharedResidenceDateFields,
  shouldEnforceAddressHistoryRules,
} from "../utils/addressHelpers";
import { ADDRESS_CONSTANTS } from "@/constants/address";
import { useTabValidation, useTabAutoSave } from "@/features/applicant/context/TabValidationContext";
import { useAddressFormState } from "../hooks/useAddressFormState";
import { useAddressFormActions } from "../hooks/useAddressFormActions";
import {
  AddressBasicFields,
  ResidenceTypeSelector,
  ResidenceForm,
} from "@components/address";
import {
  residentSectionValidation,
  residentTabValidation,
  type UpdateApplicationRequest,
  type Address,
} from "@/types/application";
import { ValidationModal } from "@components/ui/ValidationModal";
import { ConfirmDialog } from "@components/ui/ConfirmDialog";
import {
  applicantConfirmDialogProps,
  applicantValidationModalProps,
} from "@/features/applicant/constants/applicantDialogProps";

const ADDRESS_OUTSIDE_USA_MESSAGE =
  "The address you entered is outside of the USA. Please add an alternate US address.\n" +
  'If you don\'t have an alternate address in the US, you can still continue by checking the "I don\'t have an alternate address in the US" box.';
import type { AddressFormActions, AddressFormState } from "../types/address";
import { createInitialAddressState } from "../types/address";
import { extractErrorMessage } from "@/lib/utils/errorHandling";
import { geocodeAddressRaw, matchZipAndStateAngularStyle } from "@/lib/utils/geocoding";
import {
  trackApplicationAutosave,
  trackApplicationExit,
  trackApplicationSectionComplete,
} from "@/lib/utils/analytics";

export function About() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicantId: urlApplicantId } = useParams<{ applicantId?: string }>();

  // Redux state
  const applicationData = useAppSelector(selectApplicationData);
  const applications = useAppSelector(selectApplications);
  const isLoading = useAppSelector(selectApplicationLoading);
  const isReadOnly = useAppSelector(selectIsReadOnly);

  // Get applicant ID from URL or Redux
  const applicantId = urlApplicantId || applicationData?.applicant_id;

  // Redirect to resident-application route if we have applicant ID but URL doesn't have it
  useEffect(() => {
    if (
      applicantId &&
      !urlApplicantId &&
      location.pathname.startsWith("/applicant/application/")
    ) {
      const currentTab = location.pathname.split("/").pop() || "about";
      navigate(
        getTabPathWithApplicantId(currentTab as ApplicationTab, applicantId),
        {
          state: { tab: currentTab as ApplicationTab },
          replace: true,
        },
      );
    }
  }, [applicantId, urlApplicantId, location.pathname, navigate]);

  // Scroll to top when component mounts (when user navigates to this page)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  // Fetch latest application data only if we don't have data yet (initial load)
  // When navigating back, Redux already has the latest data from previous updates
  useEffect(() => {
    const currentApplicantId = applicationData?.applicant_id;
    // Only fetch if we have an ID but no address data (initial load scenario)
    // Don't fetch when navigating back - use Redux data instead
    if (currentApplicantId && !applicationData?.current_address) {
      dispatch(fetchApplicationById(currentApplicantId)).catch(() => {
        // Error fetching latest application status
        // Don't show error to user, just log it
      });
    }
  }, []); // Only run on mount

  // Form state - initialized from Redux data
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [suffix, setSuffix] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [ssn, setSsn] = useState("");
  const [commPreference, setCommPreference] = useState<"text" | "email">(
    "email",
  );
  const [isSharedPhone, setIsSharedPhone] = useState(false);
  const [showSharedPhoneErrorModal, setShowSharedPhoneErrorModal] =
    useState(false);

  // Address state
  const [currentAddress, setCurrentAddress] = useState("");
  const [streetNumber, setStreetNumber] = useState("");
  const [streetName, setStreetName] = useState("");
  const [streetType, setStreetType] = useState("");
  const [aptUnit, setAptUnit] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState<string>(
    ADDRESS_CONSTANTS.DEFAULT_COUNTRY,
  );
  const [residenceType, setResidenceType] = useState<
    "rent" | "own" | "family" | null
  >(null);
  const [stillHereRent, setStillHereRent] = useState(false);
  const [stillHereOwn, setStillHereOwn] = useState(false);
  const [stillHereFamily, setStillHereFamily] = useState(false);
  const [noAlternateAddress, setNoAlternateAddress] = useState(false);
  const [outsideUS, setOutsideUS] = useState(false);
  const [
    showPersonalProfileValidationModal,
    setShowPersonalProfileValidationModal,
  ] = useState(false);
  const [showAddressValidationModal, setShowAddressValidationModal] =
    useState(false);
  const [validationModalMessage, setValidationModalMessage] = useState("");
  const [geocodeWarning, setGeocodeWarning] = useState<string | null>(null);
  const { registerValidator, unregisterValidator } = useTabValidation();

  // Other address state (for credit search) - now supports multiple addresses
  const [otherAddresses, setOtherAddresses] = useState<
    Array<{
      id: string;
      address: string;
      streetNumber: string;
      streetName: string;
      streetType: string;
      aptUnit: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
      outsideUS: boolean;
      showDetails: boolean;
      ra_id?: number | null;
    }>
  >([]);

  // Track original alternate addresses (from API) to detect deletions
  const [originalAlternateAddressIds, setOriginalAlternateAddressIds] =
    useState<Set<number>>(new Set());

  // Residence-specific fields
  // Rent fields
  const [landlordName, setLandlordName] = useState("");
  const [landlordEmail, setLandlordEmail] = useState("");
  const [landlordMobile, setLandlordMobile] = useState("");
  const [moveInDateRent, setMoveInDateRent] = useState("");
  const [leaseEndDate, setLeaseEndDate] = useState("");
  const [rentAmount, setRentAmount] = useState("");
  const [monthsPaidRent, setMonthsPaidRent] = useState("");

  // Own fields
  const [moveInDateOwn, setMoveInDateOwn] = useState("");
  const [plannedMoveOutDate, setPlannedMoveOutDate] = useState("");
  const [monthlyMortgagePayment, setMonthlyMortgagePayment] = useState("");
  const [monthsResided, setMonthsResided] = useState("");

  // Family fields
  const [contactName, setContactName] = useState("");
  const [contactRelationship, setContactRelationship] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMobile, setContactMobile] = useState("");
  const [contactWebsite, setContactWebsite] = useState("");
  const [moveInDateFamily, setMoveInDateFamily] = useState("");
  const [plannedMoveOutDateFamily, setPlannedMoveOutDateFamily] = useState("");
  const [rentAmountFamily, setRentAmountFamily] = useState("");

  // Previous address state (shown when cumulative duration < 24 months)
  const [previousAddresses, setPreviousAddresses] = useState<AddressFormState[]>([createInitialAddressState()]);

  // Google Translate only scans DOM present at translate time. Re-run after
  // newly expanded/added fields mount (e.g. Alternate Address → Close Detail).
  const googleTranslateContentKey = useMemo(
    () =>
      [
        otherAddresses.length,
        otherAddresses.map((addr) => `${addr.id}:${addr.showDetails ? 1 : 0}`).join(','),
        previousAddresses.length,
        previousAddresses.map((addr) => addr.residenceType ?? '').join(','),
        residenceType ?? '',
      ].join('|'),
    [otherAddresses, previousAddresses, residenceType],
  );
  useGoogleTranslateRefresh(googleTranslateContentKey);

  // ---------------------------------------------------------------------------
  // Backward-compatible accessors: read from previousAddresses[0]
  // These allow all downstream code to continue using the old variable names
  // without a mass refactor of every call site.
  // ---------------------------------------------------------------------------
  const prevAddr = previousAddresses[0];
  const previousAddress = prevAddr.address;
  const prevStreetNumber = prevAddr.streetNumber;
  const prevStreetName = prevAddr.streetName;
  const prevStreetType = prevAddr.streetType;
  const prevAptUnit = prevAddr.aptUnit;
  const prevCity = prevAddr.city;
  const prevState = prevAddr.state;
  const prevZipCode = prevAddr.zipCode;
  const prevCountry = prevAddr.country;
  const prevOutsideUS = prevAddr.outsideUS;
  const prevResidenceType = prevAddr.residenceType;
  const prevStillHereRent = prevAddr.stillHereRent;
  const prevMoveInDateRent = prevAddr.moveInDateRent;
  const prevLeaseEndDate = prevAddr.leaseEndDate;
  const prevLandlordName = prevAddr.landlordName;
  const prevLandlordEmail = prevAddr.landlordEmail;
  const prevLandlordMobile = prevAddr.landlordMobile;
  const prevRentAmount = prevAddr.rentAmount;
  const prevMonthsPaidRent = prevAddr.monthsPaidRent;
  const prevStillHereOwn = prevAddr.stillHereOwn;
  const prevMoveInDateOwn = prevAddr.moveInDateOwn;
  const prevPlannedMoveOutDate = prevAddr.plannedMoveOutDate;
  const prevMonthlyMortgagePayment = prevAddr.monthlyMortgagePayment;
  const prevMonthsResided = prevAddr.monthsResided;
  const prevStillHereFamily = prevAddr.stillHereFamily;
  const prevMoveInDateFamily = prevAddr.moveInDateFamily;
  const prevPlannedMoveOutDateFamily = prevAddr.plannedMoveOutDateFamily;
  const prevContactName = prevAddr.contactName;
  const prevContactEmail = prevAddr.contactEmail;
  const prevContactMobile = prevAddr.contactMobile;
  const prevContactWebsite = prevAddr.contactWebsite;
  const prevContactRelationship = prevAddr.contactRelationship;
  const prevRentAmountFamily = prevAddr.rentAmountFamily;

  // ---------------------------------------------------------------------------
  // Backward-compatible setters: update a single field in previousAddresses[0]
  // ---------------------------------------------------------------------------
  const updatePrevAddress = <K extends keyof AddressFormState>(idx: number, field: K, value: AddressFormState[K]) => {
    setPreviousAddresses(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  const setPreviousAddress = (v: string) => updatePrevAddress(0, 'address', v);
  const setPrevStreetNumber = (v: string) => updatePrevAddress(0, 'streetNumber', v);
  const setPrevStreetName = (v: string) => updatePrevAddress(0, 'streetName', v);
  const setPrevStreetType = (v: string) => updatePrevAddress(0, 'streetType', v);
  const setPrevAptUnit = (v: string) => updatePrevAddress(0, 'aptUnit', v);
  const setPrevCity = (v: string) => updatePrevAddress(0, 'city', v);
  const setPrevState = (v: string) => updatePrevAddress(0, 'state', v);
  const setPrevZipCode = (v: string) => updatePrevAddress(0, 'zipCode', v);
  const setPrevCountry = (v: string) => updatePrevAddress(0, 'country', v);
  const setPrevOutsideUS = (v: boolean) => updatePrevAddress(0, 'outsideUS', v);
  const setPrevResidenceType = (v: 'rent' | 'own' | 'family' | null) => updatePrevAddress(0, 'residenceType', v);
  const setPrevStillHereRent = (v: boolean) => updatePrevAddress(0, 'stillHereRent', v);
  const setPrevMoveInDateRent = (v: string) => updatePrevAddress(0, 'moveInDateRent', v);
  const setPrevLeaseEndDate = (v: string) => updatePrevAddress(0, 'leaseEndDate', v);
  const setPrevLandlordName = (v: string) => updatePrevAddress(0, 'landlordName', v);
  const setPrevLandlordEmail = (v: string) => updatePrevAddress(0, 'landlordEmail', v);
  const setPrevLandlordMobile = (v: string) => updatePrevAddress(0, 'landlordMobile', v);
  const setPrevRentAmount = (v: string) => updatePrevAddress(0, 'rentAmount', v);
  const setPrevMonthsPaidRent = (v: string) => updatePrevAddress(0, 'monthsPaidRent', v);
  const setPrevStillHereOwn = (v: boolean) => updatePrevAddress(0, 'stillHereOwn', v);
  const setPrevMoveInDateOwn = (v: string) => updatePrevAddress(0, 'moveInDateOwn', v);
  const setPrevPlannedMoveOutDate = (v: string) => updatePrevAddress(0, 'plannedMoveOutDate', v);
  const setPrevMonthlyMortgagePayment = (v: string) => updatePrevAddress(0, 'monthlyMortgagePayment', v);
  const setPrevMonthsResided = (v: string) => updatePrevAddress(0, 'monthsResided', v);
  const setPrevStillHereFamily = (v: boolean) => updatePrevAddress(0, 'stillHereFamily', v);
  const setPrevMoveInDateFamily = (v: string) => updatePrevAddress(0, 'moveInDateFamily', v);
  const setPrevPlannedMoveOutDateFamily = (v: string) => updatePrevAddress(0, 'plannedMoveOutDateFamily', v);
  const setPrevContactName = (v: string) => updatePrevAddress(0, 'contactName', v);
  const setPrevContactEmail = (v: string) => updatePrevAddress(0, 'contactEmail', v);
  const setPrevContactMobile = (v: string) => updatePrevAddress(0, 'contactMobile', v);
  const setPrevContactWebsite = (v: string) => updatePrevAddress(0, 'contactWebsite', v);
  const setPrevContactRelationship = (v: string) => updatePrevAddress(0, 'contactRelationship', v);
  const setPrevRentAmountFamily = (v: string) => updatePrevAddress(0, 'rentAmountFamily', v);

  // Track if we've already attempted to fetch
  const [hasFetched, setHasFetched] = useState(false);
  const [hasFetchedById, setHasFetchedById] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [isSavingAndContinuing, setIsSavingAndContinuing] = useState(false);
  const [isSavingAndExiting, setIsSavingAndExiting] = useState(false);
  const [saveAddressMessage, setSaveAddressMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [isSavingPrevAddress, setIsSavingPrevAddress] = useState(false);
  const [savePrevAddressMessage, setSavePrevAddressMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Track if personal profile has been saved (to disable email, firstName, lastName, mobilePhone after save)
  const [isPersonalProfileSaved, setIsPersonalProfileSaved] = useState(false);

  // Validation errors for personal profile (only DOB and SSN)
  const [personalProfileErrors, setPersonalProfileErrors] = useState<{
    dateOfBirth?: string;
    ssn?: string;
  }>({});

  // Track if previous address has been initialized from API to prevent overwriting user edits
  const prevAddressInitialized = useRef(false);
  // Reset when applicant changes so revisiting the application re-loads previous_address from API (avoids duplicated/stale addresses)
  const lastLoadedApplicantIdRef = useRef<string | number | null>(null);
  // Track if user dismissed "Import from Previous Application" so we don't show again
  const importDialogDismissed = useRef(false);
  // Revisit: show dialog to import old application addresses as previous address (matches Angular)
  const [showImportPreviousAddressDialog, setShowImportPreviousAddressDialog] =
    useState(false);

  // Reference data (states) from API
  const { states, isLoadingStates } = useReferenceData();

  // Validation errors for current address
  const [fieldErrors, setFieldErrors] = useState<{
    streetNumber?: string;
    streetName?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    residenceType?: string;
    landlordName?: string;
    landlordEmail?: string;
    landlordMobile?: string;
    rentAmount?: string;
    moveInDateRent?: string;
    leaseEndDate?: string;
    monthlyMortgagePayment?: string;
    moveInDateOwn?: string;
    plannedMoveOutDate?: string;
    contactName?: string;
    contactEmail?: string;
    contactMobile?: string;
    contactRelationship?: string;
    moveInDateFamily?: string;
    plannedMoveOutDateFamily?: string;
    // Previous address errors
    prevStreetNumber?: string;
    prevStreetName?: string;
    prevCity?: string;
    prevState?: string;
    prevZipCode?: string;
    prevResidenceType?: string;
    prevLandlordName?: string;
    prevLandlordEmail?: string;
    prevLandlordMobile?: string;
    prevRentAmount?: string;
    prevMonthsPaidRent?: string;
    prevMoveInDateRent?: string;
    prevMonthlyMortgagePayment?: string;
    prevMoveInDateOwn?: string;
    prevContactName?: string;
    prevContactEmail?: string;
    prevContactMobile?: string;
    prevContactRelationship?: string;
    prevMoveInDateFamily?: string;
    monthsResided?: string;
    prevMonthsResided?: string;
    [key: string]: string | undefined;
  }>({});

  // Other address field errors
  const [otherAddressFieldErrors, setOtherAddressFieldErrors] = useState<
    Record<
      string,
      {
        streetNumber?: string;
        streetName?: string;
        city?: string;
        state?: string;
        zipCode?: string;
      }
    >
  >({});

  // Convert current address state to AddressFormState using hook
  const currentAddressState = useAddressFormState({
    address: currentAddress,
    streetNumber,
    streetName,
    streetType,
    aptUnit,
    city,
    state,
    zipCode,
    country,
    outsideUS,
    residenceType,
    stillHereRent,
    moveInDateRent,
    leaseEndDate,
    landlordName,
    landlordEmail,
    landlordMobile,
    rentAmount,
    stillHereOwn,
    moveInDateOwn,
    plannedMoveOutDate,
    monthlyMortgagePayment,
    stillHereFamily,
    moveInDateFamily,
    plannedMoveOutDateFamily,
    contactName,
    contactEmail,
    contactMobile,
    contactWebsite,
    contactRelationship,
    rentAmountFamily,
    monthsPaidRent,
    monthsResided,
  });

  // Convert current address setters to AddressFormActions using hook
  const currentAddressActions = useAddressFormActions({
    setAddress: setCurrentAddress,
    setStreetNumber,
    setStreetName,
    setStreetType,
    setAptUnit,
    setCity,
    setState,
    setZipCode,
    setCountry,
    setOutsideUS,
    setResidenceType,
    setStillHereRent,
    setMoveInDateRent,
    setLeaseEndDate,
    setLandlordName,
    setLandlordEmail,
    setLandlordMobile,
    setRentAmount,
    setStillHereOwn,
    setMoveInDateOwn,
    setPlannedMoveOutDate,
    setMonthlyMortgagePayment,
    setStillHereFamily,
    setMoveInDateFamily,
    setPlannedMoveOutDateFamily,
    setContactName,
    setContactEmail,
    setContactMobile,
    setContactWebsite,
    setContactRelationship,
    setRentAmountFamily,
    setMonthsPaidRent,
    setMonthsResided,
  });

  // Keep consolidated address field in format: Street Unit, City, State, Zip, Country
  useEffect(() => {
    const formatted = formatAddressDisplay({
      house_no: streetNumber,
      street: streetName,
      apartment: aptUnit,
      city,
      state,
      zip_code: zipCode,
      country,
    });
    if (formatted) setCurrentAddress(formatted);
  }, [
    streetNumber,
    streetName,
    streetType,
    aptUnit,
    city,
    state,
    zipCode,
    country,
  ]);

  // Convert previous address state to AddressFormState using hook
  const prevAddressState = useAddressFormState({
    address: previousAddress,
    streetNumber: prevStreetNumber,
    streetName: prevStreetName,
    streetType: prevStreetType,
    aptUnit: prevAptUnit,
    city: prevCity,
    state: prevState,
    zipCode: prevZipCode,
    country: prevCountry,
    outsideUS: prevOutsideUS,
    residenceType: prevResidenceType,
    stillHereRent: prevStillHereRent,
    moveInDateRent: prevMoveInDateRent,
    leaseEndDate: prevLeaseEndDate,
    landlordName: prevLandlordName,
    landlordEmail: prevLandlordEmail,
    landlordMobile: prevLandlordMobile,
    rentAmount: prevRentAmount,
    stillHereOwn: prevStillHereOwn,
    moveInDateOwn: prevMoveInDateOwn,
    plannedMoveOutDate: prevPlannedMoveOutDate,
    monthlyMortgagePayment: prevMonthlyMortgagePayment,
    stillHereFamily: prevStillHereFamily,
    moveInDateFamily: prevMoveInDateFamily,
    plannedMoveOutDateFamily: prevPlannedMoveOutDateFamily,
    contactName: prevContactName,
    contactEmail: prevContactEmail,
    contactMobile: prevContactMobile,
    contactWebsite: prevContactWebsite,
    contactRelationship: prevContactRelationship,
    rentAmountFamily: prevRentAmountFamily,
    monthsPaidRent: prevMonthsPaidRent,
    monthsResided: prevMonthsResided,
  });

  // Convert previous address setters to AddressFormActions using hook
  useAddressFormActions({
    setAddress: setPreviousAddress,
    setStreetNumber: setPrevStreetNumber,
    setStreetName: setPrevStreetName,
    setStreetType: setPrevStreetType,
    setAptUnit: setPrevAptUnit,
    setCity: setPrevCity,
    setState: setPrevState,
    setZipCode: setPrevZipCode,
    setCountry: setPrevCountry,
    setOutsideUS: setPrevOutsideUS,
    setResidenceType: setPrevResidenceType,
    setStillHereRent: setPrevStillHereRent,
    setMoveInDateRent: setPrevMoveInDateRent,
    setLeaseEndDate: setPrevLeaseEndDate,
    setLandlordName: setPrevLandlordName,
    setLandlordEmail: setPrevLandlordEmail,
    setLandlordMobile: setPrevLandlordMobile,
    setRentAmount: setPrevRentAmount,
    setStillHereOwn: setPrevStillHereOwn,
    setMoveInDateOwn: setPrevMoveInDateOwn,
    setPlannedMoveOutDate: setPrevPlannedMoveOutDate,
    setMonthlyMortgagePayment: setPrevMonthlyMortgagePayment,
    setStillHereFamily: setPrevStillHereFamily,
    setMoveInDateFamily: setPrevMoveInDateFamily,
    setPlannedMoveOutDateFamily: setPrevPlannedMoveOutDateFamily,
    setContactName: setPrevContactName,
    setContactEmail: setPrevContactEmail,
    setContactMobile: setPrevContactMobile,
    setContactWebsite: setPrevContactWebsite,
    setContactRelationship: setPrevContactRelationship,
    setRentAmountFamily: setPrevRentAmountFamily,
    setMonthsPaidRent: setPrevMonthsPaidRent,
    setMonthsResided: setPrevMonthsResided,
  });

  // Keep previous address consolidated field in format: Street Unit, City, State, Zip, Country
  useEffect(() => {
    const formatted = formatAddressDisplay({
      house_no: prevStreetNumber,
      street: prevStreetName,
      apartment: prevAptUnit,
      city: prevCity,
      state: prevState,
      zip_code: prevZipCode,
      country: prevCountry,
    });
    if (formatted) setPreviousAddress(formatted);
  }, [
    prevStreetNumber,
    prevStreetName,
    prevAptUnit,
    prevCity,
    prevState,
    prevZipCode,
    prevCountry,
  ]);

  // Fetch application data on mount only if not already loaded and not currently loading
  useEffect(() => {
    // Only fetch if: no data, not loading, and haven't fetched yet
    if (!applicationData && !isLoading && !hasFetched) {
      setHasFetched(true);
      dispatch(fetchApplications());
    }
  }, [applicationData, isLoading, hasFetched, dispatch]);

  // After fetching applications, fetch detailed data by ID if we have applicant_id
  useEffect(() => {
    if (
      hasFetched &&
      !hasFetchedById &&
      !isLoading &&
      (applicationData || applications.length > 0)
    ) {
      // Get applicant_id from applicationData or first application in array
      const applicantId =
        applicationData?.applicant_id ||
        (applications.length > 0 ? applications[0]?.applicant_id : null);

      if (applicantId) {
        setHasFetchedById(true);
        dispatch(fetchApplicationById(applicantId)).catch(() => {
          // If fetch by ID fails, continue with existing data
          setHasFetchedById(true); // Mark as fetched to prevent retry loop
        });
      } else {
        // No applicant_id available, mark as fetched to prevent retry
        setHasFetchedById(true);
      }
    }
  }, [
    hasFetched,
    hasFetchedById,
    isLoading,
    applicationData,
    applications,
    dispatch,
  ]);

  // Track which fields are auto-filled from API (to disable only those)
  const [autoFilledFields, setAutoFilledFields] = useState<Set<string>>(
    new Set(),
  );

  // Calculate current address duration in months (same logic as Angular getAddressMonth / month_reside | paid_rent)
  const calculateCurrentAddressDuration = (): number => {
    if (!currentAddressState.residenceType) return 0;
    const moveInDate = getMoveInDate(currentAddressState);
    if (!moveInDate) return 0;

    let endDate: string | undefined = undefined;
    if (
      currentAddressState.residenceType === "rent" &&
      !currentAddressState.stillHereRent
    ) {
      endDate = currentAddressState.leaseEndDate;
    } else if (
      currentAddressState.residenceType === "own" &&
      !currentAddressState.stillHereOwn
    ) {
      endDate = currentAddressState.plannedMoveOutDate;
    } else if (
      currentAddressState.residenceType === "family" &&
      !currentAddressState.stillHereFamily
    ) {
      endDate = currentAddressState.plannedMoveOutDateFamily;
    }

    return calendarMonthsBetween(moveInDate, endDate);
  };

  // --- Previous address visibility (Angular parity) ---
  // Angular shows previous address when: (1) current_address.valid AND (2) checkPreviousAddress().
  // checkPreviousAddress(): OWN ? month_reside < 24 : paid_rent < 24 (set in moveInValidate via moment().diff(..., 'months')).
  // Angular template: *ngIf="applicationForm.get('aboutyourselfinfo.current_address').valid && checkPreviousAddress()"
  // React: show when isCurrentAddressValid() && shouldShowPreviousAddress() (duration from calendarMonthsBetween = calendar months, same idea as moment diff).

  // Military/diplomatic APO/FPO states – skip geocoding for these (Angular parity)
  const MILITARY_STATES = ['AA', 'AE', 'AP'];

  /** Post-entry geocoding verification (Issue 13 – Angular parity: focusout on city/state/zip).
   *  Soft warning only – user can still submit. Skip when outside US or military state. */
  const handleGeocodeCheck = async () => {
    // Only run if all three fields have values
    if (!city.trim() || !state.trim() || !zipCode.trim()) return;
    // Skip for outside US addresses
    if (outsideUS || (country && country.toUpperCase() !== 'US')) return;
    // Skip for military states (AA, AE, AP)
    if (MILITARY_STATES.includes(state.trim().toUpperCase())) return;

    try {
      const addressStr = `${city}, ${state}, ${zipCode}`;
      const { result, status } = await geocodeAddressRaw(addressStr);
      if (status !== 'OK' || !result) {
        setGeocodeWarning('We were unable to verify this address with Google Maps. If you know it is correct, you may continue');
        return;
      }
      const { zipValid, stateValid } = matchZipAndStateAngularStyle(result, zipCode, state);
      if (!zipValid || !stateValid) {
        setGeocodeWarning('We were unable to verify this address with Google Maps. If you know it is correct, you may continue');
      } else {
        setGeocodeWarning(null);
      }
    } catch {
      // Geocoding errors are non-fatal – don't block the user
      setGeocodeWarning(null);
    }
  };

  function shouldShowPreviousAddress(): boolean {
    const currentMonths = calculateCurrentAddressDuration();
    return currentMonths < ADDRESS_CONSTANTS.MIN_MONTHS_FOR_PREVIOUS_ADDRESS;
  }

  // Use currentAddressState (same source as UI) so dev/prod behave identically; avoid showing previous when current is empty.
  function isCurrentAddressValid(): boolean {
    const s = currentAddressState;
    if (!s.streetNumber?.trim()) return false;
    if (!s.streetName?.trim()) return false;
    if (!s.city?.trim()) return false;
    if (!s.state?.trim()) return false;
    const digitsOnly = (s.zipCode ?? "").replace(/\D/g, "");
    if (!digitsOnly || (digitsOnly.length !== 5 && digitsOnly.length !== 6)) return false;
    if (!s.residenceType) return false;
    if (s.residenceType === "rent") {
      if (!s.landlordName?.trim() || !s.landlordEmail?.trim() || !s.landlordMobile?.trim()) return false;
      if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(s.landlordEmail.trim())) return false;
      if (!/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(s.landlordMobile.trim().replace(/\D/g, ""))) return false;
      if (!s.moveInDateRent || !s.rentAmount?.trim()) return false;
      if (!s.stillHereRent && !s.leaseEndDate?.trim()) return false;
    } else if (s.residenceType === "own") {
      if (!s.moveInDateOwn || !s.monthlyMortgagePayment?.trim()) return false;
      if (!s.stillHereOwn && !s.plannedMoveOutDate?.trim()) return false;
    } else if (s.residenceType === "family") {
      if (!s.contactName?.trim() || !s.contactRelationship?.trim() || !s.contactEmail?.trim() || !s.contactMobile?.trim()) return false;
      if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(s.contactEmail.trim())) return false;
      if (!/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(s.contactMobile.trim().replace(/\D/g, ""))) return false;
      if (!s.moveInDateFamily) return false;
      if (!s.stillHereFamily && !s.plannedMoveOutDateFamily?.trim()) return false;
    }
    return true;
  }
  // --- End Angular parity: current/previous address visibility ---

  // Calculate previous address duration in months
  const getPrevAddressDuration = (index: number): number => {
    if (!shouldShowPreviousAddress()) return 0;
    const addr = previousAddresses[index];
    if (!addr || !addr.residenceType) return 0;

    const moveInDate = getMoveInDate(addr);
    if (!moveInDate) return 0;

    let endDateStr: string | null = null;
    if (index === 0) {
      endDateStr = getMoveInDate(currentAddressState);
    } else {
      endDateStr = getMoveInDate(previousAddresses[index - 1]);
    }

    if (!endDateStr) return 0;
    return calendarMonthsBetween(moveInDate, endDateStr);
  };

  const calculateTotalAddressDuration = (): number => {
    let total = calculateCurrentAddressDuration();
    for (let i = 0; i < previousAddresses.length; i++) {
      total += getPrevAddressDuration(i);
    }
    return total;
  };

  /**
   * Whether a previous-address row is filled enough to count toward the 24‑month chain,
   * and its calendar span (may be 0, e.g. same‑month move-in vs current).
   * Do not conflate "0 months" with "row not ready": incomplete rows block appending the next slot;
   * complete rows with span 0 still allow another previous address when total is under 24 months.
   */
  const getPreviousRowCompleteAndMonths = (
    list: AddressFormState[],
    index: number,
    current: AddressFormState,
  ): { complete: boolean; months: number } => {
    const addr = list[index];
    if (!addr?.residenceType) return { complete: false, months: 0 };
    const moveInDate = getMoveInDate(addr);
    if (!moveInDate) return { complete: false, months: 0 };
    let endDateStr: string | null = null;
    if (index === 0) {
      endDateStr = getMoveInDate(current);
    } else {
      endDateStr = getMoveInDate(list[index - 1]);
    }
    if (!endDateStr) return { complete: false, months: 0 };
    return {
      complete: true,
      months: calendarMonthsBetween(moveInDate, endDateStr),
    };
  };

  /**
   * Angular resident-application-add.validateAddress():
   * If currentMonths < 24 and currentMonths + sum(previous spans) < 24, add another previous_address row.
   * Abort only while a row is still incomplete (no type / move-in / chain end date)—not when span is 0.
   */
  const appendPreviousAddressSlotIfAngularAllows = useCallback(() => {
    setPreviousAddresses((prev) => {
      const currentMonths = (() => {
        if (!currentAddressState.residenceType) return 0;
        const moveInDate = getMoveInDate(currentAddressState);
        if (!moveInDate) return 0;
        let endDate: string | undefined = undefined;
        if (
          currentAddressState.residenceType === "rent" &&
          !currentAddressState.stillHereRent
        ) {
          endDate = currentAddressState.leaseEndDate;
        } else if (
          currentAddressState.residenceType === "own" &&
          !currentAddressState.stillHereOwn
        ) {
          endDate = currentAddressState.plannedMoveOutDate;
        } else if (
          currentAddressState.residenceType === "family" &&
          !currentAddressState.stillHereFamily
        ) {
          endDate = currentAddressState.plannedMoveOutDateFamily;
        }
        return calendarMonthsBetween(moveInDate, endDate);
      })();

      if (
        currentMonths >= ADDRESS_CONSTANTS.MIN_MONTHS_FOR_PREVIOUS_ADDRESS
      ) {
        return prev;
      }

      let previousSum = 0;
      for (let i = 0; i < prev.length; i++) {
        const { complete, months } = getPreviousRowCompleteAndMonths(
          prev,
          i,
          currentAddressState,
        );
        if (!complete) {
          return prev;
        }
        previousSum += months;
      }

      if (
        currentMonths + previousSum >=
        ADDRESS_CONSTANTS.MIN_MONTHS_FOR_PREVIOUS_ADDRESS
      ) {
        return prev;
      }

      return [...prev, createInitialAddressState()];
    });
  }, [currentAddressState]);

  // Auto-calculate current address duration state
  useEffect(() => {
    if (!currentAddressState.residenceType) return;
    const duration = calculateCurrentAddressDuration();
    const durationStr = duration.toString();

    if (currentAddressState.residenceType === "own") {
      if (monthsResided !== durationStr) {
        setMonthsResided(durationStr);
      }
    } else {
      if (monthsPaidRent !== durationStr) {
        setMonthsPaidRent(durationStr);
      }
    }
  }, [
    currentAddressState.residenceType,
    currentAddressState.moveInDateRent,
    currentAddressState.moveInDateOwn,
    currentAddressState.moveInDateFamily,
    currentAddressState.stillHereRent,
    currentAddressState.stillHereOwn,
    currentAddressState.stillHereFamily,
    currentAddressState.leaseEndDate,
    currentAddressState.plannedMoveOutDate,
    currentAddressState.plannedMoveOutDateFamily,
    monthsResided,
    monthsPaidRent,
  ]);

  // Auto-calculate previous address duration state for all previous addresses
  useEffect(() => {
    if (!shouldShowPreviousAddress()) return;

    setPreviousAddresses((prev) => {
      let changed = false;
      const next = prev.map((addr, index) => {
        if (!addr.residenceType) return addr;
        const moveInDate = getMoveInDate(addr);
        if (!moveInDate) return addr;
        let endDateStr: string | null = null;
        if (index === 0) {
          endDateStr = getMoveInDate(currentAddressState);
        } else {
          endDateStr = getMoveInDate(prev[index - 1]);
        }
        if (!endDateStr) return addr;
        const months = calendarMonthsBetween(moveInDate, endDateStr);
        const monthsStr = months.toString();

        if (addr.residenceType === "own" && addr.monthsResided !== monthsStr) {
          changed = true;
          return { ...addr, monthsResided: monthsStr };
        } else if (addr.residenceType !== "own" && addr.monthsPaidRent !== monthsStr) {
          changed = true;
          return { ...addr, monthsPaidRent: monthsStr };
        }
        return addr;
      });
      return changed ? next : prev;
    });
  }, [
    currentAddressState,
    previousAddresses,
  ]);

  // When current address duration >= 24 months, do not show previous address and clear it (matches Angular: if (currentMonths >= 24) removeControl('previous_address'))
  useEffect(() => {
    if (isReadOnly) return;
    const currentMonths = calculateCurrentAddressDuration();
    if (currentMonths >= ADDRESS_CONSTANTS.MIN_MONTHS_FOR_PREVIOUS_ADDRESS) {
      setPreviousAddresses((prev) => {
        const singleEmpty =
          prev.length === 1 && !prev[0].residenceType && !getMoveInDate(prev[0]);
        return singleEmpty ? prev : [createInitialAddressState()];
      });
    }
  }, [
    currentAddressState.residenceType,
    currentAddressState.moveInDateRent,
    currentAddressState.moveInDateOwn,
    currentAddressState.moveInDateFamily,
    currentAddressState.stillHereRent,
    currentAddressState.stillHereOwn,
    currentAddressState.stillHereFamily,
    currentAddressState.leaseEndDate,
    currentAddressState.plannedMoveOutDate,
    currentAddressState.plannedMoveOutDateFamily,
  ]);

  // Angular validateAddress(): auto-append a previous-address row when cumulative months < 24
  // and every existing row already has a computed month span (no stacking empty rows).
  useEffect(() => {
    if (!shouldShowPreviousAddress() || isReadOnly) return;
    appendPreviousAddressSlotIfAngularAllows();
  }, [
    isReadOnly,
    currentAddressState,
    previousAddresses,
    appendPreviousAddressSlotIfAngularAllows,
  ]);

  // Check if all addresses (current + previous) are outside US
  const validateAllUSAddress = (): boolean => {
    // Check current address
    const currentIsOutsideUS = Boolean(
      outsideUS || (country && country.toUpperCase() !== "US"),
    );

    // Check previous address if it exists
    let prevIsOutsideUS = false;
    if (shouldShowPreviousAddress() && prevResidenceType) {
      prevIsOutsideUS = Boolean(
        prevOutsideUS || (prevCountry && prevCountry.toUpperCase() !== "US"),
      );
    }

    // If previous address exists, both must be outside US
    if (shouldShowPreviousAddress() && prevResidenceType) {
      return currentIsOutsideUS && prevIsOutsideUS;
    }

    // If only current address exists, check if it's outside US
    return currentIsOutsideUS;
  };

  // Sync outsideUS with country when country is set to non-US value
  // This ensures checkbox is checked when country indicates outside US
  // Only updates if outsideUS is currently false to preserve explicit API values
  useEffect(() => {
    if (country && country.toUpperCase() !== "US" && !outsideUS) {
      setOutsideUS(true);
    }
  }, [country, outsideUS]);

  // Auto-check "I don't have an alternate address" when "Other / Outside U.S." is checked or country is not US
  useEffect(() => {
    const currentIsOutsideUS =
      outsideUS || (country && country.toUpperCase() !== "US");
    if (currentIsOutsideUS) {
      setNoAlternateAddress(true);
    }
  }, [outsideUS, country]);

  // Auto-check "I don't have an alternate address" when previous address "Other / Outside U.S." is checked or country is not US
  useEffect(() => {
    if (shouldShowPreviousAddress() && prevResidenceType) {
      const prevIsOutsideUS = Boolean(
        prevOutsideUS || (prevCountry && prevCountry.toUpperCase() !== "US"),
      );
      if (prevIsOutsideUS) {
        setNoAlternateAddress(true);
      }
    }
  }, [prevOutsideUS, prevCountry, prevResidenceType]);

  // Helper function to build current_address object according to API
  const buildCurrentAddress = () => {
    return buildAddressData(
      currentAddressState,
      applicationData?.current_address?.ra_id || null,
    );
  };

  // Build street_address for alternate (other) addresses: include apt/unit once, no accumulation
  const buildAlternateAddressStreetAddress = (addr: {
    address: string;
    aptUnit: string;
    streetNumber: string;
    streetName: string;
  }): string => {
    const formatUnit = (unit: string) => {
      const t = unit.trim();
      return t ? (t.startsWith("#") ? t : `#${t}`) : "";
    };
    let full =
      addr.address || `${addr.streetNumber} ${addr.streetName}`.trim();
    if (addr.aptUnit && full) {
      const base = stripAptFromStreetAddress(full, null);
      const unitFormatted = formatUnit(addr.aptUnit);
      full = `${base} ${unitFormatted}`;
    }
    return full;
  };

  // Map API Address to form state (shared for previous_address load and oldapplication import)
  const mapApiAddressToFormState = (addr: Address): AddressFormState => {
    const state = createInitialAddressState();
    if (addr.apartment != null) state.aptUnit = addr.apartment || "";
    if (addr.house_no) state.streetNumber = addr.house_no;
    if (addr.street) state.streetName = addr.street;
    if (addr.street_type) state.streetType = addr.street_type;
    if (addr.city) state.city = addr.city;
    if (addr.state) state.state = addr.state;
    if (addr.zip_code) state.zipCode = addr.zip_code;
    if (addr.country) state.country = addr.country;
    state.address = formatAddressDisplay({
      house_no: addr.house_no,
      street: addr.street,
      street_type: addr.street_type,
      apartment: addr.apartment,
      city: addr.city,
      state: addr.state,
      zip_code: addr.zip_code,
      country: addr.country,
    });
    if (addr.outside_us !== undefined) {
      state.outsideUS =
        typeof addr.outside_us === "string" ? addr.outside_us === "1" : !!addr.outside_us;
    }
    if (addr.address_type) {
      const residenceTypeMap: Record<string, "rent" | "own" | "family"> = {
        RENT: "rent",
        OWN: "own",
        FAMILY: "family",
      };
      const mappedType = residenceTypeMap[addr.address_type];
      if (mappedType) state.residenceType = mappedType;
    }
    // Preserve ra_id so updates use existing ID and backend does not create duplicate entries
    if (addr.ra_id != null) {
      state.ra_id = typeof addr.ra_id === "string" ? parseInt(addr.ra_id, 10) : addr.ra_id;
    }
    if (addr.address_data) {
      const ad = addr.address_data as Record<string, unknown>;
      const moveInDateRaw = (ad.moveIn_date ?? ad.move_in_date) as string | undefined;
      const moveOutDateRaw = (ad.moveOut_date ?? ad.move_out_date) as string | undefined;
      const moveInDate = moveInDateRaw ? normalizeDateFromAPI(moveInDateRaw) : "";
      const moveOutDate = moveOutDateRaw ? normalizeDateFromAPI(moveOutDateRaw) : "";
      const currentlyLiving = ad.currently_living;
      const rentAmount = ad.rent_amount;
      if (addr.address_type === "RENT") {
        if (currentlyLiving !== undefined && currentlyLiving !== null) {
          const sh =
            typeof currentlyLiving === "string" ? currentlyLiving === "1" : !!currentlyLiving;
          state.stillHereRent = sh;
          state.stillHereOwn = sh;
          state.stillHereFamily = sh;
        }
        if (moveInDate) {
          const parts = moveInDate.split("-");
          if (parts.length === 3) state.moveInDateRent = `${parts[1]}/${parts[2]}/${parts[0]}`;
        }
        if (moveOutDate) {
          const parts = moveOutDate.split("-");
          if (parts.length === 3) state.leaseEndDate = `${parts[1]}/${parts[2]}/${parts[0]}`;
        }
        if (ad.landlord_name) state.landlordName = String(ad.landlord_name);
        if (ad.landlord_email) state.landlordEmail = String(ad.landlord_email);
        if (ad.landlord_mobile_no)
          state.landlordMobile = formatPhoneNumber(String(ad.landlord_mobile_no));
        if (rentAmount != null) state.rentAmount = String(rentAmount);
        if (ad.paid_rent != null) state.monthsPaidRent = String(ad.paid_rent);
      } else if (addr.address_type === "OWN") {
        if (currentlyLiving !== undefined && currentlyLiving !== null) {
          const sh =
            typeof currentlyLiving === "string" ? currentlyLiving === "1" : !!currentlyLiving;
          state.stillHereRent = sh;
          state.stillHereOwn = sh;
          state.stillHereFamily = sh;
        }
        if (moveInDate) {
          const parts = moveInDate.split("-");
          if (parts.length === 3) state.moveInDateOwn = `${parts[1]}/${parts[2]}/${parts[0]}`;
        }
        if (moveOutDate) {
          const parts = moveOutDate.split("-");
          if (parts.length === 3) state.plannedMoveOutDate = `${parts[1]}/${parts[2]}/${parts[0]}`;
        }
        if (ad.month_reside != null) state.monthsResided = String(ad.month_reside);
        if (rentAmount != null) state.monthlyMortgagePayment = String(rentAmount);
      } else if (addr.address_type === "FAMILY") {
        if (currentlyLiving !== undefined && currentlyLiving !== null) {
          const sh =
            typeof currentlyLiving === "string" ? currentlyLiving === "1" : !!currentlyLiving;
          state.stillHereRent = sh;
          state.stillHereOwn = sh;
          state.stillHereFamily = sh;
        }
        if (moveInDate) {
          const parts = moveInDate.split("-");
          if (parts.length === 3) state.moveInDateFamily = `${parts[1]}/${parts[2]}/${parts[0]}`;
        }
        if (moveOutDate) {
          const parts = moveOutDate.split("-");
          if (parts.length === 3)
            state.plannedMoveOutDateFamily = `${parts[1]}/${parts[2]}/${parts[0]}`;
        }
        if (ad.landlord_name) state.contactName = String(ad.landlord_name);
        if (ad.landlord_email) state.contactEmail = String(ad.landlord_email);
        if (ad.landlord_mobile_no)
          state.contactMobile = formatPhoneNumber(String(ad.landlord_mobile_no));
        if (ad.landlord_website) state.contactWebsite = String(ad.landlord_website ?? "");
        if (ad.landlord_relationship_type)
          state.contactRelationship = String(ad.landlord_relationship_type);
        if (rentAmount != null) state.rentAmountFamily = String(rentAmount);
        if (ad.paid_rent != null) state.monthsPaidRent = String(ad.paid_rent);
      }
    }
    return state;
  };

  // Populate form with application data from API
  useEffect(() => {
    if (applicationData) {
      const filledFields = new Set<string>();
      const userData = applicationData.user_data;

      // Personal info - check user_data first, then fallback to root level
      const firstName = userData?.first_name || applicationData.first_name;
      if (firstName) {
        setFirstName(firstName);
        filledFields.add("firstName");
      }

      // Handle middle_name - check user_data first, then root level
      const middleName = userData?.middle_name ?? applicationData.middle_name;
      if (middleName != null && middleName !== "") {
        setMiddleName(middleName);
        // Don't add to autoFilledFields - middleName should remain editable
      }

      const lastName = userData?.last_name || applicationData.last_name;
      if (lastName) {
        setLastName(lastName);
        filledFields.add("lastName");
      }

      // Handle suffix - check user_data first, then root level
      const suffixValue = userData?.suffix ?? applicationData.suffix;
      if (suffixValue != null) {
        setSuffix(suffixValue);
        // Don't add to autoFilledFields - suffix should remain editable
      }

      const mobileNo = userData?.mobile_no || applicationData.mobile_no;
      if (mobileNo) {
        // Ensure phone number is formatted correctly (handles 10, 12, and 13 digit numbers)
        const formattedPhone = formatPhoneNumber(mobileNo);
        setMobilePhone(formattedPhone);
        filledFields.add("mobilePhone");
      }

      const rawEmail = userData?.email || applicationData.email;
      // Fix: @noemail.com stripping — null placeholder emails on load to match Angular behavior
      const email = rawEmail?.includes('@noemail.com') ? '' : rawEmail;
      if (email) {
        setEmail(email);
        filledFields.add("email");
      }

      // Date of birth - convert to YYYY-MM-DD format for date input
      if (applicationData.birthdate) {
        const formattedDate = formatDateForInput(applicationData.birthdate);
        if (formattedDate) {
          // formatDateForInput returns YYYY-MM-DD which is perfect for date input
          setDateOfBirth(formattedDate);
          // Don't add to autoFilledFields - dateOfBirth should remain editable
        } else {
          // If formatDateForInput failed, try to parse MM/DD/YYYY and convert
          const dateStr = applicationData.birthdate.trim();
          if (dateStr.includes("/")) {
            const converted = convertToDateInputFormat(dateStr);
            if (converted) {
              setDateOfBirth(converted);
              // Don't add to autoFilledFields - dateOfBirth should remain editable
            }
          }
        }
      }

      // SSN - store raw digits (9 digits) for validation
      if (applicationData.security_number) {
        const digits = applicationData.security_number.replace(/\D/g, "");
        setSsn(digits.slice(0, 9)); // Store only 9 digits
        // Don't add to autoFilledFields - ssn should remain editable
      }

      // Communication preference
      if (applicationData.communication_preference) {
        const commPref = applicationData.communication_preference.toLowerCase();
        setCommPreference(
          commPref === "text" || commPref === "text msg" ? "text" : "email",
        );
        // Don't add to autoFilledFields - commPreference should remain editable
      }

      // Shared phone - check both root level and user_data
      // Check root level first, then fallback to user_data
      const sharedPhoneValue =
        applicationData.shared_phone_no ??
        applicationData.user_data?.shared_phone_no;
      if (
        sharedPhoneValue !== undefined &&
        sharedPhoneValue !== null &&
        sharedPhoneValue !== ""
      ) {
        // Handle string '1'/'0', boolean true/false, or number 1/0
        const isShared =
          sharedPhoneValue === "1" ||
          (typeof sharedPhoneValue === "number" && sharedPhoneValue === 1) ||
          (typeof sharedPhoneValue === "boolean" && sharedPhoneValue === true);
        setIsSharedPhone(isShared);
        // Don't add to autoFilledFields - sharedPhone should remain editable
      } else {
        // If value is null/undefined/empty, default to false (unchecked)
        setIsSharedPhone(false);
      }

      // Address fields
      if (applicationData.current_address) {
        const addr = applicationData.current_address;
        if (addr.street_address) {
          filledFields.add("currentAddress");
        }
        if (addr.apartment) {
          setAptUnit(addr.apartment || "");
          filledFields.add("aptUnit");
        }
        if (addr.house_no) {
          setStreetNumber(addr.house_no);
          filledFields.add("streetNumber");
        }
        if (addr.street) {
          setStreetName(addr.street);
          filledFields.add("streetName");
        }
        if (addr.street_type) {
          setStreetType(addr.street_type);
        }
        if (addr.city) {
          setCity(addr.city);
          filledFields.add("city");
        }
        if (addr.state) {
          setState(addr.state);
          filledFields.add("state");
        }
        if (addr.zip_code) {
          setZipCode(addr.zip_code);
          filledFields.add("zipCode");
        }
        // Set country first
        setCountry(ADDRESS_CONSTANTS.DEFAULT_COUNTRY);
        filledFields.add("country");

        // Show full format in Address field: Street Unit, City, State, Zip, Country
        setCurrentAddress(
          formatAddressDisplay({
            house_no: addr.house_no,
            street: addr.street,
            street_type: addr.street_type,
            apartment: addr.apartment,
            city: addr.city,
            state: addr.state,
            zip_code: addr.zip_code,
            country: addr.country,
          })
        );

        setOutsideUS(false);
        if (addr.address_type) {
          // Map API address_type to UI residence type
          const residenceTypeMap: Record<string, "rent" | "own" | "family"> = {
            RENT: "rent",
            OWN: "own",
            FAMILY: "family",
          };
          const mappedType = residenceTypeMap[addr.address_type];
          if (mappedType) {
            setResidenceType(mappedType);
          }
        }

        // Populate address_data fields
        if (addr.address_data) {
          const addrData = addr.address_data;

          if (addr.address_type === "RENT") {
            if (addrData.currently_living !== undefined) {
              // Handle both string "1"/"0" and boolean values
              const isCurrentlyLiving =
                typeof addrData.currently_living === "string"
                  ? addrData.currently_living === "1"
                  : addrData.currently_living;
              setStillHereRent(isCurrentlyLiving);
              setStillHereOwn(isCurrentlyLiving);
              setStillHereFamily(isCurrentlyLiving);
            }
            if (addrData.moveIn_date) {
              const ymd = normalizeDateFromAPI(addrData.moveIn_date);
              const [year, month, day] = ymd.split("-");
              if (year && month && day) {
                setMoveInDateRent(`${month}/${day}/${year}`);
              }
            }
            if (addrData.moveOut_date) {
              const ymd = normalizeDateFromAPI(addrData.moveOut_date);
              const [year, month, day] = ymd.split("-");
              if (year && month && day) {
                setLeaseEndDate(`${month}/${day}/${year}`);
              }
            }
            if (addrData.landlord_name) setLandlordName(addrData.landlord_name);
            if (addrData.landlord_email)
              setLandlordEmail(addrData.landlord_email);
            if (addrData.landlord_mobile_no)
              setLandlordMobile(formatPhoneNumber(addrData.landlord_mobile_no));
            if (addrData.rent_amount) setRentAmount(addrData.rent_amount);
            // Angular parity: load paid_rent (months at address) for current RENT
            const paidRent = (addrData as { paid_rent?: string | number }).paid_rent;
            if (paidRent != null) setMonthsPaidRent(String(paidRent));
          } else if (addr.address_type === "OWN") {
            if (addrData.currently_living !== undefined) {
              // Handle both string "1"/"0" and boolean values
              const isCurrentlyLiving =
                typeof addrData.currently_living === "string"
                  ? addrData.currently_living === "1"
                  : addrData.currently_living;
              setStillHereRent(isCurrentlyLiving);
              setStillHereOwn(isCurrentlyLiving);
              setStillHereFamily(isCurrentlyLiving);
            }
            if (addrData.moveIn_date) {
              const ymd = normalizeDateFromAPI(addrData.moveIn_date);
              const [year, month, day] = ymd.split("-");
              if (year && month && day) {
                setMoveInDateOwn(`${month}/${day}/${year}`);
              }
            }
            if (addrData.moveOut_date) {
              const ymd = normalizeDateFromAPI(addrData.moveOut_date);
              const [year, month, day] = ymd.split("-");
              if (year && month && day) {
                setPlannedMoveOutDate(`${month}/${day}/${year}`);
              }
            }
            if (addrData.rent_amount)
              setMonthlyMortgagePayment(addrData.rent_amount);
            const monthReside = addrData.month_reside;
            if (monthReside != null) setMonthsResided(String(monthReside));
          } else if (addr.address_type === "FAMILY") {
            if (addrData.currently_living !== undefined) {
              // Handle both string "1"/"0" and boolean values
              const isCurrentlyLiving =
                typeof addrData.currently_living === "string"
                  ? addrData.currently_living === "1"
                  : addrData.currently_living;
              setStillHereRent(isCurrentlyLiving);
              setStillHereOwn(isCurrentlyLiving);
              setStillHereFamily(isCurrentlyLiving);
            }
            if (addrData.moveIn_date) {
              const ymd = normalizeDateFromAPI(addrData.moveIn_date);
              const [year, month, day] = ymd.split("-");
              if (year && month && day) {
                setMoveInDateFamily(`${month}/${day}/${year}`);
              }
            }
            if (addrData.moveOut_date) {
              const ymd = normalizeDateFromAPI(addrData.moveOut_date);
              const [year, month, day] = ymd.split("-");
              if (year && month && day) {
                setPlannedMoveOutDateFamily(`${month}/${day}/${year}`);
              }
            }
            if (addrData.landlord_name) setContactName(addrData.landlord_name);
            if (addrData.landlord_email)
              setContactEmail(addrData.landlord_email);
            if (addrData.landlord_mobile_no)
              setContactMobile(formatPhoneNumber(addrData.landlord_mobile_no));
            if (addrData.landlord_website)
              setContactWebsite(addrData.landlord_website);
            if (addrData.landlord_relationship_type)
              setContactRelationship(addrData.landlord_relationship_type);
            if (addrData.rent_amount) setRentAmountFamily(addrData.rent_amount);
          }
        }
      }

      // Previous address: load previous entries from API; re-run when applicant changes (revisit) so we don't show stale/duplicated addresses
      const prevAddrs = applicationData.previous_address;
      const currentApplicantId = applicationData.applicant_id ?? null;
      const applicantChanged = lastLoadedApplicantIdRef.current !== currentApplicantId;
      if (applicantChanged) {
        lastLoadedApplicantIdRef.current = currentApplicantId;
        prevAddressInitialized.current = false;
        // When switching applicant, if new applicant has no previous addresses, start with one empty slot
        if (!prevAddrs || !Array.isArray(prevAddrs) || prevAddrs.length === 0) {
          setPreviousAddresses([createInitialAddressState()]);
        }
      }
      if (prevAddrs && Array.isArray(prevAddrs) && prevAddrs.length > 0) {
        prevAddressInitialized.current = true;
        // Deduplicate by address only (same street/city/zip = same address) so duplicate entries from API show as one card
        const seenKeys = new Set<string>();
        const deduped = prevAddrs.filter((addr: Address) => {
          const house = String(addr.house_no ?? "").trim();
          const street = String(addr.street ?? "").trim();
          const city = String(addr.city ?? "").trim();
          const zip = String(addr.zip_code ?? "").trim();
          const country = String(addr.country ?? "").trim();
          const key = [house, street, city, zip, country].join("|").toLowerCase();
          if (seenKeys.has(key)) return false;
          seenKeys.add(key);
          return true;
        });
        const mappedPrevAddresses = deduped.map((addr) => mapApiAddressToFormState(addr));
        setPreviousAddresses(mappedPrevAddresses);
      }

      // Revisit: offer to import old application addresses as previous (matches Angular)
      const oldApp = applicationData.oldapplication;
      const hasOldAddressData =
        oldApp &&
        ((oldApp.current_address && (oldApp.current_address as Address).address_data) ||
          (oldApp.previous_address && oldApp.previous_address.length > 0));
      const noPreviousAddressYet = !prevAddrs || prevAddrs.length === 0;
      if (
        noPreviousAddressYet &&
        oldApp?.user_data &&
        hasOldAddressData &&
        !importDialogDismissed.current
      ) {
        importDialogDismissed.current = true;
        setShowImportPreviousAddressDialog(true);
      }

      setAutoFilledFields(filledFields);

      // Check if personal profile has been saved (if dateOfBirth and ssn are filled)
      // This ensures fields remain disabled after page refresh if data was previously saved
      const hasDateOfBirth = Boolean(applicationData.birthdate);
      const hasSsn = Boolean(applicationData.security_number);
      if (hasDateOfBirth && hasSsn) {
        setIsPersonalProfileSaved(true);
      }

      // Load alternate addresses (other addresses for credit search)
      if (
        applicationData.alternate_address &&
        Array.isArray(applicationData.alternate_address)
      ) {
        const loadedAddresses = applicationData.alternate_address.map(
          (addr, index) => ({
            id: `other-addr-${addr.ra_id || index}-${Date.now()}`,
            address: formatAddressDisplay(addr),
            streetNumber: addr.house_no || "",
            streetName: addr.street || "",
            streetType: addr.street_type || "",
            aptUnit: addr.apartment || "",
            city: addr.city || "",
            state: addr.state || "",
            zipCode: addr.zip_code || "",
            country: addr.country || ADDRESS_CONSTANTS.DEFAULT_COUNTRY,
            outsideUS:
              typeof addr.outside_us === "string"
                ? addr.outside_us === "1"
                : addr.outside_us || false,
            showDetails: false, // Start collapsed
            ra_id: addr.ra_id || null,
          }),
        );
        setOtherAddresses(loadedAddresses);

        // Track original alternate address IDs (those that have ra_id) for deletion tracking
        // Ensure ra_id is converted to number (API may return as string)
        const originalIds = new Set<number>();
        applicationData.alternate_address.forEach((addr) => {
          if (addr.ra_id) {
            const raIdNumber =
              typeof addr.ra_id === "string"
                ? parseInt(addr.ra_id, 10)
                : addr.ra_id;
            if (!isNaN(raIdNumber)) {
              originalIds.add(raIdNumber);
            }
          }
        });
        setOriginalAlternateAddressIds(originalIds);
      } else {
        // Reset if no alternate addresses
        setOtherAddresses([]);
        setOriginalAlternateAddressIds(new Set());
      }

      // Initialize noAlternateAddress from API response
      // Handle both string "1"/"0" and boolean values (like currently_living)
      if (applicationData.non_us_address !== undefined) {
        const nonUsAddressValue =
          typeof applicationData.non_us_address === "string"
            ? applicationData.non_us_address === "1"
            : applicationData.non_us_address;
        setNoAlternateAddress(nonUsAddressValue);
      }
    }
  }, [applicationData]);

  // Register validator for Tab Validation
  useEffect(() => {
    const validateAboutTab = (): { isValid: boolean; message?: string } => {
      const errorMessages: string[] = [];
      const emailOk = (v: string) =>
        /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(v.trim());
      const phoneOk = (v: string) =>
        /^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(
          v.trim().replace(/\D/g, ""),
        );

      // 1. Validate Personal Profile
      if (!firstName || firstName.trim() === "")
        errorMessages.push("First Name is required");
      if (!lastName || lastName.trim() === "")
        errorMessages.push("Last Name is required");
      if (!mobilePhone || mobilePhone.trim() === "")
        errorMessages.push("Mobile Phone is required");

      const personalResult = validatePersonalProfile();
      if (!personalResult.isValid) {
        if (personalResult.errors.dateOfBirth) {
          errorMessages.push(
            `Date of Birth: ${personalResult.errors.dateOfBirth}`,
          );
        } else if (!dateOfBirth) {
          errorMessages.push(APPLICANT_CONTENT.errors.fieldRequired.dateOfBirth);
        }

        if (personalResult.errors.ssn) {
          errorMessages.push(`SSN/ITIN: ${personalResult.errors.ssn}`);
        } else if (!ssn) {
          errorMessages.push("SSN/ITIN is required");
        }

        // Fallback checks if specific errors didn't catch it
        if (errorMessages.length === 0) {
          errorMessages.push(
            "Personal Profile information is missing or invalid",
          );
        }
      }

      // 2. Validate Address (keep in sync with Save & Continue + isCurrentAddressValid)
      if (!streetNumber || !streetNumber.trim())
        errorMessages.push("Street Number is required");
      if (!streetName || !streetName.trim())
        errorMessages.push("Street Name is required");
      if (!city || !city.trim()) errorMessages.push(APPLICANT_CONTENT.errors.payment.city);
      if (!state || !state.trim()) errorMessages.push("State is required");
      const curZipDigits = (zipCode || "").replace(/\D/g, "");
      if (!curZipDigits)
        errorMessages.push("Zip Code is required");
      else if (curZipDigits.length !== 5 && curZipDigits.length !== 6)
        errorMessages.push("Zip Code must be 5 or 6 digits");
      if (!residenceType) errorMessages.push("Residence Type is required");

      if (residenceType === "rent") {
        if (!landlordName || !landlordName.trim())
          errorMessages.push("Landlord Name is required");
        if (!landlordEmail || !landlordEmail.trim()) {
          errorMessages.push("Landlord Email is required");
        } else if (!emailOk(landlordEmail)) {
          errorMessages.push("Landlord Email: Enter Valid Email");
        }
        if (!landlordMobile || !landlordMobile.trim()) {
          errorMessages.push("Landlord Mobile is required");
        } else if (!phoneOk(landlordMobile)) {
          errorMessages.push("Landlord Mobile: Enter Valid Phone Number");
        }
        if (!moveInDateRent) errorMessages.push("Move In Date is required");
        if (!rentAmount || !rentAmount.trim())
          errorMessages.push("Rent Amount is required");
        if (!stillHereRent && !(leaseEndDate && leaseEndDate.trim()))
          errorMessages.push("Lease End Date is required");
      } else if (residenceType === "own") {
        if (!moveInDateOwn) errorMessages.push("Move In Date is required");
        if (!monthlyMortgagePayment || !monthlyMortgagePayment.trim())
          errorMessages.push("Monthly Mortgage Payment is required");
        if (!stillHereOwn && !(plannedMoveOutDate && plannedMoveOutDate.trim()))
          errorMessages.push("Planned Move Out Date is required");
      } else if (residenceType === "family") {
        if (!contactName || !contactName.trim())
          errorMessages.push("Contact Name is required");
        if (!contactRelationship || !contactRelationship.trim())
          errorMessages.push("Contact Relationship is required");
        if (!contactEmail || !contactEmail.trim()) {
          errorMessages.push("Contact Email is required");
        } else if (!emailOk(contactEmail)) {
          errorMessages.push("Contact Email: Enter Valid Email");
        }
        if (!contactMobile || !contactMobile.trim()) {
          errorMessages.push("Contact Mobile is required");
        } else if (!phoneOk(contactMobile)) {
          errorMessages.push("Contact Mobile: Enter Valid Phone Number");
        }
        if (!moveInDateFamily) errorMessages.push("Move In Date is required");
        if (
          !stillHereFamily &&
          !(plannedMoveOutDateFamily && plannedMoveOutDateFamily.trim())
        )
          errorMessages.push("Planned Move Out Date is required");
      }

      // 3. Previous addresses (all rows) when current is valid and duration < 24 months
      if (
        shouldEnforceAddressHistoryRules(
          isCurrentAddressValid(),
          shouldShowPreviousAddress(),
        )
      ) {
        previousAddresses.forEach((addr, index) => {
          const p = `Previous Address ${index + 1}`;
          if (!addr.residenceType) {
            errorMessages.push(`${p}: Residence type is required.`);
            return;
          }
          if (!addr.streetNumber?.trim())
            errorMessages.push(`${p}: Street Number is required`);
          if (!addr.streetName?.trim())
            errorMessages.push(`${p}: Street Name is required`);
          if (!addr.city?.trim()) errorMessages.push(`${p}: City is required`);
          if (!addr.state?.trim()) errorMessages.push(`${p}: State is required`);
          const z = (addr.zipCode || "").replace(/\D/g, "");
          if (!z) errorMessages.push(`${p}: Zip Code is required`);
          else if (z.length !== 5 && z.length !== 6)
            errorMessages.push(`${p}: Zip Code must be 5 or 6 digits`);

          if (addr.residenceType === "rent") {
            if (!addr.landlordName?.trim())
              errorMessages.push(`${p}: Landlord Name is required`);
            if (!addr.moveInDateRent)
              errorMessages.push(`${p}: Move In Date is required`);
            if (!addr.landlordEmail?.trim()) {
              errorMessages.push(`${p}: Landlord Email is required`);
            } else if (!emailOk(addr.landlordEmail)) {
              errorMessages.push(`${p}: Landlord Email — Enter Valid Email`);
            }
            if (!addr.landlordMobile?.trim()) {
              errorMessages.push(`${p}: Landlord Mobile is required`);
            } else if (!phoneOk(addr.landlordMobile)) {
              errorMessages.push(`${p}: Landlord Mobile — Enter Valid Phone Number`);
            }
            if (!addr.rentAmount?.trim())
              errorMessages.push(`${p}: Rent Amount is required`);
          } else if (addr.residenceType === "own") {
            if (!addr.moveInDateOwn)
              errorMessages.push(`${p}: Move In Date is required`);
            // Previous-address OWN does not capture mortgage payment (field is not
            // rendered for previous addresses; Angular parity uses month_reside only).
            if (!addr.monthsResided?.trim())
              errorMessages.push(`${p}: Number of months you resided is required`);
          } else if (addr.residenceType === "family") {
            if (!addr.contactName?.trim())
              errorMessages.push(`${p}: Contact Name is required`);
            if (!addr.contactRelationship?.trim())
              errorMessages.push(`${p}: Contact Relationship is required`);
            if (!addr.contactEmail?.trim()) {
              errorMessages.push(`${p}: Contact Email is required`);
            } else if (!emailOk(addr.contactEmail)) {
              errorMessages.push(`${p}: Contact Email — Enter Valid Email`);
            }
            if (!addr.contactMobile?.trim()) {
              errorMessages.push(`${p}: Contact Mobile is required`);
            } else if (!phoneOk(addr.contactMobile)) {
              errorMessages.push(`${p}: Contact Mobile — Enter Valid Phone Number`);
            }
            if (!addr.moveInDateFamily)
              errorMessages.push(`${p}: Move In Date is required`);
          }
        });
      }

      if (errorMessages.length > 0) {
        return {
          isValid: false,
          message: errorMessages.join("\n"),
        };
      }

      return { isValid: true };
    };

    registerValidator("about", validateAboutTab);
    return () => unregisterValidator("about");
  }, [
    registerValidator,
    unregisterValidator,
    firstName,
    lastName,
    mobilePhone,
    dateOfBirth,
    ssn,
    streetNumber,
    streetName,
    city,
    state,
    zipCode,
    residenceType,
    landlordName,
    landlordEmail,
    landlordMobile,
    moveInDateRent,
    rentAmount,
    stillHereRent,
    leaseEndDate,
    moveInDateOwn,
    monthlyMortgagePayment,
    stillHereOwn,
    plannedMoveOutDate,
    contactName,
    contactRelationship,
    contactEmail,
    contactMobile,
    moveInDateFamily,
    stillHereFamily,
    plannedMoveOutDateFamily,
    previousAddresses,
    currentAddressState,
  ]);

  // Build the About tab's full API payload from current form state. Pure (no setState/nav/
  // dialogs); single source of truth for Save & Continue/Exit and auto-save. Returns null
  // when the address can't be built (mirrors handleSaveAllData guards) so callers can bail.
  const buildAboutUpdateData = (): UpdateApplicationRequest | null => {
    // Build current_address according to API structure
    const currentAddressData = buildCurrentAddress();
    if (!currentAddressData) {
      return null;
    }

    // Address history for the last 2 years is mandatory: when current address < 24 months, previous address is required
    let apiPreviousAddresses: Address[] | null = null;
    if (shouldShowPreviousAddress()) {
      const allValid = previousAddresses.every((addr) => addr.residenceType);
      if (!allValid) {
        return null;
      }

      apiPreviousAddresses = previousAddresses
        .map((addr) => buildAddressData(addr, addr.ra_id ?? null, true))
        .filter((a): a is Address => a != null);

      if (!apiPreviousAddresses || apiPreviousAddresses.length === 0) {
        return null;
      }
    }

    // Format date for API - send YYYY-MM-DD to match Angular (validateData → formatDate → YYYY-MM-DD)
    let dobForApi = "";
    if (dateOfBirth) {
      // Validate date before saving
      const validation = validateDateOfBirth(dateOfBirth);
      if (!validation.isValid) {
        return null;
      }
      // dateOfBirth is already YYYY-MM-DD; send as-is to match Angular
      dobForApi = dateOfBirth;
    }

    // Determine if address is outside US
    const countryCode = (currentAddressState.country || "US").toUpperCase();
    const isOutsideUS = countryCode !== "US" || outsideUS;

    // Prepare update data with personal profile fields AND address data
    const updateData: UpdateApplicationRequest = {
      ...residentTabValidation("about"),
      // Personal profile fields
      first_name: firstName || undefined,
      middle_name: middleName || "",
      last_name: lastName || undefined,
      suffix: suffix || undefined,
      mobile_no: unformatPhoneNumber(mobilePhone) || undefined,
      shared_phone_no: isSharedPhone ? "1" : "0",
      email: email || undefined,
      communication_preference:
        commPreference === "text"
          ? APPLICANT_CONTENT.application.sections.personalProfile.fields.textMsg
          : APPLICANT_CONTENT.application.sections.personalProfile.fields.email,
      birthdate: dobForApi || undefined,
      security_number: ssn || undefined,
      enter_date: {}, // Required empty object as per API structure
      is_mobile_no: mobilePhone ? "1" : "0",
      update_via_sms: "0", // Default to '0' as per API structure
      // Address fields
      current_address: currentAddressData,
      // non_us_address logic mirrors handleSaveAllData
      ...(isOutsideUS
        ? { non_us_address: noAlternateAddress ? "1" : "0" }
        : { non_us_address: "0" }),
    };

    // Remove undefined fields from personal profile
    Object.keys(updateData).forEach((key) => {
      if (
        key !== "current_address" &&
        key !== "previous_address" &&
        key !== "alternate_address" &&
        key !== "non_us_address" &&
        key !== "enter_date" &&
        key !== "shared_phone_no" &&
        key !== "is_mobile_no" &&
        key !== "update_via_sms" &&
        key !== "communication_preference" &&
        key !== "validation_scope" &&
        key !== "validation_tab" &&
        key !== "validation"
      ) {
        if (updateData[key as keyof typeof updateData] === undefined) {
          delete updateData[key as keyof typeof updateData];
        }
      }
    });

    // Angular parity: when current duration >= 24 months, send empty previous_address; otherwise send array
    if (shouldShowPreviousAddress() && apiPreviousAddresses) {
      updateData.previous_address = apiPreviousAddresses;
    } else {
      updateData.previous_address = [];
    }

    // Add alternate addresses (other addresses for credit search)
    if (otherAddresses.length > 0) {
      updateData.alternate_address = otherAddresses.map((addr) => ({
        ra_id: addr.ra_id || null,
        street_address: buildAlternateAddressStreetAddress(addr),
        outside_us: addr.outsideUS,
        house_no: addr.streetNumber,
        street: addr.streetName,
        street_type: addr.streetType || "",
        apartment: addr.aptUnit || null,
        city: addr.city,
        state: addr.state,
        zip_code: addr.zipCode,
        country: addr.country,
        address_type: undefined, // Alternate addresses don't have a residence type
        address_data: undefined,
      }));
    } else {
      // If no addresses, send empty array
      updateData.alternate_address = [];
    }

    // Calculate deleted alternate addresses (those that had ra_id but are no longer in otherAddresses)
    const currentAlternateAddressIds = new Set<number>();
    otherAddresses.forEach((addr) => {
      if (addr.ra_id) {
        const raIdNumber =
          typeof addr.ra_id === "string" ? parseInt(addr.ra_id, 10) : addr.ra_id;
        if (!isNaN(raIdNumber)) {
          currentAlternateAddressIds.add(raIdNumber);
        }
      }
    });

    const deletedAlternateAddressIds: number[] = [];
    originalAlternateAddressIds.forEach((raId) => {
      if (!currentAlternateAddressIds.has(raId)) {
        deletedAlternateAddressIds.push(raId);
      }
    });

    // Include deleted addresses in the request if any were deleted
    if (deletedAlternateAddressIds.length > 0) {
      updateData.deleted = {
        ...(updateData.deleted || {}),
        alternate_address: deletedAlternateAddressIds,
      };
    }

    return updateData;
  };

  // Auto-save-on-tab-change (Angular submitForm('fromtab') parity): persist the current
  // tab without navigating/dialogs/validation popups. Dirty-guarded by useTabAutoSave.
  const autoSaveAbout = async (): Promise<void> => {
    if (!applicationData?.applicant_id) return;
    const updateData = buildAboutUpdateData();
    if (!updateData) return;
    await dispatch(
      updateApplication({
        applicantId: applicationData.applicant_id,
        updateData,
      }),
    ).unwrap();
    await dispatch(fetchApplicationById(applicationData.applicant_id)).unwrap();
  };

  useTabAutoSave({
    name: "about",
    snapshot: buildAboutUpdateData(),
    loadedKey: applicationData?.applicant_id,
    save: autoSaveAbout,
    isReadOnly,
  });

  // Show loading state
  if (isLoading && !applicationData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="text-gray-600">
            {APPLICANT_CONTENT.application.messages.loading}
          </span>
        </div>
      </div>
    );
  }

  // Validate personal profile fields (only DOB and SSN)
  const validatePersonalProfile = (): {
    isValid: boolean;
    errors: typeof personalProfileErrors;
  } => {
    const errors: typeof personalProfileErrors = {};

    // Date of Birth validation
    if (!dateOfBirth || dateOfBirth.trim() === "") {
      errors.dateOfBirth = APPLICANT_CONTENT.errors.fieldRequired.dateOfBirth;
    } else {
      const validation = validateDateOfBirth(dateOfBirth);
      if (!validation.isValid) {
        errors.dateOfBirth = validation.error || APPLICANT_CONTENT.errors.invalidDateOfBirth;
      }
    }

    // SSN or ITIN validation
    if (!ssn || ssn.trim() === "") {
      errors.ssn = APPLICANT_CONTENT.errors.fieldRequired.ssnOrItin;
    } else {
      const digits = ssn.replace(/\D/g, "");
      if (digits.length !== 9) {
        errors.ssn = APPLICANT_CONTENT.errors.fieldRequired.ssnOrItinDigits;
      }
    }

    setPersonalProfileErrors(errors);
    return { isValid: Object.keys(errors).length === 0, errors };
  };

  // Validate individual field (for dynamic validation - only DOB and SSN)
  const validateField = (
    fieldName: keyof typeof personalProfileErrors,
    value: string,
  ) => {
    let error: string | undefined;

    switch (fieldName) {
      case "dateOfBirth":
        if (!value || value.trim() === "") {
          error = APPLICANT_CONTENT.errors.fieldRequired.dateOfBirth;
        } else {
          const validation = validateDateOfBirth(value);
          if (!validation.isValid) {
            error = validation.error || APPLICANT_CONTENT.errors.invalidDateOfBirth;
          }
        }
        break;
      case "ssn":
        if (!value || value.trim() === "") {
          error = APPLICANT_CONTENT.errors.fieldRequired.ssnOrItin;
        } else {
          const digits = value.replace(/\D/g, "");
          if (digits.length !== 9) {
            error = APPLICANT_CONTENT.errors.fieldRequired.ssnOrItinDigits;
          }
        }
        break;
    }

    // Update only this field's error
    setPersonalProfileErrors((prev) => {
      if (error) {
        return { ...prev, [fieldName]: error };
      } else {
        const { [fieldName]: _removed, ...rest } = prev;
        return rest;
      }
    });
  };

  // Reusable function to handle full form validation and saving
  // target: 'continue' (navigates to next tab), 'exit' (navigates to dashboard), 'none' (just saves)
  const handleSaveAllData = async (target: "continue" | "exit" | "none") => {
    // Only proceed if personal profile is valid
    const profileValidation = validatePersonalProfile();
    if (!profileValidation.isValid) {
      setPersonalProfileErrors(profileValidation.errors);
      // Format error messages for modal
      const errorMessages: string[] = [];
      if (profileValidation.errors.dateOfBirth)
        errorMessages.push(
          `Date of Birth: ${profileValidation.errors.dateOfBirth}`,
        );
      if (profileValidation.errors.ssn)
        errorMessages.push(`SSN: ${profileValidation.errors.ssn}`);

      setValidationModalMessage(errorMessages.join("\n"));
      setShowPersonalProfileValidationModal(true);
      return;
    }

    // Clear personal profile errors if valid
    setPersonalProfileErrors({});

    if (target === "continue") {
      setIsSavingAndContinuing(true);
    } else if (target === "exit") {
      setIsSavingAndExiting(true);
    }
    const errors: typeof fieldErrors = {};

    // Validate current address
    if (!streetNumber || !streetNumber.trim()) {
      errors.streetNumber = APPLICANT_CONTENT.errors.fieldRequired.streetNumber;
    }

    if (!streetName || !streetName.trim()) {
      errors.streetName = APPLICANT_CONTENT.errors.fieldRequired.streetName;
    }

    if (!city || !city.trim()) {
      errors.city = APPLICANT_CONTENT.errors.fieldRequired.city;
    }

    if (!state || !state.trim()) {
      errors.state = APPLICANT_CONTENT.errors.fieldRequired.state;
    }

    const digitsOnly = zipCode.replace(/\D/g, "");
    if (!digitsOnly) {
      errors.zipCode = APPLICANT_CONTENT.errors.fieldRequired.zipCode;
    } else if (digitsOnly.length !== 5 && digitsOnly.length !== 6) {
      errors.zipCode = APPLICANT_CONTENT.errors.fieldRequired.zipCodeLength;
    }

    if (!residenceType) {
      errors.residenceType = APPLICANT_CONTENT.errors.fieldRequired.residenceType;
    }

    if (residenceType === "rent") {
      if (!moveInDateRent) {
        errors.moveInDateRent = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
      }
      if (!landlordName || !landlordName.trim()) {
        errors.landlordName = APPLICANT_CONTENT.errors.fieldRequired.landlordName;
      }
      if (!landlordEmail || !landlordEmail.trim()) {
        errors.landlordEmail = APPLICANT_CONTENT.errors.fieldRequired.landlordEmail;
      }
      if (!landlordMobile || !landlordMobile.trim()) {
        errors.landlordMobile = APPLICANT_CONTENT.errors.fieldRequired.landlordMobile;
      }
      if (!rentAmount || !rentAmount.trim()) {
        errors.rentAmount = APPLICANT_CONTENT.errors.fieldRequired.rentAmount;
      }
      if (
        !stillHereRent &&
        !(leaseEndDate && leaseEndDate.trim())
      ) {
        errors.leaseEndDate = APPLICANT_CONTENT.errors.fieldRequired.leaseEndDate;
      }
    } else if (residenceType === "own") {
      if (!moveInDateOwn) {
        errors.moveInDateOwn = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
      }
      if (!monthlyMortgagePayment || !monthlyMortgagePayment.trim()) {
        errors.monthlyMortgagePayment = APPLICANT_CONTENT.errors.fieldRequired.monthlyMortgagePayment;
      }
      if (
        !stillHereOwn &&
        !(plannedMoveOutDate && plannedMoveOutDate.trim())
      ) {
        errors.plannedMoveOutDate = APPLICANT_CONTENT.errors.fieldRequired.plannedMoveOutDate;
      }
    } else if (residenceType === "family") {
      if (!moveInDateFamily) {
        errors.moveInDateFamily = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
      }
      if (!contactName || !contactName.trim()) {
        errors.contactName = APPLICANT_CONTENT.errors.fieldRequired.landlordName;
      }
      if (!contactEmail || !contactEmail.trim()) {
        errors.contactEmail = APPLICANT_CONTENT.errors.fieldRequired.landlordEmail;
      }
      if (!contactMobile || !contactMobile.trim()) {
        errors.contactMobile = APPLICANT_CONTENT.errors.fieldRequired.landlordMobile;
      }
      if (!contactRelationship || !contactRelationship.trim()) {
        errors.contactRelationship = APPLICANT_CONTENT.errors.fieldRequired.contactRelationship;
      }
      if (
        !stillHereFamily &&
        !(
          plannedMoveOutDateFamily &&
          plannedMoveOutDateFamily.trim()
        )
      ) {
        errors.plannedMoveOutDateFamily = APPLICANT_CONTENT.errors.fieldRequired.plannedMoveOutDate;
      }
    }

    // Previous-address / 24-month checks only when current is valid (Angular: current_address.valid && checkPreviousAddress())
    if (
      shouldEnforceAddressHistoryRules(
        isCurrentAddressValid(),
        shouldShowPreviousAddress(),
      )
    ) {
      previousAddresses.forEach((addr, index) => {
        const pfx = index === 0 ? "prev" : `prev${index}-`;
        const errorKey = (key: string) => index === 0 ? `prev${key.charAt(0).toUpperCase() + key.slice(1)}` : `${pfx}${key}`;

        if (!addr.residenceType) {
          errors[errorKey("residenceType")] = APPLICANT_CONTENT.errors.fieldRequired.residenceType;
        }

        if (!addr.streetNumber || !addr.streetNumber.trim()) {
          errors[errorKey("streetNumber")] = APPLICANT_CONTENT.errors.fieldRequired.streetNumber;
        }

        if (!addr.streetName || !addr.streetName.trim()) {
          errors[errorKey("streetName")] = APPLICANT_CONTENT.errors.fieldRequired.streetName;
        }

        if (!addr.city || !addr.city.trim()) {
          errors[errorKey("city")] = APPLICANT_CONTENT.errors.fieldRequired.city;
        }

        if (!addr.state || !addr.state.trim()) {
          errors[errorKey("state")] = APPLICANT_CONTENT.errors.fieldRequired.state;
        }

        const prevDigitsOnly = (addr.zipCode || "").replace(/\D/g, "");
        if (!prevDigitsOnly) {
          errors[errorKey("zipCode")] = APPLICANT_CONTENT.errors.fieldRequired.zipCode;
        } else if (prevDigitsOnly.length !== 5 && prevDigitsOnly.length !== 6) {
          errors[errorKey("zipCode")] = APPLICANT_CONTENT.errors.fieldRequired.zipCodeLength;
        }

        if (addr.residenceType === "rent") {
          if (!addr.moveInDateRent) {
            errors[errorKey("moveInDateRent")] = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
          }
          if (!addr.landlordName || !addr.landlordName.trim()) {
            errors[errorKey("landlordName")] = APPLICANT_CONTENT.errors.fieldRequired.landlordName;
          }
          if (!addr.landlordEmail || !addr.landlordEmail.trim()) {
            errors[errorKey("landlordEmail")] = APPLICANT_CONTENT.errors.fieldRequired.landlordEmail;
          }
          if (!addr.landlordMobile || !addr.landlordMobile.trim()) {
            errors[errorKey("landlordMobile")] = APPLICANT_CONTENT.errors.fieldRequired.landlordMobile;
          }
          if (!addr.rentAmount || !addr.rentAmount.trim()) {
            errors[errorKey("rentAmount")] = APPLICANT_CONTENT.errors.fieldRequired.rentAmount;
          }
        } else if (addr.residenceType === "own") {
          if (!addr.moveInDateOwn) {
            errors[errorKey("moveInDateOwn")] = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
          }
          // Previous-address OWN does not capture mortgage payment (field is not
          // rendered for previous addresses; Angular parity uses month_reside only).
          if (!addr.monthsResided || !addr.monthsResided.trim()) {
            errors[errorKey("monthsResided")] = APPLICANT_CONTENT.errors.fieldRequired.monthsResided;
          }
        } else if (addr.residenceType === "family") {
          if (!addr.contactName || !addr.contactName.trim()) {
            errors[errorKey("contactName")] = APPLICANT_CONTENT.errors.fieldRequired.landlordName;
          }
          if (!addr.contactEmail || !addr.contactEmail.trim()) {
            errors[errorKey("contactEmail")] = APPLICANT_CONTENT.errors.fieldRequired.landlordEmail;
          }
          if (!addr.contactMobile || !addr.contactMobile.trim()) {
            errors[errorKey("contactMobile")] = APPLICANT_CONTENT.errors.fieldRequired.landlordMobile;
          }
          if (!addr.contactRelationship || !addr.contactRelationship.trim()) {
            errors[errorKey("contactRelationship")] = APPLICANT_CONTENT.errors.fieldRequired.contactRelationship;
          }
          if (!addr.moveInDateFamily) {
            errors[errorKey("moveInDateFamily")] = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
          }
        }
      });

      // Block if total address history is less than 24 months — only when there are no field-level errors yet
      const totalDuration = calculateTotalAddressDuration();
      if (totalDuration < 24 && Object.keys(errors).length === 0) {
        appendPreviousAddressSlotIfAngularAllows();
        setValidationModalMessage(
          `You must provide at least 24 months of address history to proceed.\nCurrent history provided: ${totalDuration} months.\nPlease add previous addresses to cover the remaining ${24 - totalDuration} months.`
        );
        setShowAddressValidationModal(true);
        setIsSavingAndContinuing(false);
        setIsSavingAndExiting(false);
        return;
      }
    }

    // Validate alternate addresses (other addresses) if they exist
    // Only validate if the address has been started (has at least one field filled)
    const alternateAddressErrors: Record<
      string,
      {
        streetNumber?: string;
        streetName?: string;
        city?: string;
        state?: string;
        zipCode?: string;
      }
    > = {};

    otherAddresses.forEach((addr) => {
      // Check if address has been started (has any field filled)
      const hasStarted =
        addr.address ||
        addr.streetNumber ||
        addr.streetName ||
        addr.city ||
        addr.state ||
        addr.zipCode ||
        addr.country;

      if (hasStarted) {
        const addrErrors: (typeof alternateAddressErrors)[string] = {};

        // Validate all required fields for this alternate address
        if (!addr.streetNumber || !addr.streetNumber.trim()) {
          addrErrors.streetNumber = APPLICANT_CONTENT.errors.fieldRequired.streetNumber;
        }

        if (!addr.streetName || !addr.streetName.trim()) {
          addrErrors.streetName = APPLICANT_CONTENT.errors.fieldRequired.streetName;
        }

        if (!addr.city || !addr.city.trim()) {
          addrErrors.city = APPLICANT_CONTENT.errors.fieldRequired.city;
        }

        if (!addr.state || !addr.state.trim()) {
          addrErrors.state = APPLICANT_CONTENT.errors.fieldRequired.state;
        }

        const digitsOnly = (addr.zipCode || "").replace(/\D/g, "");
        if (!digitsOnly) {
          addrErrors.zipCode = APPLICANT_CONTENT.errors.fieldRequired.zipCode;
        } else if (digitsOnly.length !== 5 && digitsOnly.length !== 6) {
          addrErrors.zipCode = APPLICANT_CONTENT.errors.fieldRequired.zipCodeLength;
        }

        // Only add to errors if there are any errors for this address
        if (Object.keys(addrErrors).length > 0) {
          alternateAddressErrors[addr.id] = addrErrors;
        }
      }
    });

    // Set alternate address errors
    if (Object.keys(alternateAddressErrors).length > 0) {
      setOtherAddressFieldErrors(alternateAddressErrors);
    } else {
      setOtherAddressFieldErrors({});
    }

    // Check if there are any validation errors (current/previous address or alternate addresses)
    const hasAlternateAddressErrors =
      Object.keys(alternateAddressErrors).length > 0;
    const hasCurrentAddressErrors = Object.keys(errors).length > 0;

    if (hasCurrentAddressErrors || hasAlternateAddressErrors) {
      setFieldErrors(errors);
      setIsSavingAndContinuing(false);
      setIsSavingAndExiting(false);
      // Show validation popup with all errors
      const errorMessages: string[] = [];
      if (errors.streetNumber)
        errorMessages.push(`Street Number: ${errors.streetNumber}`);
      if (errors.streetName)
        errorMessages.push(`Street Name: ${errors.streetName}`);
      if (errors.city) errorMessages.push(`City: ${errors.city}`);
      if (errors.state) errorMessages.push(`State: ${errors.state}`);
      if (errors.zipCode) errorMessages.push(`Zip Code: ${errors.zipCode}`);
      if (errors.residenceType)
        errorMessages.push(`Residence Type: ${errors.residenceType}`);
      if (errors.landlordName)
        errorMessages.push(`Landlord Name: ${errors.landlordName}`);
      if (errors.landlordEmail)
        errorMessages.push(`Landlord Email: ${errors.landlordEmail}`);
      if (errors.landlordMobile)
        errorMessages.push(`Landlord Mobile: ${errors.landlordMobile}`);
      if (errors.moveInDateRent)
        errorMessages.push(`Move In Date: ${errors.moveInDateRent}`);
      if (errors.rentAmount)
        errorMessages.push(`Rent Amount: ${errors.rentAmount}`);
      if (errors.leaseEndDate)
        errorMessages.push(`Lease End Date: ${errors.leaseEndDate}`);
      if (errors.moveInDateOwn)
        errorMessages.push(`Move In Date: ${errors.moveInDateOwn}`);
      if (errors.monthlyMortgagePayment)
        errorMessages.push(
          `Monthly Mortgage Payment: ${errors.monthlyMortgagePayment}`,
        );
      if (errors.plannedMoveOutDate)
        errorMessages.push(
          `Planned Move Out Date: ${errors.plannedMoveOutDate}`,
        );
      if (errors.contactName)
        errorMessages.push(`Contact Name: ${errors.contactName}`);
      if (errors.contactRelationship)
        errorMessages.push(
          `Contact Relationship: ${errors.contactRelationship}`,
        );
      if (errors.contactEmail)
        errorMessages.push(`Contact Email: ${errors.contactEmail}`);
      if (errors.contactMobile)
        errorMessages.push(`Contact Mobile: ${errors.contactMobile}`);
      if (errors.moveInDateFamily)
        errorMessages.push(`Move In Date: ${errors.moveInDateFamily}`);
      if (errors.plannedMoveOutDateFamily)
        errorMessages.push(
          `Planned Move Out Date: ${errors.plannedMoveOutDateFamily}`,
        );
      // Previous address errors
      previousAddresses.forEach((_addr, index) => {
        const pfx = index === 0 ? "prev" : `prev${index}-`;
        const errorKey = (key: string) => index === 0 ? `prev${key.charAt(0).toUpperCase() + key.slice(1)}` : `${pfx}${key}`;

        if (errors[errorKey("streetNumber")])
          errorMessages.push(
            `Previous Address ${index + 1} - Street Number: ${errors[errorKey("streetNumber")]}`,
          );
        if (errors[errorKey("streetName")])
          errorMessages.push(
            `Previous Address ${index + 1} - Street Name: ${errors[errorKey("streetName")]}`,
          );
        if (errors[errorKey("city")])
          errorMessages.push(
            `Previous Address ${index + 1} - City: ${errors[errorKey("city")]}`,
          );
        if (errors[errorKey("state")])
          errorMessages.push(
            `Previous Address ${index + 1} - State: ${errors[errorKey("state")]}`,
          );
        if (errors[errorKey("zipCode")])
          errorMessages.push(`Previous Address ${index + 1} - Zip Code: ${errors[errorKey("zipCode")]}`);
        if (errors[errorKey("landlordName")])
          errorMessages.push(`Previous Address ${index + 1} - Landlord Name: ${errors[errorKey("landlordName")]}`);
        if (errors[errorKey("landlordEmail")])
          errorMessages.push(`Previous Address ${index + 1} - Landlord Email: ${errors[errorKey("landlordEmail")]}`);
        if (errors[errorKey("landlordMobile")])
          errorMessages.push(`Previous Address ${index + 1} - Landlord Mobile: ${errors[errorKey("landlordMobile")]}`);
        if (errors[errorKey("moveInDateRent")])
          errorMessages.push(`Previous Address ${index + 1} - Move In Date: ${errors[errorKey("moveInDateRent")]}`);
        if (errors[errorKey("rentAmount")])
          errorMessages.push(`Previous Address ${index + 1} - Rent Amount: ${errors[errorKey("rentAmount")]}`);
        if (errors[errorKey("moveInDateOwn")])
          errorMessages.push(`Previous Address ${index + 1} - Move In Date: ${errors[errorKey("moveInDateOwn")]}`);
        if (errors[errorKey("monthsResided")])
          errorMessages.push(
            `Previous Address ${index + 1} - Number of months you resided: ${errors[errorKey("monthsResided")]}`,
          );
        if (errors[errorKey("contactName")])
          errorMessages.push(`Previous Address ${index + 1} - Contact Name: ${errors[errorKey("contactName")]}`);
        if (errors[errorKey("contactRelationship")])
          errorMessages.push(
            `Previous Address ${index + 1} - Contact Relationship: ${errors[errorKey("contactRelationship")]}`,
          );
        if (errors[errorKey("contactEmail")])
          errorMessages.push(`Previous Address ${index + 1} - Contact Email: ${errors[errorKey("contactEmail")]}`);
        if (errors[errorKey("contactMobile")])
          errorMessages.push(`Previous Address ${index + 1} - Contact Mobile: ${errors[errorKey("contactMobile")]}`);
        if (errors[errorKey("moveInDateFamily")])
          errorMessages.push(`Previous Address ${index + 1} - Move In Date: ${errors[errorKey("moveInDateFamily")]}`);
        if (errors[errorKey("residenceType")])
          errorMessages.push(`Previous Address ${index + 1} - Residence Type: ${errors[errorKey("residenceType")]}`);
      });
      // Alternate address errors
      Object.keys(alternateAddressErrors).forEach((addrId) => {
        const addrErrors = alternateAddressErrors[addrId];
        if (addrErrors.streetNumber)
          errorMessages.push(
            `Alternate Address - Street Number: ${addrErrors.streetNumber}`,
          );
        if (addrErrors.streetName)
          errorMessages.push(
            `Alternate Address - Street Name: ${addrErrors.streetName}`,
          );
        if (addrErrors.city)
          errorMessages.push(`Alternate Address - City: ${addrErrors.city}`);
        if (addrErrors.state)
          errorMessages.push(`Alternate Address - State: ${addrErrors.state}`);
        if (addrErrors.zipCode)
          errorMessages.push(
            `Alternate Address - Zip Code: ${addrErrors.zipCode}`,
          );
      });
      if (errorMessages.length > 0) {
        setValidationModalMessage(errorMessages.join("\n"));
        setShowAddressValidationModal(true);
      }
      return; // BLOCK API CALL - Do not proceed with save when validation errors exist
    }

    // Clear errors if validation passes
    setFieldErrors({});
    setOtherAddressFieldErrors({});

    try {
      // Build current_address according to API structure
      const currentAddressData = buildCurrentAddress();
      if (!currentAddressData) {
        setSaveAddressMessage({
          type: "error",
          text: "Failed to build address data. Please check all required fields.",
        });
        setIsSavingAndContinuing(false);
        setIsSavingAndExiting(false);
        setTimeout(() => setSaveAddressMessage(null), 5000);
        return;
      }

      // Address history for the last 2 years is mandatory: when current address < 24 months, previous address is required
      if (shouldShowPreviousAddress()) {
        const allValid = previousAddresses.every(addr => addr.residenceType);
        if (!allValid) {
          setSaveAddressMessage({
            type: "error",
            text: APPLICANT_CONTENT.application.sections.previousAddress.subtitle,
          });
          setIsSavingAndContinuing(false);
          setIsSavingAndExiting(false);
          setTimeout(() => setSaveAddressMessage(null), 5000);
          return;
        }

        const apiPreviousAddresses = previousAddresses
          .map(addr => buildAddressData(addr, addr.ra_id ?? null, true))
          .filter((a): a is Address => a != null);

        if (!apiPreviousAddresses || apiPreviousAddresses.length === 0) {
          setSaveAddressMessage({
            type: "error",
            text: APPLICANT_CONTENT.application.sections.previousAddress.subtitle,
          });
          setIsSavingAndContinuing(false);
          setIsSavingAndExiting(false);
          setTimeout(() => setSaveAddressMessage(null), 5000);
          return;
        }
      }

      // Validate date before saving (UI-facing guard; builder also guards purely)
      if (dateOfBirth) {
        const validation = validateDateOfBirth(dateOfBirth);
        if (!validation.isValid) {
          setIsSavingAndContinuing(false);
          setIsSavingAndExiting(false);
          return;
        }
      }

      // Build the full-tab payload from the single source of truth shared with auto-save.
      const updateData = buildAboutUpdateData();
      if (!updateData) {
        setSaveAddressMessage({
          type: "error",
          text: "Failed to build address data. Please check all required fields.",
        });
        setIsSavingAndContinuing(false);
        setIsSavingAndExiting(false);
        setTimeout(() => setSaveAddressMessage(null), 5000);
        return;
      }

      // Save address data
      // updateApplication already fetches and returns the latest data, so no need for additional GET call
      if (!applicationData?.applicant_id) {
        setIsSavingAndContinuing(false);
        setIsSavingAndExiting(false);
        return;
      }
      const updatedData = await dispatch(
        updateApplication({
          applicantId: applicationData.applicant_id,
          updateData: updateData,
        }),
      ).unwrap();

      // Update local state with the latest alternate addresses from API response
      if (
        updatedData?.alternate_address &&
        Array.isArray(updatedData.alternate_address)
      ) {
        const updatedAddresses = updatedData.alternate_address.map(
          (addr: Address, index: number) => ({
            id: `other-addr-${addr.ra_id || index}-${Date.now()}`,
            address: addr.street_address || "",
            streetNumber: addr.house_no || "",
            streetName: addr.street || "",
            streetType: addr.street_type || "",
            aptUnit: addr.apartment || "",
            city: addr.city || "",
            state: addr.state || "",
            zipCode: addr.zip_code || "",
            country: addr.country || ADDRESS_CONSTANTS.DEFAULT_COUNTRY,
            outsideUS:
              typeof addr.outside_us === "string"
                ? addr.outside_us === "1"
                : addr.outside_us || false,
            showDetails: false,
            ra_id: addr.ra_id || null,
          }),
        );
        setOtherAddresses(updatedAddresses);

        // Update original alternate address IDs tracking after successful save
        // Ensure ra_id is converted to number (API may return as string)
        const updatedOriginalIds = new Set<number>();
        updatedData.alternate_address.forEach((addr: Address) => {
          if (addr.ra_id) {
            const raIdNumber =
              typeof addr.ra_id === "string"
                ? parseInt(addr.ra_id, 10)
                : addr.ra_id;
            if (!isNaN(raIdNumber)) {
              updatedOriginalIds.add(raIdNumber);
            }
          }
        });
        setOriginalAlternateAddressIds(updatedOriginalIds);
      } else {
        // No alternate addresses, clear the state
        setOtherAddresses([]);
        setOriginalAlternateAddressIds(new Set());
      }

      // Verify that current_address is present before navigating
      if (!updatedData?.current_address) {
        setSaveAddressMessage({
          type: "error",
          text: "Address saved but validation failed. Please refresh the page and try again.",
        });
        setIsSavingAndContinuing(false);
        setIsSavingAndExiting(false);
        return;
      }

      // Call both all_property and all_agent APIs after saving (non-blocking)
      // These calls are made in parallel and don't block navigation

      // Get property_id from multiple sources
      const propertyId =
        updatedData?.property_id ||
        updatedData?.property?.property_id ||
        applicationData?.property_id ||
        applicationData?.property?.property_id;

      // Get invite_code from applicationData
      const inviteCode = updatedData?.invite_code || applicationData?.invite_code;

      // Call all_property API
      getAllProperties(1).catch(() => {
        // Error fetching properties - non-blocking, continue navigation
      });

      // Call all_agent API if we have the required parameters
      if (propertyId && inviteCode) {
        getAgent(propertyId, inviteCode).catch(() => {
          // Error fetching agent - non-blocking, continue navigation
        });
      }

      // Show success message
      trackApplicationAutosave("about_yourself", 1, "success");
      if (target === "continue") {
        trackApplicationSectionComplete("about_yourself", 1);
      }
      setSaveAddressMessage({
        type: "success",
        text: "Address saved successfully! Navigating...",
      });

      // Handle navigation based on target
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (target === "continue") {
            const propertyPath = applicantId
              ? getTabPathWithApplicantId("property", applicantId)
              : "/applicant/application/property";
            navigate(propertyPath, {
              ...(applicantId ? getTabNavigationOptions("property") : {}),
              replace: true,
            });
          } else if (target === "exit") {
            navigate("/applicant/dashboard", { replace: true });
          }
          setIsSavingAndContinuing(false);
          setIsSavingAndExiting(false);
        }, 300);
      });
    } catch (error) {
      trackApplicationAutosave("about_yourself", 1, "error");
      // Handle error and show message
      const errorMessage =
        typeof error === "string"
          ? error
          : (error as { message?: string })?.message ||
            "Failed to save address. Please check all required fields and try again.";

      if (
        errorMessage &&
        (errorMessage.toLowerCase().includes("registered") ||
          errorMessage.toLowerCase().includes("exists") ||
          errorMessage.toLowerCase().includes("already in use") ||
          errorMessage.toLowerCase().includes("duplicate")) &&
        !isSharedPhone
      ) {
        setShowSharedPhoneErrorModal(true);
      } else {
        setValidationModalMessage(errorMessage);
        setShowAddressValidationModal(true);
      }

      setIsSavingAndContinuing(false);
      setIsSavingAndExiting(false);
    }
  };

  return (
    <div>
      {/* Header - Resident Application */}
      <ApplicationHeader
        {...getApplicationHeaderProps(
          applicationData,
          APPLICANT_CONTENT.application.status.incomplete,
        )}
        balanceDue={
          applicationData?.balance_due
            ? Number(applicationData.balance_due)
            : undefined
        }
        paymentDueDate={applicationData?.payment_due_date}
        applicantStatus={
          applicationData?.applicant_status ||
          applicationData?.application_status ||
          ""
        }
      />

      {/* Tab Navigation */}
      <ApplicantTabNavigation />

      {/* Content - form-readonly when applicant_lock so header/stepper stay clickable (per Angular) */}
      <div className={applicationData?.applicant_lock === "1" ? "form-readonly" : undefined}>
      <div data-testid="about-page" className="p-3 sm:p-6 bg-gray-50 min-h-screen">
        {/* Personal Profile Info */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">
                {APPLICANT_CONTENT.application.steps.about.title}
              </h2>
            </div>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 [&>div]:min-w-0">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {APPLICANT_CONTENT.application.sections.personalProfile.fields.firstName}*
                </label>
                <input
                  type="text"
                  data-testid="about-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={
                    isReadOnly ||
                    autoFilledFields.has("firstName") ||
                    isPersonalProfileSaved
                  }
                  placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.firstName}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-left disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {APPLICANT_CONTENT.application.sections.personalProfile.fields.middleName}
                </label>
                <input
                  type="text"
                  data-testid="about-middle-name"
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                  disabled={isReadOnly}
                  placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.middleName}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {APPLICANT_CONTENT.application.sections.personalProfile.fields.lastName}*
                </label>
                <input
                  type="text"
                  data-testid="about-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={
                    isReadOnly ||
                    autoFilledFields.has("lastName") ||
                    isPersonalProfileSaved
                  }
                  placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.lastName}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {APPLICANT_CONTENT.application.sections.personalProfile.fields.suffix}
                </label>
                <input
                  type="text"
                  data-testid="about-suffix"
                  value={suffix}
                  onChange={(e) => setSuffix(e.target.value)}
                  disabled={isReadOnly}
                  placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.suffix}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 [&>div]:min-w-0">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {APPLICANT_CONTENT.application.sections.personalProfile.fields.mobilePhone}
                  *
                </label>
                <input
                  type="text"
                  data-testid="about-phone"
                  value={mobilePhone}
                  onChange={(e) => {
                    const formatted = filterPhoneInput(e.target.value);
                    setMobilePhone(formatted);
                  }}
                  disabled={
                    isReadOnly ||
                    autoFilledFields.has("mobilePhone") ||
                    isPersonalProfileSaved
                  }
                  placeholder="(xxx) xxx-xxxx"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                />
                <div className="flex items-center mt-3">
                  <input
                    type="checkbox"
                    data-testid="about-shared-phone-checkbox"
                    checked={isSharedPhone}
                    onChange={(e) => setIsSharedPhone(e.target.checked)}
                    disabled={isReadOnly}
                    className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed"
                  />
                  <label className="text-sm text-gray-600 cursor-pointer">
                    {APPLICANT_CONTENT.application.sections.personalProfile.fields.sharedPhone}
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {APPLICANT_CONTENT.forgotPassword.emailLabel}*
                </label>
                <input
                  type="email"
                  data-testid="about-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={
                    isReadOnly ||
                    autoFilledFields.has("email") ||
                    isPersonalProfileSaved
                  }
                  placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.email}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2 relative">
                  {APPLICANT_CONTENT.application.sections.personalProfile.fields.communicationPreference}{" "}
                  *
                  <div className="group">
                    <Info className="w-4 h-4 text-green-600 cursor-help" />
                    <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-2 hidden group-hover:block w-full sm:w-96 max-w-[calc(100vw-3rem)] p-4 bg-green-50 border-2 border-green-200 rounded-lg shadow-xl z-10">
                      <p className="text-sm text-green-600">
                        Note: Email is our primary method currently. We are
                        shifting our messages from emails to text messages for
                        your convenience.
                      </p>
                    </div>
                  </div>
                </label>
                <div className="flex flex-wrap gap-3 mt-2">
                  <button
                    type="button"
                    data-testid="about-comm-preference-text"
                    onClick={() => !isReadOnly && setCommPreference("text")}
                    disabled={isReadOnly}
                    className={`px-5 py-2.5 border rounded-lg transition-all font-medium flex items-center gap-2 ${commPreference === "text"
                      ? "bg-[#1597ff] text-white border-[#1597ff] shadow-md"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    {APPLICANT_CONTENT.application.sections.personalProfile.fields.textMsg}
                  </button>
                  <button
                    type="button"
                    data-testid="about-comm-preference-email"
                    onClick={() => !isReadOnly && setCommPreference("email")}
                    disabled={isReadOnly}
                    className={`px-5 py-2.5 border rounded-lg transition-all font-medium flex items-center gap-2 ${commPreference === "email"
                      ? "bg-[#1597ff] text-white border-[#1597ff] shadow-md"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <Mail className="w-4 h-4" />
                    {APPLICANT_CONTENT.forgotPassword.emailLabel}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 [&>div]:min-w-0">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {APPLICANT_CONTENT.application.sections.personalProfile.fields.dateOfBirth}
                  *
                </label>
                <DatePicker
                  data-testid="about-dob"
                  value={dateOfBirth}
                  onChange={(value) => {
                    setDateOfBirth(value);
                    validateField("dateOfBirth", value);
                  }}
                  disabled={isReadOnly}
                  placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
                  showHelperText={false}
                  error={personalProfileErrors.dateOfBirth}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {APPLICANT_CONTENT.application.sections.personalProfile.fields.ssnOrItin}*
                </label>
                <SSNInput
                  data-testid="about-ssn"
                  value={ssn}
                  onChange={(value) => {
                    setSsn(value);
                    validateField("ssn", value);
                  }}
                  disabled={isReadOnly}
                  placeholder="xxx-xx-xxxx "
                  className={`w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${personalProfileErrors.ssn
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                />
                {personalProfileErrors.ssn && (
                  <p className="mt-1 text-sm text-red-600">
                    {personalProfileErrors.ssn}
                  </p>
                )}
              </div>
            </div>

            {/* Save Button */}
            {!isReadOnly && (
              <div className="pt-4 pb5 border-t border-gray-200">
                {saveMessage && saveMessage.type === "success" && (
                  <div className="mb-3 p-3 rounded-lg bg-green-50 text-green-800 border border-green-200">
                    <p className="text-sm font-medium">{saveMessage.text}</p>
                  </div>
                )}
                <button
                  onClick={async () => {
                    // Prevent API calls when application is read-only
                    if (isReadOnly) {
                      return;
                    }

                    // Validate DOB and SSN before saving
                    const validationResult = validatePersonalProfile();
                    if (!validationResult.isValid) {
                      // Show validation popup with all errors - use errors from validation result
                      const errorMessages: string[] = [];
                      if (validationResult.errors.dateOfBirth) {
                        errorMessages.push(
                          `Date of Birth: ${validationResult.errors.dateOfBirth}`,
                        );
                      }
                      if (validationResult.errors.ssn) {
                        errorMessages.push(
                          `SSN/ITIN: ${validationResult.errors.ssn}`,
                        );
                      }
                      if (errorMessages.length > 0) {
                        setValidationModalMessage(errorMessages.join("\n"));
                        setShowPersonalProfileValidationModal(true);
                      }
                      return;
                    }

                    if (!applicationData?.applicant_id) {
                      return;
                    }

                    setIsSaving(true);
                    setSaveMessage(null);

                    try {
                      // Format date for API - send YYYY-MM-DD to match Angular (validateData → formatDate → YYYY-MM-DD)
                      let dobForApi = "";
                      if (dateOfBirth) {
                        // Validate date before saving
                        const validation = validateDateOfBirth(dateOfBirth);
                        if (!validation.isValid) {
                          setIsSaving(false);
                          return;
                        }
                        // dateOfBirth is already YYYY-MM-DD; send as-is to match Angular
                        dobForApi = dateOfBirth;
                      }

                      // Prepare update data
                      const updateData = {
                        ...residentSectionValidation("about"),
                        first_name: firstName || undefined,
                        middle_name: middleName || "",
                        last_name: lastName || undefined,
                        suffix: suffix || undefined,
                        mobile_no:
                          unformatPhoneNumber(mobilePhone) || undefined,
                        shared_phone_no: isSharedPhone ? "1" : "0",
                        email: email || undefined,
                        communication_preference:
                          commPreference === "text" ? APPLICANT_CONTENT.application.sections.personalProfile.fields.textMsg : APPLICANT_CONTENT.application.sections.personalProfile.fields.email,
                        birthdate: dobForApi || undefined,
                        security_number: ssn || undefined,
                        validation: false, // Angular parity: partial save, skip server validation
                      };

                      // Remove undefined fields
                      Object.keys(updateData).forEach((key) => {
                        if (
                          updateData[key as keyof typeof updateData] ===
                          undefined
                        ) {
                          delete updateData[key as keyof typeof updateData];
                        }
                      });

                      // updateApplication already fetches and returns the latest data internally, so no need for additional GET call
                      await dispatch(
                        updateApplication({
                          applicantId: applicationData.applicant_id,
                          updateData: updateData,
                        }),
                      ).unwrap();

                      // Mark personal profile as saved to disable email, firstName, lastName, mobilePhone
                      setIsPersonalProfileSaved(true);

                      trackApplicationAutosave("about_yourself", 1, "success");
                      setSaveMessage({
                        type: "success",
                        text: APPLICANT_CONTENT.application.messages.saveProgressSuccess,
                      });

                      // Clear success message after 3 seconds
                      setTimeout(() => {
                        setSaveMessage(null);
                      }, 3000);
                    } catch (error: unknown) {
                      trackApplicationAutosave("about_yourself", 1, "error");
                      const errorMessage = extractErrorMessage(error, "");

                      if (
                        errorMessage &&
                        (errorMessage.toLowerCase().includes("registered") ||
                          errorMessage.toLowerCase().includes("exists") ||
                          errorMessage
                            .toLowerCase()
                            .includes("already in use") ||
                          errorMessage.toLowerCase().includes("duplicate")) &&
                        !isSharedPhone
                      ) {
                        setShowSharedPhoneErrorModal(true);
                      } else if (errorMessage) {
                        setValidationModalMessage(errorMessage);
                        setShowPersonalProfileValidationModal(true);
                      }
                    } finally {
                      setIsSaving(false);
                    }
                  }}
                  disabled={isSaving || !applicationData?.applicant_id}
                  data-testid="about-save-personal-profile-button"
                  className="inline-flex items-center gap-2 border-2 border-[#1597ff] text-[#1597ff] hover:bg-blue-50 font-medium px-8 py-2.5 rounded-lg transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {APPLICANT_CONTENT.application.messages.saving}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {APPLICANT_CONTENT.application.sections.personalProfile.save}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Current Living Address */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">
                {APPLICANT_CONTENT.application.sections.currentAddress.title}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {APPLICANT_CONTENT.application.sections.currentAddress.subtitle}
              </p>
            </div>
          </div>

          {/* Geocoding verification warning (Issue 13) */}
          {geocodeWarning && (
            <div className="mb-4 p-3 rounded-lg bg-yellow-50 border border-yellow-300 flex items-start gap-2" role="alert">
              <Info className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-yellow-800">{geocodeWarning}</p>
            </div>
          )}

          <div className="space-y-6">
            <AddressBasicFields
              state={currentAddressState}
              actions={currentAddressActions}
              states={states}
              isLoadingStates={isLoadingStates}
              fieldErrors={fieldErrors}
              onFieldErrorChange={(field, error) => {
                setFieldErrors((prev) => ({ ...prev, [field]: error }));
              }}
              lockToUS
              isReadOnly={isReadOnly}
              onCityStateZipBlur={handleGeocodeCheck}
              onAddressSelect={() => setGeocodeWarning(null)}
            />

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <ResidenceTypeSelector
                    value={currentAddressState.residenceType}
                    onChange={(value) => {
                      const synced = syncSharedResidenceDateFields({
                        moveInDateRent,
                        moveInDateOwn,
                        moveInDateFamily,
                        leaseEndDate,
                        plannedMoveOutDate,
                        plannedMoveOutDateFamily,
                      });
                      setMoveInDateRent(synced.moveInDateRent);
                      setMoveInDateOwn(synced.moveInDateOwn);
                      setMoveInDateFamily(synced.moveInDateFamily);
                      setLeaseEndDate(synced.leaseEndDate);
                      setPlannedMoveOutDate(synced.plannedMoveOutDate);
                      setPlannedMoveOutDateFamily(
                        synced.plannedMoveOutDateFamily,
                      );
                      currentAddressActions.setResidenceType(value);
                      setFieldErrors((prev) => {
                        const next: typeof prev = {
                          ...prev,
                          residenceType: undefined,
                        };
                        const mi = synced.moveInDateRent.trim();
                        if (mi) {
                          next.moveInDateRent = undefined;
                          next.moveInDateOwn = undefined;
                          next.moveInDateFamily = undefined;
                        }
                        const end = synced.leaseEndDate.trim();
                        if (end) {
                          next.leaseEndDate = undefined;
                          next.plannedMoveOutDate = undefined;
                          next.plannedMoveOutDateFamily = undefined;
                        }
                        return next;
                      });
                    }}
                    isReadOnly={isReadOnly}
                  />
                  {fieldErrors.residenceType && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      {fieldErrors.residenceType}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    {APPLICANT_CONTENT.application.sections.currentAddress.duration} :{" "}
                    <span className="font-medium">
                      {calculateCurrentAddressDuration()}{" "}
                      {APPLICANT_CONTENT.application.sections.currentAddress.months}
                    </span>
                  </p>
                  {/* 2 year address history message removed per user request */}
                </div>
              </div>
            </div>
          </div>

          <ResidenceForm
            residenceType={currentAddressState.residenceType}
            isReadOnly={isReadOnly}
            rentProps={
              currentAddressState.residenceType === "rent"
                ? {
                  landlordName: currentAddressState.landlordName,
                  landlordEmail: currentAddressState.landlordEmail,
                  landlordMobile: currentAddressState.landlordMobile,
                  moveInDate: currentAddressState.moveInDateRent,
                  leaseEndDate: currentAddressState.leaseEndDate,
                  rentAmount: currentAddressState.rentAmount,
                  stillHere: currentAddressState.stillHereRent,
                  onLandlordNameChange: (value) => {
                    currentAddressActions.setLandlordName(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      landlordName: undefined,
                    }));
                  },
                  onLandlordEmailChange: (value) => {
                    currentAddressActions.setLandlordEmail(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      landlordEmail: undefined,
                    }));
                  },
                  onLandlordMobileChange: (value) => {
                    currentAddressActions.setLandlordMobile(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      landlordMobile: undefined,
                    }));
                  },
                  onMoveInDateChange: (value) => {
                    currentAddressActions.setMoveInDateRent(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      moveInDateRent: undefined,
                    }));
                  },
                  onLeaseEndDateChange: (value) => {
                    currentAddressActions.setLeaseEndDate(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      leaseEndDate: undefined,
                    }));
                  },
                  onRentAmountChange: (value) => {
                    currentAddressActions.setRentAmount(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      rentAmount: undefined,
                    }));
                  },
                  onStillHereChange: (checked) => {
                    currentAddressActions.setStillHereRent(checked);
                    setFieldErrors((prev) => ({
                      ...prev,
                      ...(checked
                        ? {
                            leaseEndDate: undefined,
                            plannedMoveOutDate: undefined,
                            plannedMoveOutDateFamily: undefined,
                          }
                        : {}),
                    }));
                  },
                  errors: {
                    landlordName: fieldErrors.landlordName,
                    landlordEmail: fieldErrors.landlordEmail,
                    landlordMobile: fieldErrors.landlordMobile,
                    moveInDate: fieldErrors.moveInDateRent,
                    leaseEndDate: fieldErrors.leaseEndDate,
                    rentAmount: fieldErrors.rentAmount,
                  },
                }
                : undefined
            }
            ownProps={
              currentAddressState.residenceType === "own"
                ? {
                  moveInDate: currentAddressState.moveInDateOwn,
                  plannedMoveOutDate: currentAddressState.plannedMoveOutDate,
                  monthlyMortgagePayment:
                    currentAddressState.monthlyMortgagePayment,
                  monthsResided: currentAddressState.monthsResided,
                  stillHere: currentAddressState.stillHereOwn,
                  onMoveInDateChange: (value) => {
                    currentAddressActions.setMoveInDateOwn(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      moveInDateOwn: undefined,
                    }));
                  },
                  onPlannedMoveOutDateChange: (value) => {
                    currentAddressActions.setPlannedMoveOutDate(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      plannedMoveOutDate: undefined,
                    }));
                  },
                  onMonthlyMortgagePaymentChange: (value) => {
                    currentAddressActions.setMonthlyMortgagePayment(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      monthlyMortgagePayment: undefined,
                    }));
                  },
                  onMonthsResidedChange: (value) => {
                    currentAddressActions.setMonthsResided(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      monthsResided: undefined,
                    }));
                  },
                  onStillHereChange: (checked) => {
                    currentAddressActions.setStillHereOwn(checked);
                    setFieldErrors((prev) => ({
                      ...prev,
                      ...(checked
                        ? {
                            leaseEndDate: undefined,
                            plannedMoveOutDate: undefined,
                            plannedMoveOutDateFamily: undefined,
                          }
                        : {}),
                    }));
                  },
                  errors: {
                    moveInDate: fieldErrors.moveInDateOwn,
                    plannedMoveOutDate: fieldErrors.plannedMoveOutDate,
                    monthlyMortgagePayment:
                      fieldErrors.monthlyMortgagePayment,
                    monthsResided: fieldErrors.monthsResided,
                  },
                }
                : undefined
            }
            familyProps={
              currentAddressState.residenceType === "family"
                ? {
                  contactName: currentAddressState.contactName,
                  contactRelationship:
                    currentAddressState.contactRelationship,
                  contactEmail: currentAddressState.contactEmail,
                  contactMobile: currentAddressState.contactMobile,
                  contactWebsite: currentAddressState.contactWebsite,
                  moveInDate: currentAddressState.moveInDateFamily,
                  plannedMoveOutDate:
                    currentAddressState.plannedMoveOutDateFamily,
                  rentAmount: currentAddressState.rentAmountFamily,
                  stillHere: currentAddressState.stillHereFamily,
                  onContactNameChange: (value) => {
                    currentAddressActions.setContactName(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      contactName: undefined,
                    }));
                  },
                  onContactRelationshipChange: (value) => {
                    currentAddressActions.setContactRelationship(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      contactRelationship: undefined,
                    }));
                  },
                  onContactEmailChange: (value) => {
                    currentAddressActions.setContactEmail(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      contactEmail: undefined,
                    }));
                  },
                  onContactMobileChange: (value) => {
                    currentAddressActions.setContactMobile(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      contactMobile: undefined,
                    }));
                  },
                  onContactWebsiteChange:
                    currentAddressActions.setContactWebsite,
                  onMoveInDateChange: (value) => {
                    currentAddressActions.setMoveInDateFamily(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      moveInDateFamily: undefined,
                    }));
                  },
                  onPlannedMoveOutDateChange: (value) => {
                    currentAddressActions.setPlannedMoveOutDateFamily(value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      plannedMoveOutDateFamily: undefined,
                    }));
                  },
                  onRentAmountChange:
                    currentAddressActions.setRentAmountFamily,
                  onStillHereChange: (checked) => {
                    currentAddressActions.setStillHereFamily(checked);
                    setFieldErrors((prev) => ({
                      ...prev,
                      ...(checked
                        ? {
                            leaseEndDate: undefined,
                            plannedMoveOutDate: undefined,
                            plannedMoveOutDateFamily: undefined,
                          }
                        : {}),
                    }));
                  },
                  errors: {
                    contactName: fieldErrors.contactName,
                    contactRelationship: fieldErrors.contactRelationship,
                    contactEmail: fieldErrors.contactEmail,
                    contactMobile: fieldErrors.contactMobile,
                    moveInDate: fieldErrors.moveInDateFamily,
                    plannedMoveOutDate: fieldErrors.plannedMoveOutDateFamily,
                  },
                }
                : undefined
            }
          />

          {/* Save Button */}
          {!isReadOnly && (
            <div className="pt-4 pb-5 border-t border-gray-200">
              {saveAddressMessage && saveAddressMessage.type === "success" && (
                <div className="mb-3 p-3 rounded-lg bg-green-50 text-green-800 border border-green-200">
                  <p className="text-sm font-medium">
                    {saveAddressMessage.text}
                  </p>
                </div>
              )}
              <button
                onClick={async () => {
                  // Prevent API calls when application is read-only
                  if (isReadOnly) {
                    return;
                  }

                  if (!applicationData?.applicant_id) {
                    return;
                  }

                  // Check if all addresses are outside US and checkbox is not checked
                  if (validateAllUSAddress() && !noAlternateAddress) {
                    setValidationModalMessage(ADDRESS_OUTSIDE_USA_MESSAGE);
                    setShowAddressValidationModal(true);
                    return;
                  }

                  setIsSavingAddress(true);
                  setSaveAddressMessage(null);

                  // Validate required fields (Address Validation)
                  const errors: typeof fieldErrors = {};

                  // Basic address fields
                  if (!currentAddressState.streetNumber.trim()) {
                    errors.streetNumber = APPLICANT_CONTENT.errors.fieldRequired.streetNumber;
                  }

                  if (!currentAddressState.streetName.trim()) {
                    errors.streetName = APPLICANT_CONTENT.errors.fieldRequired.streetName;
                  }

                  if (!currentAddressState.city.trim()) {
                    errors.city = APPLICANT_CONTENT.errors.fieldRequired.city;
                  }

                  if (!currentAddressState.state.trim()) {
                    errors.state = APPLICANT_CONTENT.errors.fieldRequired.state;
                  }

                  const digitsOnly = currentAddressState.zipCode.replace(
                    /\D/g,
                    "",
                  );
                  if (!digitsOnly) {
                    errors.zipCode = APPLICANT_CONTENT.errors.fieldRequired.zipCode;
                  } else if (
                    digitsOnly.length !== 5 &&
                    digitsOnly.length !== 6
                  ) {
                    errors.zipCode = APPLICANT_CONTENT.errors.fieldRequired.zipCodeLength;
                  }

                  // Residence type validation - required
                  if (!currentAddressState.residenceType) {
                    errors.residenceType = APPLICANT_CONTENT.errors.fieldRequired.residenceType;
                  }

                  // Residence type specific validation
                  if (currentAddressState.residenceType === "rent") {
                    if (!currentAddressState.landlordName.trim()) {
                      errors.landlordName = APPLICANT_CONTENT.errors.fieldRequired.landlordName;
                    }
                    if (!currentAddressState.landlordEmail.trim()) {
                      errors.landlordEmail = APPLICANT_CONTENT.errors.fieldRequired.landlordEmail;
                    } else if (
                      !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(
                        currentAddressState.landlordEmail.trim(),
                      )
                    ) {
                      errors.landlordEmail = "Enter Valid Email";
                    }
                    if (!currentAddressState.landlordMobile.trim()) {
                      errors.landlordMobile = APPLICANT_CONTENT.errors.fieldRequired.landlordMobile;
                    } else if (
                      !/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(
                        currentAddressState.landlordMobile
                          .trim()
                          .replace(/\D/g, ""),
                      )
                    ) {
                      errors.landlordMobile = "Enter Valid Phone Number";
                    }
                    if (!currentAddressState.moveInDateRent) {
                      errors.moveInDateRent = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
                    }
                    if (!currentAddressState.rentAmount.trim()) {
                      errors.rentAmount = APPLICANT_CONTENT.errors.fieldRequired.rentAmount;
                    }
                    if (
                      !currentAddressState.stillHereRent &&
                      !(
                        currentAddressState.leaseEndDate &&
                        currentAddressState.leaseEndDate.trim()
                      )
                    ) {
                      errors.leaseEndDate = APPLICANT_CONTENT.errors.fieldRequired.leaseEndDate;
                    }
                  } else if (currentAddressState.residenceType === "own") {
                    if (!currentAddressState.moveInDateOwn) {
                      errors.moveInDateOwn = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
                    }
                    if (!currentAddressState.monthlyMortgagePayment.trim()) {
                      errors.monthlyMortgagePayment = APPLICANT_CONTENT.errors.fieldRequired.monthlyMortgagePayment;
                    }
                    if (
                      !currentAddressState.stillHereOwn &&
                      !(
                        currentAddressState.plannedMoveOutDate &&
                        currentAddressState.plannedMoveOutDate.trim()
                      )
                    ) {
                      errors.plannedMoveOutDate = APPLICANT_CONTENT.errors.fieldRequired.plannedMoveOutDate;
                    }
                  } else if (currentAddressState.residenceType === "family") {
                    if (!currentAddressState.contactName.trim()) {
                      errors.contactName = APPLICANT_CONTENT.errors.fieldRequired.contactName;
                    }
                    if (!currentAddressState.contactRelationship.trim()) {
                      errors.contactRelationship = APPLICANT_CONTENT.errors.fieldRequired.contactRelationship;
                    }
                    if (!currentAddressState.contactEmail.trim()) {
                      errors.contactEmail = APPLICANT_CONTENT.errors.fieldRequired.contactEmail;
                    } else if (
                      !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(
                        currentAddressState.contactEmail.trim(),
                      )
                    ) {
                      errors.contactEmail = "Enter Valid Email";
                    }
                    if (!currentAddressState.contactMobile.trim()) {
                      errors.contactMobile = APPLICANT_CONTENT.errors.fieldRequired.contactMobile;
                    } else if (
                      !/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(
                        currentAddressState.contactMobile
                          .trim()
                          .replace(/\D/g, ""),
                      )
                    ) {
                      errors.contactMobile = "Enter Valid Phone Number";
                    }
                    if (!currentAddressState.moveInDateFamily) {
                      errors.moveInDateFamily = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
                    }
                    if (
                      !currentAddressState.stillHereFamily &&
                      !(
                        currentAddressState.plannedMoveOutDateFamily &&
                        currentAddressState.plannedMoveOutDateFamily.trim()
                      )
                    ) {
                      errors.plannedMoveOutDateFamily = APPLICANT_CONTENT.errors.fieldRequired.plannedMoveOutDate;
                    }
                  }

                  // Previous Address Validation
                  if (
                    shouldShowPreviousAddress() &&
                    prevAddressState.residenceType
                  ) {
                    if (prevAddressState.residenceType === "rent") {
                      if (!prevAddressState.landlordName.trim()) {
                        errors.prevLandlordName = APPLICANT_CONTENT.errors.fieldRequired.landlordName;
                      }

                      if (!prevAddressState.landlordEmail.trim()) {
                        errors.prevLandlordEmail = APPLICANT_CONTENT.errors.fieldRequired.landlordEmail;
                      } else if (
                        !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(
                          prevAddressState.landlordEmail.trim(),
                        )
                      ) {
                        errors.prevLandlordEmail = "Enter Valid Email";
                      }

                      if (!prevAddressState.landlordMobile.trim()) {
                        errors.prevLandlordMobile = APPLICANT_CONTENT.errors.fieldRequired.landlordMobile;
                      } else if (
                        !/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(
                          prevAddressState.landlordMobile
                            .trim()
                            .replace(/\D/g, ""),
                        )
                      ) {
                        errors.prevLandlordMobile = "Enter Valid Phone Number";
                      }

                      if (!prevAddressState.moveInDateRent) {
                        errors.prevMoveInDateRent = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
                      }
                      if (!prevAddressState.rentAmount.trim()) {
                        errors.prevRentAmount = APPLICANT_CONTENT.errors.fieldRequired.rentAmount;
                      }
                    }
                  }

                  if (Object.keys(errors).length > 0) {
                    setFieldErrors(errors);
                    setIsSavingAddress(false);
                    // Show validation popup with all errors
                    const errorMessages: string[] = [];
                    if (errors.streetNumber)
                      errorMessages.push(
                        `Street Number: ${errors.streetNumber}`,
                      );
                    if (errors.streetName)
                      errorMessages.push(`Street Name: ${errors.streetName}`);
                    if (errors.city) errorMessages.push(`City: ${errors.city}`);
                    if (errors.state)
                      errorMessages.push(`State: ${errors.state}`);
                    if (errors.zipCode)
                      errorMessages.push(`Zip Code: ${errors.zipCode}`);
                    if (errors.residenceType)
                      errorMessages.push(
                        `Residence Type: ${errors.residenceType}`,
                      );
                    if (errors.landlordName)
                      errorMessages.push(
                        `Landlord Name: ${errors.landlordName}`,
                      );
                    if (errors.landlordEmail)
                      errorMessages.push(
                        `Landlord Email: ${errors.landlordEmail}`,
                      );
                    if (errors.landlordMobile)
                      errorMessages.push(
                        `Landlord Mobile: ${errors.landlordMobile}`,
                      );
                    if (errors.moveInDateRent)
                      errorMessages.push(
                        `Move In Date: ${errors.moveInDateRent}`,
                      );
                    if (errors.rentAmount)
                      errorMessages.push(`Rent Amount: ${errors.rentAmount}`);
                    if (errors.leaseEndDate)
                      errorMessages.push(
                        `Lease End Date: ${errors.leaseEndDate}`,
                      );
                    if (errors.moveInDateOwn)
                      errorMessages.push(
                        `Move In Date: ${errors.moveInDateOwn}`,
                      );
                    if (errors.monthlyMortgagePayment)
                      errorMessages.push(
                        `Monthly Mortgage Payment: ${errors.monthlyMortgagePayment}`,
                      );
                    if (errors.plannedMoveOutDate)
                      errorMessages.push(
                        `Planned Move Out Date: ${errors.plannedMoveOutDate}`,
                      );
                    if (errors.contactName)
                      errorMessages.push(`Contact Name: ${errors.contactName}`);
                    if (errors.contactRelationship)
                      errorMessages.push(
                        `Contact Relationship: ${errors.contactRelationship}`,
                      );
                    if (errors.contactEmail)
                      errorMessages.push(
                        `Contact Email: ${errors.contactEmail}`,
                      );
                    if (errors.contactMobile)
                      errorMessages.push(
                        `Contact Mobile: ${errors.contactMobile}`,
                      );
                    if (errors.moveInDateFamily)
                      errorMessages.push(
                        `Move In Date: ${errors.moveInDateFamily}`,
                      );
                    if (errors.plannedMoveOutDateFamily)
                      errorMessages.push(
                        `Planned Move Out Date: ${errors.plannedMoveOutDateFamily}`,
                      );

                    // Previous Address Errors
                    if (errors.prevLandlordName)
                      errorMessages.push(
                        `Previous Landlord Name: ${errors.prevLandlordName}`,
                      );
                    if (errors.prevLandlordEmail)
                      errorMessages.push(
                        `Previous Landlord Email: ${errors.prevLandlordEmail}`,
                      );
                    if (errors.prevLandlordMobile)
                      errorMessages.push(
                        `Previous Landlord Mobile: ${errors.prevLandlordMobile}`,
                      );
                    if (errors.prevMoveInDateRent)
                      errorMessages.push(
                        `Previous Move In Date: ${errors.prevMoveInDateRent}`,
                      );
                    if (errors.prevRentAmount)
                      errorMessages.push(
                        `Previous Rent Amount: ${errors.prevRentAmount}`,
                      );

                    if (errorMessages.length > 0) {
                      setValidationModalMessage(errorMessages.join("\n"));
                      setShowAddressValidationModal(true);
                    }
                    return; // BLOCK API CALL - Do not proceed with save when validation errors exist
                  }

                  // Clear errors if validation passes
                  setFieldErrors({});

                  try {
                    if (!residenceType) {
                      setIsSavingAddress(false);
                      return;
                    }

                    // Build current_address according to API structure
                    const currentAddressData = buildCurrentAddress();
                    if (!currentAddressData) {
                      setIsSavingAddress(false);
                      return;
                    }

                    // Address history for the last 2 years is mandatory: when current address < 24 months, previous address is required
                    // Use full previousAddresses array so all addresses are in payload (same as Save and Continue)
                    let apiPreviousAddressesSection: Address[] | null = null;
                    if (shouldShowPreviousAddress()) {
                      const allValid = previousAddresses.every(
                        (addr) => addr.residenceType,
                      );
                      if (!allValid) {
                        setSaveAddressMessage({
                          type: "error",
                          text: APPLICANT_CONTENT.application.sections.previousAddress.subtitle,
                        });
                        setTimeout(() => setSaveAddressMessage(null), 5000);
                        setIsSavingAddress(false);
                        return;
                      }
                      apiPreviousAddressesSection = previousAddresses
                        .map((addr) => buildAddressData(addr, addr.ra_id ?? null, true))
                        .filter((a): a is Address => a != null);
                      if (
                        !apiPreviousAddressesSection ||
                        apiPreviousAddressesSection.length === 0
                      ) {
                        setSaveAddressMessage({
                          type: "error",
                          text: APPLICANT_CONTENT.application.sections.previousAddress.subtitle,
                        });
                        setTimeout(() => setSaveAddressMessage(null), 5000);
                        setIsSavingAddress(false);
                        return;
                      }
                    }

                    // Determine if address is outside US
                    const countryCode = (
                      currentAddressState.country || "US"
                    ).toUpperCase();
                    const isOutsideUS = countryCode !== "US" || outsideUS;

                    const updateData: UpdateApplicationRequest = {
                      ...residentSectionValidation("about"),
                      current_address: currentAddressData,
                      // Only include enter_date if it has actual date entries (remove empty object)
                      // enter_date: {}, // Removed - only include if there are actual date entries
                      // non_us_address logic:
                      // - If user has alternate US address: false/"0" (or omit)
                      // - If user does NOT have alternate US address: true/"1"
                      // Only include if address is outside US
                      // Convert boolean to string "1" or "0" to match API format
                      ...(isOutsideUS
                        ? { non_us_address: noAlternateAddress ? "1" : "0" }
                        : { non_us_address: "0" }),
                      validation: false, // Angular parity: partial save, skip server validation
                    };

                    // Add previous address(es) - include all when present
                    if (
                      shouldShowPreviousAddress() &&
                      apiPreviousAddressesSection
                    ) {
                      updateData.previous_address =
                        apiPreviousAddressesSection;
                    } else {
                      updateData.previous_address = [];
                    }

                    // Add alternate addresses (other addresses for credit search)
                    if (otherAddresses.length > 0) {
                      updateData.alternate_address = otherAddresses.map(
                        (addr) => ({
                          ra_id: addr.ra_id || null,
                          street_address: buildAlternateAddressStreetAddress(
                            addr,
                          ),
                          outside_us: addr.outsideUS,
                          house_no: addr.streetNumber,
                          street: addr.streetName,
                          street_type: addr.streetType || "",
                          apartment: addr.aptUnit || null,
                          city: addr.city,
                          state: addr.state,
                          zip_code: addr.zipCode,
                          country: addr.country,
                          address_type: undefined,
                          address_data: undefined,
                        }),
                      );
                    } else {
                      // If no addresses, send empty array
                      updateData.alternate_address = [];
                    }

                    // updateApplication already fetches and returns the latest data internally, so no need for additional GET call
                    await dispatch(
                      updateApplication({
                        applicantId: applicationData.applicant_id,
                        updateData: updateData,
                      }),
                    ).unwrap();

                    const successMessage =
                      apiPreviousAddressesSection?.length
                        ? APPLICANT_CONTENT.application.messages.saveAddressesSuccess
                        : APPLICANT_CONTENT.application.messages.saveAddressSuccess;
                    trackApplicationAutosave("about_yourself", 1, "success");
                    setSaveAddressMessage({
                      type: "success",
                      text: successMessage,
                    });

                    // Clear message after 3 seconds
                    setTimeout(() => {
                      setSaveAddressMessage(null);
                    }, 3000);
                  } catch (error) {
                    trackApplicationAutosave("about_yourself", 1, "error");
                    // Error handled silently - validation shown on fields
                    setValidationModalMessage(
                      typeof error === "string"
                        ? error
                        : (error as { message?: string })?.message ||
                          APPLICANT_CONTENT.errors.saveAddressFailed,
                    );
                    setShowAddressValidationModal(true);
                  } finally {
                    setIsSavingAddress(false);
                  }
                }}
                disabled={
                  isSavingAddress ||
                  isSavingAndContinuing ||
                  !applicationData?.applicant_id
                }
                data-testid="about-save-address-button"
                className="inline-flex items-center gap-2 border-2 border-[#1597ff] text-[#1597ff] hover:bg-blue-50 font-medium px-8 py-2.5 rounded-lg transition-all hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingAddress ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          )}

          {/* Previous Address Section - shown only when current address is valid AND duration < 24 months (matches Angular: current_address.valid && checkPreviousAddress()) */}
          {isCurrentAddressValid() && shouldShowPreviousAddress() && (
            <div className="space-y-6 mb-6">
              {previousAddresses.map((addr, index) => {
                const isPrimary = index === 0;
                const pfx = isPrimary ? "prev" : `prev${index}-`;
                const errorKey = (key: string) => isPrimary ? `prev${key.charAt(0).toUpperCase() + key.slice(1)}` : `${pfx}${key}`;

                const createAction = <K extends keyof AddressFormState>(setterKey: K) => (value: AddressFormState[K]) => {
                  setPreviousAddresses(prev => prev.map((a, i) => i === index ? { ...a, [setterKey]: value } : a));
                  setFieldErrors((prev) => ({
                    ...prev,
                    [errorKey(setterKey)]: undefined,
                  }));
                };

                // Helper to cleanly apply to address fields inside AddressBasicFields
                const onBasicFieldAction = (field: string, error: string | undefined) => {
                  setFieldErrors(prev => ({ ...prev, [errorKey(field)]: error }));
                };

                return (
                  <div key={`prev-addr-${index}`} className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-xl font-bold text-gray-900">
                          {APPLICANT_CONTENT.application.sections.previousAddress.title} {index > 0 ? `${index + 1}` : ""}*
                        </h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {APPLICANT_CONTENT.application.sections.previousAddress.subtitle}
                        </p>
                      </div>
                      {!isPrimary && !isReadOnly && (
                        <button
                          data-testid={`about-remove-prev-address-${index}-button`}
                          onClick={() => {
                            setPreviousAddresses(prev => prev.filter((_, i) => i !== index));
                            setFieldErrors(prev => {
                              const newErrs = { ...prev };
                              // Optional: Clean up errors for this index
                              Object.keys(newErrs).forEach(k => {
                                if (k.startsWith(pfx)) {
                                  delete newErrs[k as keyof typeof fieldErrors];
                                }
                              });
                              return newErrs;
                            });
                          }}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="space-y-6">
                      <AddressBasicFields
                        state={addr}
                        actions={{
                          setAddress: createAction("address"),
                          setStreetNumber: createAction("streetNumber"),
                          setStreetName: createAction("streetName"),
                          setStreetType: createAction("streetType"),
                          setAptUnit: createAction("aptUnit"),
                          setCity: createAction("city"),
                          setState: createAction("state"),
                          setZipCode: createAction("zipCode"),
                          setOutsideUS: createAction("outsideUS"),
                          setCountry: createAction("country"),
                        } as AddressFormActions}
                        states={states}
                        isLoadingStates={isLoadingStates}
                        fieldErrors={{
                          streetNumber: fieldErrors[errorKey("streetNumber")],
                          streetName: fieldErrors[errorKey("streetName")],
                          city: fieldErrors[errorKey("city")],
                          state: fieldErrors[errorKey("state")],
                          zipCode: fieldErrors[errorKey("zipCode")],
                        }}
                        isReadOnly={isReadOnly}
                        onFieldErrorChange={(field, error) => {
                          const mapping: Record<string, string> = {
                            streetNumber: "streetNumber",
                            streetName: "streetName",
                            city: "city",
                            state: "state",
                            zipCode: "zipCode"
                          };
                          if (mapping[field]) {
                            onBasicFieldAction(mapping[field], error);
                          } else {
                            onBasicFieldAction(field, error);
                          }
                        }}
                        prefix={errorKey("")}
                      />

                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <ResidenceTypeSelector
                              value={addr.residenceType}
                              onChange={(value) => {
                                const synced = syncSharedResidenceDateFields(addr);
                                setPreviousAddresses((prev) =>
                                  prev.map((a, i) => {
                                    if (i !== index) return a;
                                    return {
                                      ...a,
                                      ...synced,
                                      residenceType: value,
                                    };
                                  }),
                                );
                                setFieldErrors((prev) => {
                                  const next = { ...prev };
                                  next[errorKey("residenceType")] = undefined;
                                  const mi = synced.moveInDateRent.trim();
                                  if (mi) {
                                    next[errorKey("moveInDateRent")] = undefined;
                                    next[errorKey("moveInDateOwn")] = undefined;
                                    next[errorKey("moveInDateFamily")] = undefined;
                                  }
                                  const end = synced.leaseEndDate.trim();
                                  if (end) {
                                    next[errorKey("leaseEndDate")] = undefined;
                                    next[errorKey("plannedMoveOutDate")] =
                                      undefined;
                                    next[errorKey("plannedMoveOutDateFamily")] =
                                      undefined;
                                  }
                                  return next;
                                });
                              }}
                              name={`prev-residence-${index}`}
                              isReadOnly={isReadOnly}
                            />
                            {fieldErrors[errorKey("residenceType")] && (
                              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                                {fieldErrors[errorKey("residenceType")]}
                              </p>
                            )}
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">
                              {APPLICANT_CONTENT.application.sections.currentAddress.duration} :{" "}
                              <span className="font-medium">
                                {getPrevAddressDuration(index)}{" "}
                                {APPLICANT_CONTENT.application.sections.currentAddress.months}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>

                      <ResidenceForm
                        residenceType={addr.residenceType}
                        isReadOnly={isReadOnly}
                        isPreviousAddress={true}
                        rentProps={
                          addr.residenceType === "rent"
                            ? {
                              landlordName: addr.landlordName,
                              landlordEmail: addr.landlordEmail,
                              landlordMobile: addr.landlordMobile,
                              moveInDate: addr.moveInDateRent,
                              leaseEndDate: addr.leaseEndDate,
                              rentAmount: addr.rentAmount,
                              stillHere: addr.stillHereRent,
                              onLandlordNameChange: createAction("landlordName"),
                              onLandlordEmailChange: createAction("landlordEmail"),
                              onLandlordMobileChange: createAction("landlordMobile"),
                              onMoveInDateChange: createAction("moveInDateRent"),
                              onLeaseEndDateChange: createAction("leaseEndDate"),
                              onRentAmountChange: createAction("rentAmount"),
                              monthsPaid: addr.monthsPaidRent,
                              onMonthsPaidChange: createAction("monthsPaidRent"),
                              onStillHereChange: createAction("stillHereRent"),
                              errors: {
                                landlordName: fieldErrors[errorKey("landlordName")],
                                landlordEmail: fieldErrors[errorKey("landlordEmail")],
                                landlordMobile: fieldErrors[errorKey("landlordMobile")],
                                moveInDate: fieldErrors[errorKey("moveInDateRent")],
                                rentAmount: fieldErrors[errorKey("rentAmount")],
                                monthsPaidRent: fieldErrors[errorKey("monthsPaidRent")],
                              },
                            }
                            : undefined
                        }
                        ownProps={
                          addr.residenceType === "own"
                            ? {
                              moveInDate: addr.moveInDateOwn,
                              plannedMoveOutDate: addr.plannedMoveOutDate,
                              monthlyMortgagePayment: addr.monthlyMortgagePayment,
                              monthsResided: addr.monthsResided,
                              stillHere: addr.stillHereOwn,
                              onMoveInDateChange: createAction("moveInDateOwn"),
                              onPlannedMoveOutDateChange: createAction("plannedMoveOutDate"),
                              onMonthlyMortgagePaymentChange: createAction("monthlyMortgagePayment"),
                              onMonthsResidedChange: createAction("monthsResided"),
                              onStillHereChange: createAction("stillHereOwn"),
                              errors: {
                                moveInDate: fieldErrors[errorKey("moveInDateOwn")],
                                monthlyMortgagePayment: fieldErrors[errorKey("monthlyMortgagePayment")],
                                monthsResided: fieldErrors[errorKey("monthsResided")],
                              },
                            }
                            : undefined
                        }
                        familyProps={
                          addr.residenceType === "family"
                            ? {
                              contactName: addr.contactName,
                              contactRelationship: addr.contactRelationship,
                              contactEmail: addr.contactEmail,
                              contactMobile: addr.contactMobile,
                              contactWebsite: addr.contactWebsite,
                              moveInDate: addr.moveInDateFamily,
                              plannedMoveOutDate: addr.plannedMoveOutDateFamily,
                              rentAmount: addr.rentAmountFamily,
                              stillHere: addr.stillHereFamily,
                              onContactNameChange: createAction("contactName"),
                              onContactRelationshipChange: createAction("contactRelationship"),
                              onContactEmailChange: createAction("contactEmail"),
                              onContactMobileChange: createAction("contactMobile"),
                              onContactWebsiteChange: createAction("contactWebsite"),
                              onMoveInDateChange: createAction("moveInDateFamily"),
                              onPlannedMoveOutDateChange: createAction("plannedMoveOutDateFamily"),
                              onRentAmountChange: createAction("rentAmountFamily"),
                              monthsPaid: addr.monthsPaidRent,
                              onMonthsPaidChange: createAction("monthsPaidRent"),
                              onStillHereChange: createAction("stillHereFamily"),
                              errors: {
                                contactName: fieldErrors[errorKey("contactName")],
                                contactRelationship: fieldErrors[errorKey("contactRelationship")],
                                contactEmail: fieldErrors[errorKey("contactEmail")],
                                contactMobile: fieldErrors[errorKey("contactMobile")],
                                moveInDate: fieldErrors[errorKey("moveInDateFamily")],
                                monthsPaidRent: fieldErrors[errorKey("monthsPaidRent")],
                              },
                            }
                            : undefined
                        }
                      />
                    </div>
                  </div>
                );
              })}

              {/* Save Button for Previous Address */}
              {!isReadOnly && (
                <div className="pt-4 pb-5 border-t border-gray-200">
                  {savePrevAddressMessage && (
                    <div
                      className={`mb-3 p-3 rounded-lg ${savePrevAddressMessage?.type === "success"
                        ? "bg-green-50 text-green-800 border border-green-200"
                        : "bg-red-50 text-red-800 border border-red-200"
                        }`}
                    >
                      <p className="text-sm font-medium">
                        {savePrevAddressMessage?.text}
                      </p>
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      // Prevent API calls when application is read-only
                      if (isReadOnly) {
                        return;
                      }

                      if (!applicationData?.applicant_id) {
                        setSavePrevAddressMessage({
                          type: "error",
                          text: APPLICANT_CONTENT.errors.applicantIdNotFound,
                        });
                        return;
                      }

                      // Check if all addresses are outside US and checkbox is not checked
                      if (validateAllUSAddress() && !noAlternateAddress) {
                        setValidationModalMessage(ADDRESS_OUTSIDE_USA_MESSAGE);
                    setShowAddressValidationModal(true);
                        return;
                      }

                      setIsSavingPrevAddress(true);
                      setSavePrevAddressMessage(null);

                      // Validate previous address fields - same rules as Current Living Address
                      const errors: typeof fieldErrors = {};

                      previousAddresses.forEach((addr, index) => {
                        const pfx = index === 0 ? "prev" : `prev${index}-`;
                        const errorKey = (key: string) => index === 0 ? `prev${key.charAt(0).toUpperCase() + key.slice(1)}` : `${pfx}${key}`;

                        if (!addr.streetNumber.trim()) {
                          errors[errorKey("streetNumber")] = APPLICANT_CONTENT.errors.fieldRequired.streetNumber;
                        }
                        if (!addr.streetName.trim()) {
                          errors[errorKey("streetName")] = APPLICANT_CONTENT.errors.fieldRequired.streetName;
                        }
                        if (!addr.city.trim()) {
                          errors[errorKey("city")] = APPLICANT_CONTENT.errors.fieldRequired.city;
                        }
                        if (!addr.state.trim()) {
                          errors[errorKey("state")] = APPLICANT_CONTENT.errors.fieldRequired.state;
                        }
                        const prevDigitsOnly = addr.zipCode.replace(/\D/g, "");
                        if (!prevDigitsOnly) {
                          errors[errorKey("zipCode")] = APPLICANT_CONTENT.errors.fieldRequired.zipCode;
                        } else if (prevDigitsOnly.length !== 5 && prevDigitsOnly.length !== 6) {
                          errors[errorKey("zipCode")] = APPLICANT_CONTENT.errors.fieldRequired.zipCodeLength;
                        }
                        if (!addr.residenceType) {
                          errors[errorKey("residenceType")] = APPLICANT_CONTENT.errors.fieldRequired.residenceType;
                        }
                        if (addr.residenceType === "rent") {
                          if (!addr.landlordName.trim()) {
                            errors[errorKey("landlordName")] = APPLICANT_CONTENT.errors.fieldRequired.landlordName;
                          }
                          if (!addr.landlordEmail.trim()) {
                            errors[errorKey("landlordEmail")] = APPLICANT_CONTENT.errors.fieldRequired.landlordEmail;
                          } else if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(addr.landlordEmail.trim())) {
                            errors[errorKey("landlordEmail")] = "Enter Valid Email";
                          }
                          if (!addr.landlordMobile.trim()) {
                            errors[errorKey("landlordMobile")] = APPLICANT_CONTENT.errors.fieldRequired.landlordMobile;
                          } else if (!/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(addr.landlordMobile.trim().replace(/\D/g, ""))) {
                            errors[errorKey("landlordMobile")] = "Enter Valid Phone Number";
                          }
                          if (!addr.moveInDateRent) {
                            errors[errorKey("moveInDateRent")] = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
                          }
                          if (!addr.rentAmount.trim()) {
                            errors[errorKey("rentAmount")] = APPLICANT_CONTENT.errors.fieldRequired.rentAmount;
                          }
                          if (!addr.monthsPaidRent.trim()) {
                            errors[errorKey("monthsPaidRent")] = APPLICANT_CONTENT.errors.fieldRequired.monthsPaidRent;
                          }
                        } else if (addr.residenceType === "own") {
                          if (!addr.moveInDateOwn) {
                            errors[errorKey("moveInDateOwn")] = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
                          }
                          // Previous-address OWN does not capture mortgage payment (field is not
                          // rendered for previous addresses; Angular parity uses month_reside only).
                          if (!addr.monthsResided?.trim()) {
                            errors[errorKey("monthsResided")] = APPLICANT_CONTENT.errors.fieldRequired.monthsResided;
                          }
                        } else if (addr.residenceType === "family") {
                          if (!addr.contactName.trim()) {
                            errors[errorKey("contactName")] = APPLICANT_CONTENT.errors.fieldRequired.contactName;
                          }
                          if (!addr.contactRelationship.trim()) {
                            errors[errorKey("contactRelationship")] = APPLICANT_CONTENT.errors.fieldRequired.contactRelationship;
                          }
                          if (!addr.contactEmail.trim()) {
                            errors[errorKey("contactEmail")] = APPLICANT_CONTENT.errors.fieldRequired.contactEmail;
                          } else if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(addr.contactEmail.trim())) {
                            errors[errorKey("contactEmail")] = "Enter Valid Email";
                          }
                          if (!addr.contactMobile.trim()) {
                            errors[errorKey("contactMobile")] = APPLICANT_CONTENT.errors.fieldRequired.contactMobile;
                          } else if (!/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(addr.contactMobile.trim().replace(/\D/g, ""))) {
                            errors[errorKey("contactMobile")] = "Enter Valid Phone Number";
                          }
                          if (!addr.moveInDateFamily) {
                            errors[errorKey("moveInDateFamily")] = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
                          }
                          if (!addr.monthsPaidRent.trim()) {
                            errors[errorKey("monthsPaidRent")] = APPLICANT_CONTENT.errors.fieldRequired.monthsPaidRent;
                          }
                        }
                      });

                      if (Object.keys(errors).length > 0) {
                        setFieldErrors(errors);
                        setIsSavingPrevAddress(false);
                        const errorMessages: string[] = [];

                        previousAddresses.forEach((_addr, index) => {
                          const pfx = index === 0 ? "prev" : `prev${index}-`;
                          const errorKey = (key: string) => index === 0 ? `prev${key.charAt(0).toUpperCase() + key.slice(1)}` : `${pfx}${key}`;

                          if (errors[errorKey("streetNumber")]) errorMessages.push(`Previous Address ${index + 1} - Street Number: ${errors[errorKey("streetNumber")]}`);
                          if (errors[errorKey("streetName")]) errorMessages.push(`Previous Address ${index + 1} - Street Name: ${errors[errorKey("streetName")]}`);
                          if (errors[errorKey("city")]) errorMessages.push(`Previous Address ${index + 1} - City: ${errors[errorKey("city")]}`);
                          if (errors[errorKey("state")]) errorMessages.push(`Previous Address ${index + 1} - State: ${errors[errorKey("state")]}`);
                          if (errors[errorKey("zipCode")]) errorMessages.push(`Previous Address ${index + 1} - Zip Code: ${errors[errorKey("zipCode")]}`);
                          if (errors[errorKey("residenceType")]) errorMessages.push(`Previous Address ${index + 1} - Type of residence: ${errors[errorKey("residenceType")]}`);
                          if (errors[errorKey("landlordName")]) errorMessages.push(`Previous Address ${index + 1} - Landlord Name: ${errors[errorKey("landlordName")]}`);
                          if (errors[errorKey("landlordEmail")]) errorMessages.push(`Previous Address ${index + 1} - Landlord Email: ${errors[errorKey("landlordEmail")]}`);
                          if (errors[errorKey("landlordMobile")]) errorMessages.push(`Previous Address ${index + 1} - Landlord Mobile: ${errors[errorKey("landlordMobile")]}`);
                          if (errors[errorKey("moveInDateRent")]) errorMessages.push(`Previous Address ${index + 1} - Move In Date: ${errors[errorKey("moveInDateRent")]}`);
                          if (errors[errorKey("rentAmount")]) errorMessages.push(`Previous Address ${index + 1} - Rent Amount: ${errors[errorKey("rentAmount")]}`);
                          if (errors[errorKey("monthsPaidRent")]) errorMessages.push(`Previous Address ${index + 1} - Months Paid: ${errors[errorKey("monthsPaidRent")]}`);
                          if (errors[errorKey("moveInDateOwn")]) errorMessages.push(`Previous Address ${index + 1} - Move In Date: ${errors[errorKey("moveInDateOwn")]}`);
                          if (errors[errorKey("monthsResided")]) errorMessages.push(`Previous Address ${index + 1} - Number of months you resided: ${errors[errorKey("monthsResided")]}`);
                          if (errors[errorKey("contactName")]) errorMessages.push(`Previous Address ${index + 1} - Contact Name: ${errors[errorKey("contactName")]}`);
                          if (errors[errorKey("contactRelationship")]) errorMessages.push(`Previous Address ${index + 1} - Contact Relationship: ${errors[errorKey("contactRelationship")]}`);
                          if (errors[errorKey("contactEmail")]) errorMessages.push(`Previous Address ${index + 1} - Contact Email: ${errors[errorKey("contactEmail")]}`);
                          if (errors[errorKey("contactMobile")]) errorMessages.push(`Previous Address ${index + 1} - Contact Mobile: ${errors[errorKey("contactMobile")]}`);
                          if (errors[errorKey("moveInDateFamily")]) errorMessages.push(`Previous Address ${index + 1} - Move In Date: ${errors[errorKey("moveInDateFamily")]}`);
                        });

                        if (errorMessages.length > 0) {
                          setValidationModalMessage(errorMessages.join("\n"));
                          setShowAddressValidationModal(true);
                        }
                        return;
                      }

                      // Clear errors if validation passes
                      setFieldErrors({});

                      try {
                        // Build previous_address array
                        const apiPreviousAddresses = previousAddresses
                          .map(addr => buildAddressData(addr, addr.ra_id ?? null, true))
                          .filter((a): a is NonNullable<typeof a> => a != null);

                        if (!apiPreviousAddresses || apiPreviousAddresses.length === 0) {
                          setSavePrevAddressMessage({
                            type: "error",
                            text: APPLICANT_CONTENT.errors.buildPreviousAddressDataFailed,
                          });
                          setIsSavingPrevAddress(false);
                          return;
                        }

                        // Build current_address (needed for API)
                        const currentAddressData = buildCurrentAddress();
                        if (!currentAddressData) {
                          setSavePrevAddressMessage({
                            type: "error",
                            text: APPLICANT_CONTENT.errors.buildAddressDataFailed,
                          });
                          setIsSavingPrevAddress(false);
                          return;
                        }

                        // Determine if address is outside US
                        const countryCode = (
                          currentAddressState.country || "US"
                        ).toUpperCase();
                        const isOutsideUS = countryCode !== "US" || outsideUS;

                        const updateData: UpdateApplicationRequest = {
                          ...residentSectionValidation("about"),
                          current_address: currentAddressData,
                          previous_address: apiPreviousAddresses,
                          ...(isOutsideUS
                            ? {
                              non_us_address: noAlternateAddress
                                ? "1"
                                : "0",
                            }
                            : { non_us_address: "0" }),
                          // Include alternate addresses
                          alternate_address:
                            otherAddresses.length > 0
                              ? otherAddresses.map((addr) => ({
                                ra_id: addr.ra_id || null,
                                street_address:
                                  buildAlternateAddressStreetAddress(addr),
                                outside_us: addr.outsideUS,
                                house_no: addr.streetNumber,
                                street: addr.streetName,
                                street_type: addr.streetType || "",
                                apartment: addr.aptUnit || null,
                                city: addr.city,
                                state: addr.state,
                                zip_code: addr.zipCode,
                                country: addr.country,
                                address_type: undefined,
                                address_data: undefined,
                              }))
                              : [],
                        };

                        // Calculate deleted alternate addresses
                        const currentAlternateAddressIds = new Set<number>();
                        otherAddresses.forEach((addr) => {
                          if (addr.ra_id) {
                            const raIdNumber =
                              typeof addr.ra_id === "string"
                                ? parseInt(addr.ra_id, 10)
                                : addr.ra_id;
                            if (!isNaN(raIdNumber)) {
                              currentAlternateAddressIds.add(raIdNumber);
                            }
                          }
                        });

                        const deletedAlternateAddressIds: number[] = [];
                        originalAlternateAddressIds.forEach((raId) => {
                          if (!currentAlternateAddressIds.has(raId)) {
                            deletedAlternateAddressIds.push(raId);
                          }
                        });

                        if (deletedAlternateAddressIds.length > 0) {
                          updateData.deleted = {
                            ...(updateData.deleted || {}),
                            alternate_address: deletedAlternateAddressIds,
                          };
                        }

                        const updatedData = await dispatch(
                          updateApplication({
                            applicantId: applicationData.applicant_id,
                            updateData: updateData,
                          }),
                        ).unwrap();

                        // Update local state with the latest alternate addresses from API response
                        if (
                          updatedData?.alternate_address &&
                          Array.isArray(updatedData.alternate_address)
                        ) {
                          const updatedAddresses =
                            updatedData.alternate_address.map(
                              (addr: Address, index: number) => ({
                                id: `other-addr-${addr.ra_id || index}-${Date.now()}`,
                                address: formatAddressDisplay(addr),
                                streetNumber: addr.house_no || "",
                                streetName: addr.street || "",
                                streetType: addr.street_type || "",
                                aptUnit: addr.apartment || "",
                                city: addr.city || "",
                                state: addr.state || "",
                                zipCode: addr.zip_code || "",
                                country:
                                  addr.country ||
                                  ADDRESS_CONSTANTS.DEFAULT_COUNTRY,
                                outsideUS:
                                  typeof addr.outside_us === "string"
                                    ? addr.outside_us === "1"
                                    : addr.outside_us || false,
                                showDetails: false,
                                ra_id: addr.ra_id || null,
                              }),
                            );
                          setOtherAddresses(updatedAddresses);

                          const updatedOriginalIds = new Set<number>();
                          updatedData.alternate_address.forEach(
                            (addr: Address) => {
                              if (addr.ra_id) {
                                const raIdNumber =
                                  typeof addr.ra_id === "string"
                                    ? parseInt(addr.ra_id, 10)
                                    : addr.ra_id;
                                if (!isNaN(raIdNumber)) {
                                  updatedOriginalIds.add(raIdNumber);
                                }
                              }
                            },
                          );
                          setOriginalAlternateAddressIds(updatedOriginalIds);
                        } else {
                          setOtherAddresses([]);
                          setOriginalAlternateAddressIds(new Set());
                        }

                        setSavePrevAddressMessage({
                          type: "success",
                          text: APPLICANT_CONTENT.application.messages.saveAddressSuccess,
                        });

                        setTimeout(() => {
                          setSavePrevAddressMessage(null);
                        }, 3000);
                      } catch (error) {
                        trackApplicationAutosave("about_yourself", 1, "error");
                        setValidationModalMessage(
                          typeof error === "string"
                            ? error
                            : (error as { message?: string })?.message ||
                              APPLICANT_CONTENT.errors.saveAddressFailed,
                        );
                        setShowAddressValidationModal(true);
                      } finally {
                        setIsSavingPrevAddress(false);
                      }
                    }}
                    disabled={
                      isSavingPrevAddress || !applicationData?.applicant_id
                    }
                    data-testid="about-save-prev-address-button"
                    className="inline-flex items-center gap-2 border-2 border-[#1597ff] text-[#1597ff] hover:bg-blue-50 font-medium px-8 py-2.5 rounded-lg transition-all hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingPrevAddress ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {APPLICANT_CONTENT.application.messages.saving}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        {APPLICANT_CONTENT.application.sections.personalProfile.save}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="pt-6 border-t border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Other Address for Credit Search (Optional)
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              If for some reason, we are unable to match your address(es) above
              to your credit file, you may enter another address here.
            </p>
            {/* Only show checkbox if current address is outside US */}
            {(() => {
              const isCurrentAddressOutsideUS =
                currentAddressState.outsideUS ||
                (currentAddressState.country &&
                  currentAddressState.country.toUpperCase() !== "US");
              return isCurrentAddressOutsideUS ? (
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    data-testid="about-no-alternate-address-checkbox"
                    checked={noAlternateAddress}
                    onChange={(e) => setNoAlternateAddress(e.target.checked)}
                    disabled={isReadOnly}
                    className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed"
                  />
                  <label className="text-sm text-gray-700 cursor-pointer">
                    I don't have an alternate address in the US
                  </label>
                </div>
              ) : null;
            })()}

            {/* Display all other addresses */}
            <div className="space-y-4 mb-4">
              {otherAddresses.map((addr) => (
                <div key={addr.id} className="mb-4">
                  <div className="flex items-center mb-2 relative">
                    <h4 className="text-sm font-semibold text-gray-900">
                      Alternate Address
                    </h4>
                    <button
                      data-testid={`about-remove-alternate-address-${addr.id}-button`}
                      onClick={() => {
                        setOtherAddresses((prev) =>
                          prev.filter((a) => a.id !== addr.id),
                        );
                        setOtherAddressFieldErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors[addr.id];
                          return newErrors;
                        });
                      }}
                      disabled={isReadOnly}
                      className={`text-red-600 hover:text-red-700 text-sm font-medium ml-auto flex-shrink-0 ${isReadOnly ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      Remove
                    </button>
                  </div>

                  <div className="flex gap-2 items-center mb-3">
                    <div className="flex-1 max-w-2xl">
                      <AddressAutocomplete
                        value={addr.address}
                        onChange={(value) => {
                          setOtherAddresses((prev) =>
                            prev.map((a) =>
                              a.id === addr.id ? { ...a, address: value } : a,
                            ),
                          );
                        }}
                        onPlaceSelect={(place: PlaceResult) => {
                          const parsed = parseAddressComponents(place);
                          const aptUnit = extractAptUnit(
                            parsed.formattedAddress || "",
                          );
                          setOtherAddresses((prev) =>
                            prev.map((a) =>
                              a.id === addr.id
                                ? {
                                  ...a,
                                  address: parsed.formattedAddress || "",
                                  streetNumber: parsed.streetNumber || "",
                                  streetName: parsed.streetName || "",
                                  aptUnit: aptUnit || "",
                                  city: parsed.city || "",
                                  state: parsed.state || "",
                                  zipCode: parsed.zipCode || "",
                                  country:
                                    parsed.country ||
                                    ADDRESS_CONSTANTS.DEFAULT_COUNTRY,
                                  outsideUS: parsed.country !== "US",
                                }
                                : a,
                            ),
                          );
                          // Clear errors for this address when Google autofills
                          setOtherAddressFieldErrors((prev) => ({
                            ...prev,
                            [addr.id]: {},
                          }));
                        }}
                        placeholder="Enter your address"
                        label=""
                        disabled={isReadOnly}
                      />
                    </div>
                    <button
                      data-testid={`about-toggle-alternate-address-detail-${addr.id}-button`}
                      onClick={() => {
                        setOtherAddresses((prev) =>
                          prev.map((a) =>
                            a.id === addr.id
                              ? { ...a, showDetails: !a.showDetails }
                              : a,
                          ),
                        );
                      }}
                      className="px-4 py-2.5 bg-[#1597ff] text-white rounded-lg hover:bg-[#0d7acc] transition-colors text-sm font-medium whitespace-nowrap"
                    >
                      {addr.showDetails ? APPLICANT_CONTENT.application.buttons.closeDetail : APPLICANT_CONTENT.application.buttons.viewDetail}
                    </button>
                  </div>

                  {/* Detailed address fields - shown when showDetails is true */}
                  {addr.showDetails && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <AddressBasicFields
                        state={{
                          address: addr.address,
                          streetNumber: addr.streetNumber,
                          streetName: addr.streetName,
                          streetType: addr.streetType || "",
                          aptUnit: addr.aptUnit,
                          city: addr.city,
                          state: addr.state,
                          zipCode: addr.zipCode,
                          country: addr.country,
                          outsideUS: addr.outsideUS,
                          residenceType: null,
                          stillHereRent: false,
                          moveInDateRent: "",
                          leaseEndDate: "",
                          landlordName: "",
                          landlordEmail: "",
                          landlordMobile: "",
                          rentAmount: "",
                          monthsPaidRent: "",
                          stillHereOwn: false,
                          moveInDateOwn: "",
                          plannedMoveOutDate: "",
                          monthlyMortgagePayment: "",
                          stillHereFamily: false,
                          moveInDateFamily: "",
                          plannedMoveOutDateFamily: "",
                          contactName: "",
                          contactEmail: "",
                          contactMobile: "",
                          contactWebsite: "",
                          contactRelationship: "",
                          rentAmountFamily: "",
                          monthsResided: "",
                        }}
                        actions={{
                          setAddress: (value) => {
                            setOtherAddresses((prev) =>
                              prev.map((a) =>
                                a.id === addr.id ? { ...a, address: value } : a,
                              ),
                            );
                          },
                          setStreetNumber: (value) => {
                            setOtherAddresses((prev) =>
                              prev.map((a) =>
                                a.id === addr.id
                                  ? { ...a, streetNumber: value }
                                  : a,
                              ),
                            );
                            // Clear error when user types
                            setOtherAddressFieldErrors((prev) => ({
                              ...prev,
                              [addr.id]: {
                                ...(prev[addr.id] || {}),
                                streetNumber: undefined,
                              },
                            }));
                          },
                          setStreetName: (value) => {
                            setOtherAddresses((prev) =>
                              prev.map((a) =>
                                a.id === addr.id
                                  ? { ...a, streetName: value }
                                  : a,
                              ),
                            );
                            // Clear error when user types
                            setOtherAddressFieldErrors((prev) => ({
                              ...prev,
                              [addr.id]: {
                                ...(prev[addr.id] || {}),
                                streetName: undefined,
                              },
                            }));
                          },
                          setStreetType: (value) => {
                            setOtherAddresses((prev) =>
                              prev.map((a) =>
                                a.id === addr.id
                                  ? { ...a, streetType: value }
                                  : a,
                              ),
                            );
                          },
                          setAptUnit: (value) => {
                            setOtherAddresses((prev) =>
                              prev.map((a) =>
                                a.id === addr.id ? { ...a, aptUnit: value } : a,
                              ),
                            );
                          },
                          setCity: (value) => {
                            setOtherAddresses((prev) =>
                              prev.map((a) =>
                                a.id === addr.id ? { ...a, city: value } : a,
                              ),
                            );
                            // Clear error when user types
                            setOtherAddressFieldErrors((prev) => ({
                              ...prev,
                              [addr.id]: {
                                ...(prev[addr.id] || {}),
                                city: undefined,
                              },
                            }));
                          },
                          setState: (value) => {
                            setOtherAddresses((prev) =>
                              prev.map((a) =>
                                a.id === addr.id ? { ...a, state: value } : a,
                              ),
                            );
                            // Clear error when user types
                            setOtherAddressFieldErrors((prev) => ({
                              ...prev,
                              [addr.id]: {
                                ...(prev[addr.id] || {}),
                                state: undefined,
                              },
                            }));
                          },
                          setZipCode: (value) => {
                            setOtherAddresses((prev) =>
                              prev.map((a) =>
                                a.id === addr.id ? { ...a, zipCode: value } : a,
                              ),
                            );
                            // Clear error when user types
                            setOtherAddressFieldErrors((prev) => ({
                              ...prev,
                              [addr.id]: {
                                ...(prev[addr.id] || {}),
                                zipCode: undefined,
                              },
                            }));
                          },
                          setCountry: (value) => {
                            setOtherAddresses((prev) =>
                              prev.map((a) =>
                                a.id === addr.id ? { ...a, country: value } : a,
                              ),
                            );
                          },
                          setOutsideUS: (value) => {
                            setOtherAddresses((prev) =>
                              prev.map((a) =>
                                a.id === addr.id
                                  ? { ...a, outsideUS: value }
                                  : a,
                              ),
                            );
                          },
                          setResidenceType: () => { },
                          setStillHereRent: () => { },
                          setMoveInDateRent: () => { },
                          setLeaseEndDate: () => { },
                          setLandlordName: () => { },
                          setLandlordEmail: () => { },
                          setLandlordMobile: () => { },
                          setRentAmount: () => { },
                          setMonthsPaidRent: () => { },
                          setStillHereOwn: () => { },
                          setMoveInDateOwn: () => { },
                          setPlannedMoveOutDate: () => { },
                          setMonthlyMortgagePayment: () => { },
                          setStillHereFamily: () => { },
                          setMoveInDateFamily: () => { },
                          setPlannedMoveOutDateFamily: () => { },
                          setContactName: () => { },
                          setContactEmail: () => { },
                          setContactMobile: () => { },
                          setContactWebsite: () => { },
                          setContactRelationship: () => { },
                          setRentAmountFamily: () => { },
                          setMonthsResided: (value) => {
                            setOtherAddresses((prev) =>
                              prev.map((a) =>
                                a.id === addr.id
                                  ? { ...a, monthsResided: value }
                                  : a,
                              ),
                            );
                          },
                        }}
                        states={states}
                        isLoadingStates={isLoadingStates}
                        fieldErrors={otherAddressFieldErrors[addr.id] || {}}
                        onFieldErrorChange={(field, error) => {
                          setOtherAddressFieldErrors((prev) => ({
                            ...prev,
                            [addr.id]: {
                              ...(prev[addr.id] || {}),
                              [field]: error,
                            },
                          }));
                        }}
                        hideAddressField={true}
                        hideCheckbox={true}
                        isReadOnly={isReadOnly}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add Other Address Button - always at bottom */}
            <button
              data-testid="about-add-other-address-button"
              onClick={() => {
                // Add new empty address entry
                const newAddress = {
                  id: `other-addr-${Date.now()}`,
                  address: "",
                  streetNumber: "",
                  streetName: "",
                  streetType: "",
                  aptUnit: "",
                  city: "",
                  state: "",
                  zipCode: "",
                  country: ADDRESS_CONSTANTS.DEFAULT_COUNTRY,
                  outsideUS: false,
                  showDetails: false,
                  ra_id: null,
                };
                setOtherAddresses((prev) => [...prev, newAddress]);
              }}
              disabled={isReadOnly}
              className={`text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center transition-colors ${isReadOnly ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <span className="text-xl mr-2">+</span> Add Other Address
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-4 pb-6">
          {!isReadOnly && (
            <button
              onClick={async () => {
                // If read-only mode, just navigate to next section without saving
                if (isReadOnly) {
                  const propertyPath = applicantId
                    ? getTabPathWithApplicantId("property", applicantId)
                    : "/applicant/application/property";
                  if (applicantId) {
                    navigate(propertyPath, getTabNavigationOptions("property"));
                  } else {
                    navigate(propertyPath);
                  }
                  return;
                }

                if (!applicationData?.applicant_id) {
                  setSaveAddressMessage({
                    type: "error",
                    text: "Application ID not found. Please refresh the page and try again.",
                  });
                  setTimeout(() => setSaveAddressMessage(null), 5000);
                  return;
                }

                // Check if all addresses are outside US and checkbox is not checked
                if (validateAllUSAddress() && !noAlternateAddress) {
                        setValidationModalMessage(ADDRESS_OUTSIDE_USA_MESSAGE);
                        setShowAddressValidationModal(true);
                  return;
                }

                // Prevent API calls when application is read-only
                if (isReadOnly) {
                  return;
                }

                setIsSavingAndContinuing(true);
                setSaveAddressMessage(null);

                // --- Personal Profile Validation Start (Bottom Button) ---
                const personalErrorMessages: string[] = [];

                if (!firstName || firstName.trim() === "")
                  personalErrorMessages.push(
                    APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.firstName +
                    " is required",
                  );
                if (!lastName || lastName.trim() === "")
                  personalErrorMessages.push(
                    APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.lastName +
                    " is required",
                  );
                if (!mobilePhone || mobilePhone.trim() === "")
                  personalErrorMessages.push(
                    `${APPLICANT_CONTENT.application.sections.personalProfile.fields.mobilePhone} is required`,
                  );
                if (!email || email.trim() === "")
                  personalErrorMessages.push(
                    APPLICANT_CONTENT.application.sections.personalProfile.fields.email +
                    " is required",
                  );

                // Use existing validation function for DOB and SSN
                const personalValidation = validatePersonalProfile();
                if (!personalValidation.isValid) {
                  if (personalValidation.errors.dateOfBirth) {
                    personalErrorMessages.push(
                      personalValidation.errors.dateOfBirth,
                    );
                  }
                  if (personalValidation.errors.ssn) {
                    personalErrorMessages.push(personalValidation.errors.ssn);
                  }
                }

                if (personalErrorMessages.length > 0) {
                  setValidationModalMessage(personalErrorMessages.join("\n"));
                  setShowPersonalProfileValidationModal(true);
                  // Ensure loading state is reset
                  setIsSavingAndContinuing(false);
                  return;
                }
                // --- Personal Profile Validation End ---

                // Validate required fields
                const errors: typeof fieldErrors & {
                  streetNumber?: string;
                  streetName?: string;
                  city?: string;
                  state?: string;
                  zipCode?: string;
                  residenceType?: string;
                  landlordName?: string;
                  landlordEmail?: string;
                  landlordMobile?: string;
                  moveInDateRent?: string;
                  rentAmount?: string;
                  leaseEndDate?: string;
                  moveInDateOwn?: string;
                  plannedMoveOutDate?: string;
                  monthlyMortgagePayment?: string;
                  contactName?: string;
                  contactEmail?: string;
                  contactMobile?: string;
                  contactRelationship?: string;
                  moveInDateFamily?: string;
                  plannedMoveOutDateFamily?: string;
                  prevStreetNumber?: string;
                  prevZipCode?: string;
                  prevResidenceType?: string;
                  prevLandlordName?: string;
                  prevLandlordEmail?: string;
                  prevLandlordMobile?: string;
                  prevRentAmount?: string;
                  prevMoveInDateRent?: string;
                  prevMonthlyMortgagePayment?: string;
                  prevMoveInDateOwn?: string;
                  prevContactName?: string;
                  prevContactEmail?: string;
                  prevContactMobile?: string;
                  prevContactRelationship?: string;
                  prevMoveInDateFamily?: string;
                } = {};

                if (!streetNumber.trim()) {
                  errors.streetNumber = APPLICANT_CONTENT.errors.fieldRequired.streetNumber;
                }

                if (!streetName.trim()) {
                  errors.streetName = APPLICANT_CONTENT.errors.fieldRequired.streetName;
                }

                if (!city.trim()) {
                  errors.city = APPLICANT_CONTENT.errors.fieldRequired.city;
                }

                if (!state.trim()) {
                  errors.state = APPLICANT_CONTENT.errors.fieldRequired.state;
                }

                const digitsOnly = zipCode.replace(/\D/g, "");
                if (!digitsOnly) {
                  errors.zipCode = APPLICANT_CONTENT.errors.fieldRequired.zipCode;
                } else if (digitsOnly.length !== 5 && digitsOnly.length !== 6) {
                  errors.zipCode = APPLICANT_CONTENT.errors.fieldRequired.zipCodeLength;
                }

                // Validate current address residence type - required
                if (!residenceType) {
                  errors.residenceType = APPLICANT_CONTENT.errors.fieldRequired.residenceType;
                } else {
                  // Current address residence type specific validation
                  if (residenceType === "rent") {
                    if (!landlordName.trim()) {
                      errors.landlordName = APPLICANT_CONTENT.errors.fieldRequired.landlordName;
                    }
                    if (!landlordEmail.trim()) {
                      errors.landlordEmail = APPLICANT_CONTENT.errors.fieldRequired.landlordEmail;
                    } else if (
                      !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(
                        landlordEmail.trim(),
                      )
                    ) {
                      errors.landlordEmail = "Enter Valid Email";
                    }

                    if (!landlordMobile.trim()) {
                      errors.landlordMobile = APPLICANT_CONTENT.errors.fieldRequired.landlordMobile;
                    } else if (
                      !/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(
                        landlordMobile.trim().replace(/\D/g, ""),
                      )
                    ) {
                      errors.landlordMobile = "Enter Valid Phone Number";
                    }

                    if (!moveInDateRent) {
                      errors.moveInDateRent = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
                    }
                    if (!rentAmount.trim()) {
                      errors.rentAmount = APPLICANT_CONTENT.errors.fieldRequired.rentAmount;
                    }
                    if (!stillHereRent && !(leaseEndDate && leaseEndDate.trim())) {
                      errors.leaseEndDate = APPLICANT_CONTENT.errors.fieldRequired.leaseEndDate;
                    }
                  } else if (residenceType === "own") {
                    if (!moveInDateOwn) {
                      errors.moveInDateOwn = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
                    }
                    if (!monthlyMortgagePayment.trim()) {
                      errors.monthlyMortgagePayment = APPLICANT_CONTENT.errors.fieldRequired.monthlyMortgagePayment;
                    }
                    if (!stillHereOwn && !(plannedMoveOutDate && plannedMoveOutDate.trim())) {
                      errors.plannedMoveOutDate = APPLICANT_CONTENT.errors.fieldRequired.plannedMoveOutDate;
                    }
                  } else if (residenceType === "family") {
                    if (!contactName.trim()) {
                      errors.contactName = APPLICANT_CONTENT.errors.fieldRequired.contactName;
                    }
                    if (!contactRelationship.trim()) {
                      errors.contactRelationship = APPLICANT_CONTENT.errors.fieldRequired.contactRelationship;
                    }
                    if (!contactEmail.trim()) {
                      errors.contactEmail = APPLICANT_CONTENT.errors.fieldRequired.contactEmail;
                    } else if (
                      !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(
                        contactEmail.trim(),
                      )
                    ) {
                      errors.contactEmail = "Enter Valid Email";
                    }

                    if (!contactMobile.trim()) {
                      errors.contactMobile = APPLICANT_CONTENT.errors.fieldRequired.contactMobile;
                    } else if (
                      !/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(
                        contactMobile.trim().replace(/\D/g, ""),
                      )
                    ) {
                      errors.contactMobile = "Enter Valid Phone Number";
                    }

                    if (!moveInDateFamily) {
                      errors.moveInDateFamily = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
                    }
                    if (
                      !stillHereFamily &&
                      !(
                        plannedMoveOutDateFamily &&
                        plannedMoveOutDateFamily.trim()
                      )
                    ) {
                      errors.plannedMoveOutDateFamily = APPLICANT_CONTENT.errors.fieldRequired.plannedMoveOutDate;
                    }
                  }
                }

                // Previous address / 24-month history: only when current is valid (Save & Continue uses this path)
                if (
                  shouldEnforceAddressHistoryRules(
                    isCurrentAddressValid(),
                    shouldShowPreviousAddress(),
                  )
                ) {
                  previousAddresses.forEach((addr, index) => {
                    const pfx = index === 0 ? "prev" : `prev${index}-`;
                    const errorKey = (key: string) =>
                      index === 0
                        ? `prev${key.charAt(0).toUpperCase() + key.slice(1)}`
                        : `${pfx}${key}`;

                    if (!addr.residenceType) {
                      errors[errorKey("residenceType")] = APPLICANT_CONTENT.errors.fieldRequired.residenceType;
                    }

                    if (!addr.streetNumber || !addr.streetNumber.trim()) {
                      errors[errorKey("streetNumber")] = APPLICANT_CONTENT.errors.fieldRequired.streetNumber;
                    }

                    if (!addr.streetName || !addr.streetName.trim()) {
                      errors[errorKey("streetName")] = APPLICANT_CONTENT.errors.fieldRequired.streetName;
                    }

                    if (!addr.city || !addr.city.trim()) {
                      errors[errorKey("city")] = APPLICANT_CONTENT.errors.fieldRequired.city;
                    }

                    if (!addr.state || !addr.state.trim()) {
                      errors[errorKey("state")] = APPLICANT_CONTENT.errors.fieldRequired.state;
                    }

                    const prevDigitsOnly = (addr.zipCode || "").replace(/\D/g, "");
                    if (!prevDigitsOnly) {
                      errors[errorKey("zipCode")] = APPLICANT_CONTENT.errors.fieldRequired.zipCode;
                    } else if (
                      prevDigitsOnly.length !== 5 &&
                      prevDigitsOnly.length !== 6
                    ) {
                      errors[errorKey("zipCode")] = APPLICANT_CONTENT.errors.fieldRequired.zipCodeLength;
                    }

                    if (addr.residenceType === "rent") {
                      if (!addr.moveInDateRent) {
                        errors[errorKey("moveInDateRent")] = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
                      }
                      if (!addr.landlordName || !addr.landlordName.trim()) {
                        errors[errorKey("landlordName")] = APPLICANT_CONTENT.errors.fieldRequired.landlordName;
                      }
                      if (!addr.landlordEmail || !addr.landlordEmail.trim()) {
                        errors[errorKey("landlordEmail")] = APPLICANT_CONTENT.errors.fieldRequired.landlordEmail;
                      } else if (
                        !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(
                          addr.landlordEmail.trim(),
                        )
                      ) {
                        errors[errorKey("landlordEmail")] = "Enter Valid Email";
                      }
                      if (!addr.landlordMobile || !addr.landlordMobile.trim()) {
                        errors[errorKey("landlordMobile")] = APPLICANT_CONTENT.errors.fieldRequired.landlordMobile;
                      } else if (
                        !/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(
                          addr.landlordMobile.trim().replace(/\D/g, ""),
                        )
                      ) {
                        errors[errorKey("landlordMobile")] =
                          "Enter Valid Phone Number";
                      }
                      if (!addr.rentAmount || !addr.rentAmount.trim()) {
                        errors[errorKey("rentAmount")] = APPLICANT_CONTENT.errors.fieldRequired.rentAmount;
                      }
                    } else if (addr.residenceType === "own") {
                      if (!addr.moveInDateOwn) {
                        errors[errorKey("moveInDateOwn")] = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
                      }
                      // Previous-address OWN does not capture mortgage payment (field is not
                      // rendered for previous addresses; Angular parity uses month_reside only).
                      if (!addr.monthsResided || !addr.monthsResided.trim()) {
                        errors[errorKey("monthsResided")] = APPLICANT_CONTENT.errors.fieldRequired.monthsResided;
                      }
                    } else if (addr.residenceType === "family") {
                      if (!addr.contactName || !addr.contactName.trim()) {
                        errors[errorKey("contactName")] = APPLICANT_CONTENT.errors.fieldRequired.landlordName;
                      }
                      if (
                        !addr.contactRelationship ||
                        !addr.contactRelationship.trim()
                      ) {
                        errors[errorKey("contactRelationship")] = APPLICANT_CONTENT.errors.fieldRequired.contactRelationship;
                      }
                      if (!addr.contactEmail || !addr.contactEmail.trim()) {
                        errors[errorKey("contactEmail")] = APPLICANT_CONTENT.errors.fieldRequired.landlordEmail;
                      } else if (
                        !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(
                          addr.contactEmail.trim(),
                        )
                      ) {
                        errors[errorKey("contactEmail")] = "Enter Valid Email";
                      }
                      if (!addr.contactMobile || !addr.contactMobile.trim()) {
                        errors[errorKey("contactMobile")] = APPLICANT_CONTENT.errors.fieldRequired.landlordMobile;
                      } else if (
                        !/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(
                          addr.contactMobile.trim().replace(/\D/g, ""),
                        )
                      ) {
                        errors[errorKey("contactMobile")] =
                          "Enter Valid Phone Number";
                      }
                      if (!addr.moveInDateFamily) {
                        errors[errorKey("moveInDateFamily")] = APPLICANT_CONTENT.errors.fieldRequired.moveInDate;
                      }
                    }
                  });

                  const totalDuration = calculateTotalAddressDuration();
                  if (totalDuration < 24 && Object.keys(errors).length === 0) {
                    appendPreviousAddressSlotIfAngularAllows();
                    setValidationModalMessage(
                      `You must provide at least 24 months of address history to proceed.\nCurrent history provided: ${totalDuration} months.\nPlease add previous addresses to cover the remaining ${24 - totalDuration} months.`
                    );
                    setShowAddressValidationModal(true);
                    setIsSavingAndContinuing(false);
                    return;
                  }
                }

                // Validate alternate addresses (other addresses) if they exist
                // Only validate if the address has been started (has at least one field filled)
                const alternateAddressErrors: Record<
                  string,
                  {
                    streetNumber?: string;
                    streetName?: string;
                    city?: string;
                    state?: string;
                    zipCode?: string;
                  }
                > = {};

                otherAddresses.forEach((addr) => {
                  // Check if address has been started (has any field filled)
                  const hasStarted =
                    addr.address ||
                    addr.streetNumber ||
                    addr.streetName ||
                    addr.city ||
                    addr.state ||
                    addr.zipCode ||
                    addr.country;

                  if (hasStarted) {
                    const addrErrors: (typeof alternateAddressErrors)[string] =
                      {};

                    // Validate all required fields for this alternate address
                    if (!addr.streetNumber || !addr.streetNumber.trim()) {
                      addrErrors.streetNumber = APPLICANT_CONTENT.errors.fieldRequired.streetNumber;
                    }

                    if (!addr.streetName || !addr.streetName.trim()) {
                      addrErrors.streetName = APPLICANT_CONTENT.errors.fieldRequired.streetName;
                    }

                    if (!addr.city || !addr.city.trim()) {
                      addrErrors.city = APPLICANT_CONTENT.errors.fieldRequired.city;
                    }

                    if (!addr.state || !addr.state.trim()) {
                      addrErrors.state = APPLICANT_CONTENT.errors.fieldRequired.state;
                    }

                    const digitsOnly = (addr.zipCode || "").replace(/\D/g, "");
                    if (!digitsOnly) {
                      addrErrors.zipCode = APPLICANT_CONTENT.errors.fieldRequired.zipCode;
                    } else if (
                      digitsOnly.length !== 5 &&
                      digitsOnly.length !== 6
                    ) {
                      addrErrors.zipCode = APPLICANT_CONTENT.errors.fieldRequired.zipCodeLength;
                    }

                    // Only add to errors if there are any errors for this address
                    if (Object.keys(addrErrors).length > 0) {
                      alternateAddressErrors[addr.id] = addrErrors;
                    }
                  }
                });

                // Set alternate address errors
                if (Object.keys(alternateAddressErrors).length > 0) {
                  setOtherAddressFieldErrors(alternateAddressErrors);
                } else {
                  setOtherAddressFieldErrors({});
                }

                // Check if there are any validation errors (current/previous address or alternate addresses)
                const hasAlternateAddressErrors =
                  Object.keys(alternateAddressErrors).length > 0;
                const hasCurrentAddressErrors = Object.keys(errors).length > 0;

                if (hasCurrentAddressErrors || hasAlternateAddressErrors) {
                  setFieldErrors(errors);
                  setIsSavingAndContinuing(false);
                  // Show validation popup with all errors
                  const errorMessages: string[] = [];
                  if (errors.streetNumber)
                    errorMessages.push(`Street Number: ${errors.streetNumber}`);
                  if (errors.streetName)
                    errorMessages.push(`Street Name: ${errors.streetName}`);
                  if (errors.city) errorMessages.push(`City: ${errors.city}`);
                  if (errors.state)
                    errorMessages.push(`State: ${errors.state}`);
                  if (errors.zipCode)
                    errorMessages.push(`Zip Code: ${errors.zipCode}`);
                  if (errors.residenceType)
                    errorMessages.push(
                      `Residence Type: ${errors.residenceType}`,
                    );
                  if (errors.landlordName)
                    errorMessages.push(`Landlord Name: ${errors.landlordName}`);
                  if (errors.landlordEmail)
                    errorMessages.push(
                      `Landlord Email: ${errors.landlordEmail}`,
                    );
                  if (errors.landlordMobile)
                    errorMessages.push(
                      `Landlord Mobile: ${errors.landlordMobile}`,
                    );
                  if (errors.moveInDateRent)
                    errorMessages.push(
                      `Move In Date: ${errors.moveInDateRent}`,
                    );
                  if (errors.rentAmount)
                    errorMessages.push(`Rent Amount: ${errors.rentAmount}`);
                  if (errors.leaseEndDate)
                    errorMessages.push(`Lease End Date: ${errors.leaseEndDate}`);
                  if (errors.moveInDateOwn)
                    errorMessages.push(`Move In Date: ${errors.moveInDateOwn}`);
                  if (errors.monthlyMortgagePayment)
                    errorMessages.push(
                      `Monthly Mortgage Payment: ${errors.monthlyMortgagePayment}`,
                    );
                  if (errors.plannedMoveOutDate)
                    errorMessages.push(
                      `Planned Move Out Date: ${errors.plannedMoveOutDate}`,
                    );
                  if (errors.contactName)
                    errorMessages.push(`Contact Name: ${errors.contactName}`);
                  if (errors.contactRelationship)
                    errorMessages.push(
                      `Contact Relationship: ${errors.contactRelationship}`,
                    );
                  if (errors.contactEmail)
                    errorMessages.push(`Contact Email: ${errors.contactEmail}`);
                  if (errors.contactMobile)
                    errorMessages.push(
                      `Contact Mobile: ${errors.contactMobile}`,
                    );
                  if (errors.moveInDateFamily)
                    errorMessages.push(
                      `Move In Date: ${errors.moveInDateFamily}`,
                    );
                  if (errors.plannedMoveOutDateFamily)
                    errorMessages.push(
                      `Planned Move Out Date: ${errors.plannedMoveOutDateFamily}`,
                    );
                  previousAddresses.forEach((_addr, index) => {
                    const pfx = index === 0 ? "prev" : `prev${index}-`;
                    const errorKey = (key: string) =>
                      index === 0
                        ? `prev${key.charAt(0).toUpperCase() + key.slice(1)}`
                        : `${pfx}${key}`;

                    if (errors[errorKey("streetNumber")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Street Number: ${errors[errorKey("streetNumber")]}`,
                      );
                    if (errors[errorKey("streetName")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Street Name: ${errors[errorKey("streetName")]}`,
                      );
                    if (errors[errorKey("city")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - City: ${errors[errorKey("city")]}`,
                      );
                    if (errors[errorKey("state")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - State: ${errors[errorKey("state")]}`,
                      );
                    if (errors[errorKey("zipCode")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Zip Code: ${errors[errorKey("zipCode")]}`,
                      );
                    if (errors[errorKey("landlordName")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Landlord Name: ${errors[errorKey("landlordName")]}`,
                      );
                    if (errors[errorKey("landlordEmail")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Landlord Email: ${errors[errorKey("landlordEmail")]}`,
                      );
                    if (errors[errorKey("landlordMobile")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Landlord Mobile: ${errors[errorKey("landlordMobile")]}`,
                      );
                    if (errors[errorKey("moveInDateRent")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Move In Date: ${errors[errorKey("moveInDateRent")]}`,
                      );
                    if (errors[errorKey("rentAmount")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Rent Amount: ${errors[errorKey("rentAmount")]}`,
                      );
                    if (errors[errorKey("moveInDateOwn")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Move In Date: ${errors[errorKey("moveInDateOwn")]}`,
                      );
                    if (errors[errorKey("monthsResided")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Number of months you resided: ${errors[errorKey("monthsResided")]}`,
                      );
                    if (errors[errorKey("contactName")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Contact Name: ${errors[errorKey("contactName")]}`,
                      );
                    if (errors[errorKey("contactRelationship")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Contact Relationship: ${errors[errorKey("contactRelationship")]}`,
                      );
                    if (errors[errorKey("contactEmail")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Contact Email: ${errors[errorKey("contactEmail")]}`,
                      );
                    if (errors[errorKey("contactMobile")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Contact Mobile: ${errors[errorKey("contactMobile")]}`,
                      );
                    if (errors[errorKey("moveInDateFamily")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Move In Date: ${errors[errorKey("moveInDateFamily")]}`,
                      );
                    if (errors[errorKey("residenceType")])
                      errorMessages.push(
                        `Previous Address ${index + 1} - Residence Type: ${errors[errorKey("residenceType")]}`,
                      );
                  });
                  // Alternate address errors
                  Object.keys(alternateAddressErrors).forEach((addrId) => {
                    const addrErrors = alternateAddressErrors[addrId];
                    if (addrErrors.streetNumber)
                      errorMessages.push(
                        `Alternate Address - Street Number: ${addrErrors.streetNumber}`,
                      );
                    if (addrErrors.streetName)
                      errorMessages.push(
                        `Alternate Address - Street Name: ${addrErrors.streetName}`,
                      );
                    if (addrErrors.city)
                      errorMessages.push(
                        `Alternate Address - City: ${addrErrors.city}`,
                      );
                    if (addrErrors.state)
                      errorMessages.push(
                        `Alternate Address - State: ${addrErrors.state}`,
                      );
                    if (addrErrors.zipCode)
                      errorMessages.push(
                        `Alternate Address - Zip Code: ${addrErrors.zipCode}`,
                      );
                  });
                  if (errorMessages.length > 0) {
                    setValidationModalMessage(errorMessages.join("\n"));
                    setShowAddressValidationModal(true);
                  }
                  return; // BLOCK API CALL - Do not proceed with save when validation errors exist
                }

                // Clear errors if validation passes
                setFieldErrors({});
                setOtherAddressFieldErrors({});

                try {
                  // Build current_address according to API structure
                  const currentAddressData = buildCurrentAddress();
                  if (!currentAddressData) {
                    setSaveAddressMessage({
                      type: "error",
                      text: "Failed to build address data. Please check all required fields.",
                    });
                    setIsSavingAndContinuing(false);
                    setTimeout(() => setSaveAddressMessage(null), 5000);
                    return;
                  }

                  // Address history for the last 2 years is mandatory: when current address < 24 months, previous address is required
                  // Use full previousAddresses array (same as address section Save and handleSaveAllData) so all addresses are in payload
                  let apiPreviousAddresses: Address[] | null = null;
                  if (shouldShowPreviousAddress()) {
                    const allValid = previousAddresses.every(
                      (addr) => addr.residenceType,
                    );
                    if (!allValid) {
                      setSaveAddressMessage({
                        type: "error",
                        text: APPLICANT_CONTENT.application.sections.previousAddress.subtitle,
                      });
                      setIsSavingAndContinuing(false);
                      setTimeout(() => setSaveAddressMessage(null), 5000);
                      return;
                    }
                    apiPreviousAddresses = previousAddresses
                      .map((addr) => buildAddressData(addr, addr.ra_id ?? null, true))
                      .filter((a): a is Address => a != null);
                    if (
                      !apiPreviousAddresses ||
                      apiPreviousAddresses.length === 0
                    ) {
                      setSaveAddressMessage({
                        type: "error",
                        text: APPLICANT_CONTENT.application.sections.previousAddress.subtitle,
                      });
                      setIsSavingAndContinuing(false);
                      setTimeout(() => setSaveAddressMessage(null), 5000);
                      return;
                    }
                  }

                  // Format date for API - send YYYY-MM-DD to match Angular (validateData → formatDate → YYYY-MM-DD)
                  let dobForApi = "";
                  if (dateOfBirth) {
                    // Validate date before saving
                    const validation = validateDateOfBirth(dateOfBirth);
                    if (!validation.isValid) {
                      setIsSavingAndContinuing(false);
                      return;
                    }
                    // dateOfBirth is already YYYY-MM-DD; send as-is to match Angular
                    dobForApi = dateOfBirth;
                  }

                  // Determine if address is outside US
                  const countryCode = (
                    currentAddressState.country || "US"
                  ).toUpperCase();
                  const isOutsideUS = countryCode !== "US" || outsideUS;

                  // Prepare update data with personal profile fields AND address data
                  const updateData: UpdateApplicationRequest = {
                    ...residentTabValidation("about"),
                    // Personal profile fields
                    first_name: firstName || undefined,
                    middle_name: middleName || "",
                    last_name: lastName || undefined,
                    suffix: suffix || undefined,
                    mobile_no: unformatPhoneNumber(mobilePhone) || undefined,
                    shared_phone_no: isSharedPhone ? "1" : "0",
                    email: email || undefined,
                    communication_preference:
                      commPreference === "text" ? APPLICANT_CONTENT.application.sections.personalProfile.fields.textMsg : APPLICANT_CONTENT.application.sections.personalProfile.fields.email,
                    birthdate: dobForApi || undefined,
                    security_number: ssn || undefined,
                    enter_date: {}, // Required empty object as per API structure
                    is_mobile_no: mobilePhone ? "1" : "0",
                    update_via_sms: "0", // Default to '0' as per API structure
                    // Address fields
                    current_address: currentAddressData,
                    // non_us_address logic:
                    // - If user has alternate US address: false/"0" (or omit)
                    // - If user does NOT have alternate US address: true/"1"
                    // Only include if address is outside US
                    // Convert boolean to string "1" or "0" to match API format
                    ...(isOutsideUS
                      ? { non_us_address: noAlternateAddress ? "1" : "0" }
                      : { non_us_address: "0" }),
                  };

                  // Remove undefined fields from personal profile
                  Object.keys(updateData).forEach((key) => {
                    if (
                      key !== "current_address" &&
                      key !== "previous_address" &&
                      key !== "alternate_address" &&
                      key !== "non_us_address" &&
                      key !== "enter_date" &&
                      key !== "shared_phone_no" &&
                      key !== "is_mobile_no" &&
                      key !== "update_via_sms" &&
                      key !== "communication_preference" &&
                      key !== "validation_scope" &&
                      key !== "validation_tab" &&
                      key !== "validation"
                    ) {
                      if (
                        updateData[key as keyof typeof updateData] === undefined
                      ) {
                        delete updateData[key as keyof typeof updateData];
                      }
                    }
                  });

                  // Add previous address(es) - include all when present (match address section Save and handleSaveAllData)
                  if (shouldShowPreviousAddress() && apiPreviousAddresses) {
                    updateData.previous_address = apiPreviousAddresses;
                  } else {
                    updateData.previous_address = [];
                  }

                  // Add alternate addresses (other addresses for credit search)
                  if (otherAddresses.length > 0) {
                    updateData.alternate_address = otherAddresses.map(
                      (addr) => ({
                        ra_id: addr.ra_id || null,
                        street_address:
                          buildAlternateAddressStreetAddress(addr),
                        outside_us: addr.outsideUS,
                        house_no: addr.streetNumber,
                        street: addr.streetName,
                        street_type: addr.streetType || "",
                        apartment: addr.aptUnit || null,
                        city: addr.city,
                        state: addr.state,
                        zip_code: addr.zipCode,
                        country: addr.country,
                        address_type: undefined, // Alternate addresses don't have a residence type
                        address_data: undefined,
                      }),
                    );
                  } else {
                    // If no addresses, send empty array
                    updateData.alternate_address = [];
                  }

                  // Calculate deleted alternate addresses (those that had ra_id but are no longer in otherAddresses)
                  // Ensure ra_id values are numbers
                  const currentAlternateAddressIds = new Set<number>();
                  otherAddresses.forEach((addr) => {
                    if (addr.ra_id) {
                      const raIdNumber =
                        typeof addr.ra_id === "string"
                          ? parseInt(addr.ra_id, 10)
                          : addr.ra_id;
                      if (!isNaN(raIdNumber)) {
                        currentAlternateAddressIds.add(raIdNumber);
                      }
                    }
                  });

                  const deletedAlternateAddressIds: number[] = [];
                  originalAlternateAddressIds.forEach((raId) => {
                    if (!currentAlternateAddressIds.has(raId)) {
                      deletedAlternateAddressIds.push(raId);
                    }
                  });

                  // Include deleted addresses in the request if any were deleted
                  if (deletedAlternateAddressIds.length > 0) {
                    updateData.deleted = {
                      ...(updateData.deleted || {}),
                      alternate_address: deletedAlternateAddressIds,
                    };
                  }

                  // Save address data
                  // updateApplication already fetches and returns the latest data, so no need for additional GET call
                  if (!applicationData?.applicant_id) {
                    setIsSavingAndContinuing(false);
                    setTimeout(() => setSaveAddressMessage(null), 5000);
                    return;
                  }
                  const updatedData = await dispatch(
                    updateApplication({
                      applicantId: applicationData.applicant_id,
                      updateData: updateData,
                    }),
                  ).unwrap();

                  // Update local state with the latest alternate addresses from API response
                  if (
                    updatedData?.alternate_address &&
                    Array.isArray(updatedData.alternate_address)
                  ) {
                    const updatedAddresses = updatedData.alternate_address.map(
                      (addr: Address, index: number) => ({
                        id: `other-addr-${addr.ra_id || index}-${Date.now()}`,
                        address: formatAddressDisplay(addr),
                        streetNumber: addr.house_no || "",
                        streetName: addr.street || "",
                        streetType: addr.street_type || "",
                        aptUnit: addr.apartment || "",
                        city: addr.city || "",
                        state: addr.state || "",
                        zipCode: addr.zip_code || "",
                        country:
                          addr.country || ADDRESS_CONSTANTS.DEFAULT_COUNTRY,
                        outsideUS:
                          typeof addr.outside_us === "string"
                            ? addr.outside_us === "1"
                            : addr.outside_us || false,
                        showDetails: false,
                        ra_id: addr.ra_id || null,
                      }),
                    );
                    setOtherAddresses(updatedAddresses);

                    // Update original alternate address IDs tracking after successful save
                    // Ensure ra_id is converted to number (API may return as string)
                    const updatedOriginalIds = new Set<number>();
                    updatedData.alternate_address.forEach((addr: Address) => {
                      if (addr.ra_id) {
                        const raIdNumber =
                          typeof addr.ra_id === "string"
                            ? parseInt(addr.ra_id, 10)
                            : addr.ra_id;
                        if (!isNaN(raIdNumber)) {
                          updatedOriginalIds.add(raIdNumber);
                        }
                      }
                    });
                    setOriginalAlternateAddressIds(updatedOriginalIds);
                  } else {
                    // No alternate addresses, clear the state
                    setOtherAddresses([]);
                    setOriginalAlternateAddressIds(new Set());
                  }

                  // Verify that current_address is present before navigating
                  if (!updatedData?.current_address) {
                    setSaveAddressMessage({
                      type: "error",
                      text: "Address saved but validation failed. Please refresh the page and try again.",
                    });
                    setIsSavingAndContinuing(false);
                    return;
                  }

                  // Call both all_property and all_agent APIs after saving (non-blocking)
                  // These calls are made in parallel and don't block navigation

                  // Get property_id from multiple sources
                  const propertyId =
                    updatedData?.property_id ||
                    updatedData?.property?.property_id ||
                    applicationData?.property_id ||
                    applicationData?.property?.property_id;

                  // Get invite_code from applicationData
                  const inviteCode =
                    updatedData?.invite_code || applicationData?.invite_code;

                  // Call all_property API
                  getAllProperties(1).catch(() => {
                    // Error fetching properties - non-blocking, continue navigation
                  });

                  // Call all_agent API if we have the required parameters
                  if (propertyId && inviteCode) {
                    getAgent(propertyId, inviteCode).catch(() => {
                      // Error fetching agent - non-blocking, continue navigation
                    });
                  }

                  // Show success message
                  trackApplicationAutosave("about_yourself", 1, "success");
                  trackApplicationSectionComplete("about_yourself", 1);
                  setSaveAddressMessage({
                    type: "success",
                    text: "Address saved successfully! Navigating to next page...",
                  });

                  // Navigate to property page after ensuring Redux state is updated
                  // updateApplication already updates Redux state synchronously
                  // Use requestAnimationFrame to ensure React has processed the state update
                  // before navigating, so the route guard sees the updated current_address
                  requestAnimationFrame(() => {
                    setTimeout(() => {
                      const propertyPath = applicantId
                        ? getTabPathWithApplicantId("property", applicantId)
                        : "/applicant/application/property";
                      if (applicantId) {
                        navigate(propertyPath, {
                          ...getTabNavigationOptions("property"),
                          replace: true,
                        });
                      } else {
                        navigate(propertyPath, { replace: true });
                      }
                      setIsSavingAndContinuing(false);
                    }, 300);
                  });
                } catch (error) {
                  trackApplicationAutosave("about_yourself", 1, "error");
                  // Handle error and show message
                  const errorMessage =
                    typeof error === "string"
                      ? error
                      : (error as { message?: string })?.message ||
                        "Failed to save address. Please check all required fields and try again.";

                  if (
                    errorMessage &&
                    (errorMessage.toLowerCase().includes("registered") ||
                      errorMessage.toLowerCase().includes("exists") ||
                      errorMessage.toLowerCase().includes("already in use") ||
                      errorMessage.toLowerCase().includes("duplicate")) &&
                    !isSharedPhone
                  ) {
                    setShowSharedPhoneErrorModal(true);
                  } else {
                    setValidationModalMessage(errorMessage);
                    setShowAddressValidationModal(true);
                  }

                  setIsSavingAndContinuing(false);
                }
              }}
              data-testid="about-save-continue-button"
              disabled={
                isSavingAddress ||
                isSavingAndContinuing ||
                isSavingAndExiting ||
                !applicationData?.applicant_id
              }
              className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSavingAndContinuing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {APPLICANT_CONTENT.application.messages.saving}
                </>
              ) : (
                <>
                  {isReadOnly ? (
                    <>
                      {APPLICANT_CONTENT.applyNow.step3.nextButton}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {APPLICANT_CONTENT.application.buttons.saveContinue}
                    </>
                  )}
                </>
              )}
            </button>
          )}
          {isReadOnly && (
            <button
              data-testid="about-next-button"
              onClick={() => {
                const propertyPath = applicantId
                  ? getTabPathWithApplicantId("property", applicantId)
                  : "/applicant/application/property";
                if (applicantId) {
                  navigate(propertyPath, getTabNavigationOptions("property"));
                } else {
                  navigate(propertyPath);
                }
              }}
              className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-md hover:shadow-lg"
            >
              {APPLICANT_CONTENT.applyNow.step3.nextButton}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
          <button
            data-testid="about-save-exit-button"
            disabled={isSavingAndExiting || isSavingAndContinuing}
            onClick={() => {
              if (isReadOnly) {
                navigate("/applicant/dashboard", { replace: true });
                return;
              }
              trackApplicationExit("about_yourself", 1);
              handleSaveAllData("exit");
            }}
            className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSavingAndExiting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {APPLICANT_CONTENT.application.messages.saving}
              </>
            ) : (
              <>
                {isReadOnly
                  ? APPLICANT_CONTENT.application.buttons.exit
                  : APPLICANT_CONTENT.application.buttons.saveExit}
              </>
            )}
          </button>
        </div>
      </div>

      <ValidationModal
        {...applicantValidationModalProps}
        isOpen={showPersonalProfileValidationModal}
        title={APPLICANT_CONTENT.errors.missingFields}
        message={validationModalMessage}
        onClose={() => setShowPersonalProfileValidationModal(false)}
      />

      <ValidationModal
        {...applicantValidationModalProps}
        isOpen={showAddressValidationModal}
        title={APPLICANT_CONTENT.errors.missingFields}
        message={validationModalMessage}
        onClose={() => setShowAddressValidationModal(false)}
      />

      <ValidationModal
        {...applicantValidationModalProps}
        isOpen={showSharedPhoneErrorModal}
        title="Mobile Number Already Registered"
        message="The phone number is already in use by another user. If this is a shared phone number (e.g., shared with a spouse), please check the 'Shared Phone Number' box above to proceed."
        onClose={() => setShowSharedPhoneErrorModal(false)}
      />

      {/* Revisit: Import from previous application (matches Angular setOldApplicationData('neutral')) */}
      <ConfirmDialog
        {...applicantConfirmDialogProps}
        isOpen={showImportPreviousAddressDialog}
        title="Import from Previous Application"
        message="We found some address records from your previous application with us. Would you like us to import these?"
        primaryLabel="Yes, import as previous address"
        onPrimary={() => {
          const oldApp = applicationData?.oldapplication;
          if (!oldApp) return;
          const merged: Address[] = [];
          if (oldApp.current_address) merged.push(oldApp.current_address);
          if (oldApp.previous_address?.length) {
            oldApp.previous_address.forEach((a) => merged.push(a));
          }
          if (merged.length > 0) {
            prevAddressInitialized.current = true;
            setPreviousAddresses(merged.map((addr) => mapApiAddressToFormState(addr)));
          }
          setShowImportPreviousAddressDialog(false);
        }}
        secondaryLabel="No, thank you"
        onSecondary={() => setShowImportPreviousAddressDialog(false)}
        variant="info"
      />
      </div>
    </div>
  );
}
